// Ultimate Card Game (UCG) - Particle & Visual Effects Engine
import { getSpriteGrid, getHDSpriteGrid, buildSpriteBitmap } from './render/unitSprites.js';
import { SUPERWEAPON_IMPACT_DELAY } from '../data/balance.js';

// Gameplay-relevant timings of the two superweapon sequences - the board
// wipe fires when these elapse (see triggerNuclearStrike()/
// triggerBioPlasmaStrike()), so they live in balance.js, shared with the bot
// AI and the headless sims, rather than as literals in this cosmetic engine.
const NUKE_IMPACT_DELAY = SUPERWEAPON_IMPACT_DELAY.Human;
const BIO_WIPE_AT = SUPERWEAPON_IMPACT_DELAY.Sul;

// Budget cap - oldest particles get evicted first. Mutable (not const):
// js/engine/performance.js retunes this per quality tier. Purely a visual
// cap - never read by anything gameplay-affecting - so it's safe for two
// online peers to run different values.
let MAX_PARTICLES = 800;

export function setParticleBudget(n) {
  MAX_PARTICLES = n;
}

// Pre-cached font strings for floating damage texts to avoid per-frame font parsing
const DAMAGE_TEXT_FONT_CACHE = Object.freeze({
  13: 'bold 13px "Orbitron", "Courier New", monospace',
  14: 'bold 14px "Orbitron", "Courier New", monospace',
  15: 'bold 15px "Orbitron", "Courier New", monospace',
  16: 'bold 16px "Orbitron", "Courier New", monospace',
  17: 'bold 17px "Orbitron", "Courier New", monospace',
  24: 'bold 24px "Orbitron", "Courier New", monospace'
});

const BASTION_MOLTEN_PALETTE = Object.freeze([
  Object.freeze({ base: '#ea580c', hl: '#ffffff' }), // fiery molten orange + white core
  Object.freeze({ base: '#f97316', hl: '#fef08a' }), // incandescent amber + yellow core
  Object.freeze({ base: '#38bdf8', hl: '#ffffff' })  // blue plasma streak + white core
]);
const BASTION_SMOKE_COLORS = Object.freeze(['#475569', '#64748b', '#94a3b8']);

let _cachedPulpSpotGrad = null;
function getPulpSpotGrad(ctx) {
  if (!_cachedPulpSpotGrad) {
    _cachedPulpSpotGrad = ctx.createRadialGradient(0, 0, 4, 0, 0, 36);
    _cachedPulpSpotGrad.addColorStop(0, 'rgba(251, 191, 36, 0.45)');
    _cachedPulpSpotGrad.addColorStop(0.45, 'rgba(168, 85, 247, 0.25)');
    _cachedPulpSpotGrad.addColorStop(1, 'rgba(124, 58, 237, 0)');
  }
  return _cachedPulpSpotGrad;
}

let _pinkBeretLimeGrad = null;
function getPinkBeretLimeGrad(ctx) {
  if (!_pinkBeretLimeGrad) {
    const g = ctx.createRadialGradient(0, 0, 4, 0, 0, 26);
    g.addColorStop(0, 'rgba(255, 105, 180, 0.28)');
    g.addColorStop(0.5, 'rgba(255, 20, 147, 0.12)');
    g.addColorStop(1, 'rgba(255, 20, 147, 0)');
    _pinkBeretLimeGrad = g;
  }
  return _pinkBeretLimeGrad;
}

const HEX_LUT = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

const SHADOW_SMOKE_COLORS = Object.freeze(['#020617', '#0f172a', '#1e293b', '#334155']);
const FEATHER_COLORS = Object.freeze(['#fffbeb', '#fef3c7', '#78716c', '#57534e', '#dc2626']);
const PARASITE_SPLATTER_COLORS = Object.freeze(['#450a0a', '#7f1d1d', '#991b1b', '#dc2626', '#ef4444']);

const _flareLUTs = new Map();
function getAnamorphicFlareLUT(color) {
  let lut = _flareLUTs.get(color);
  if (!lut) {
    if (typeof document === 'undefined') return null;
    lut = document.createElement('canvas');
    lut.width = 128;
    lut.height = 4;
    const lctx = lut.getContext('2d');
    const grad = lctx.createLinearGradient(0, 0, 128, 0);
    grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    grad.addColorStop(0.35, color);
    grad.addColorStop(0.5, '#ffffff');
    grad.addColorStop(0.65, color);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    lctx.fillStyle = grad;
    lctx.fillRect(0, 0, 128, 4);
    _flareLUTs.set(color, lut);
  }
  return lut;
}

let _bioPlasmaColumnBeamGrad = null;
function getBioPlasmaColumnBeamGrad(ctx, colW) {
  if (!_bioPlasmaColumnBeamGrad) {
    const g = ctx.createLinearGradient(-colW / 2, 0, colW / 2, 0);
    g.addColorStop(0, 'rgba(168, 85, 247, 0.5)');
    g.addColorStop(0.25, '#d946ef');
    g.addColorStop(0.5, '#ffffff');
    g.addColorStop(0.75, '#d946ef');
    g.addColorStop(1, 'rgba(168, 85, 247, 0.5)');
    _bioPlasmaColumnBeamGrad = g;
  }
  return _bioPlasmaColumnBeamGrad;
}

let _bioPlasmaWakeGrad = null;
function getBioPlasmaWakeGrad(ctx) {
  if (!_bioPlasmaWakeGrad) {
    _bioPlasmaWakeGrad = ctx.createRadialGradient(0, 0, 0.01, 0, 0, 1);
    _bioPlasmaWakeGrad.addColorStop(0, 'rgba(217, 70, 239, 0.08)');
    _bioPlasmaWakeGrad.addColorStop(0.6, 'rgba(168, 85, 247, 0.10)');
    _bioPlasmaWakeGrad.addColorStop(0.9, 'rgba(132, 204, 22, 0.12)');
    _bioPlasmaWakeGrad.addColorStop(1, 'rgba(132, 204, 22, 0)');
  }
  return _bioPlasmaWakeGrad;
}

let _nukeHaloGrad = null;
function getNukeHaloGrad(ctx) {
  if (!_nukeHaloGrad) {
    _nukeHaloGrad = ctx.createRadialGradient(0, 0, 0.02, 0, 0, 1);
    _nukeHaloGrad.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    _nukeHaloGrad.addColorStop(0.3, 'rgba(254, 240, 138, 0.7)');
    _nukeHaloGrad.addColorStop(0.65, 'rgba(249, 115, 22, 0.4)');
    _nukeHaloGrad.addColorStop(1, 'rgba(239, 68, 68, 0)');
  }
  return _nukeHaloGrad;
}

let _sulEnergyOrbCanvas = null;
function getSulEnergyOrbCanvas() {
  if (!_sulEnergyOrbCanvas) {
    if (typeof document === 'undefined') return null;
    _sulEnergyOrbCanvas = document.createElement('canvas');
    _sulEnergyOrbCanvas.width = 64;
    _sulEnergyOrbCanvas.height = 64;
    const gctx = _sulEnergyOrbCanvas.getContext('2d');
    const grad = gctx.createRadialGradient(32, 32, 1, 32, 32, 32);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.35, '#f472b6');
    grad.addColorStop(0.7, '#d946ef');
    grad.addColorStop(1, 'rgba(147, 51, 234, 0)');
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, 64, 64);
  }
  return _sulEnergyOrbCanvas;
}

let _plasmaPoolCanvas = null;
function getPlasmaPoolCanvas() {
  if (!_plasmaPoolCanvas) {
    if (typeof document === 'undefined') return null;
    _plasmaPoolCanvas = document.createElement('canvas');
    _plasmaPoolCanvas.width = 64;
    _plasmaPoolCanvas.height = 64;
    const gctx = _plasmaPoolCanvas.getContext('2d');
    const grad = gctx.createRadialGradient(32, 32, 2, 32, 32, 32);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.25, '#a3e635');
    grad.addColorStop(0.55, '#84cc16');
    grad.addColorStop(0.85, '#4a044e');
    grad.addColorStop(1, 'rgba(59, 7, 100, 0)');
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, 64, 64);
  }
  return _plasmaPoolCanvas;
}

let _plasmaPuffWhiteCanvas = null;
let _plasmaPuffGreenCanvas = null;
let _plasmaPuffPurpleCanvas = null;

function getPlasmaPuffCanvas(type) {
  if (typeof document === 'undefined') return null;
  if (type === 'white') {
    if (!_plasmaPuffWhiteCanvas) {
      _plasmaPuffWhiteCanvas = document.createElement('canvas');
      _plasmaPuffWhiteCanvas.width = 64;
      _plasmaPuffWhiteCanvas.height = 64;
      const gctx = _plasmaPuffWhiteCanvas.getContext('2d');
      const g = gctx.createRadialGradient(32, 32, 2, 32, 32, 32);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(0.4, '#fdf4ff');
      g.addColorStop(0.8, 'rgba(217, 70, 239, 0.7)');
      g.addColorStop(1, 'rgba(59, 7, 100, 0)');
      gctx.fillStyle = g;
      gctx.fillRect(0, 0, 64, 64);
    }
    return _plasmaPuffWhiteCanvas;
  }
  if (type === 'green') {
    if (!_plasmaPuffGreenCanvas) {
      _plasmaPuffGreenCanvas = document.createElement('canvas');
      _plasmaPuffGreenCanvas.width = 64;
      _plasmaPuffGreenCanvas.height = 64;
      const gctx = _plasmaPuffGreenCanvas.getContext('2d');
      const g = gctx.createRadialGradient(32, 32, 2, 32, 32, 32);
      g.addColorStop(0, '#bef264');
      g.addColorStop(0.5, '#84cc16');
      g.addColorStop(0.8, '#365314');
      g.addColorStop(1, 'rgba(54, 83, 20, 0)');
      gctx.fillStyle = g;
      gctx.fillRect(0, 0, 64, 64);
    }
    return _plasmaPuffGreenCanvas;
  }
  if (!_plasmaPuffPurpleCanvas) {
    _plasmaPuffPurpleCanvas = document.createElement('canvas');
    _plasmaPuffPurpleCanvas.width = 64;
    _plasmaPuffPurpleCanvas.height = 64;
    const gctx = _plasmaPuffPurpleCanvas.getContext('2d');
    const g = gctx.createRadialGradient(32, 32, 2, 32, 32, 32);
    g.addColorStop(0, '#f5d0fe');
    g.addColorStop(0.4, '#d946ef');
    g.addColorStop(0.75, '#581c87');
    g.addColorStop(1, 'rgba(59, 7, 100, 0)');
    gctx.fillStyle = g;
    gctx.fillRect(0, 0, 64, 64);
  }
  return _plasmaPuffPurpleCanvas;
}

const _vultureBioRocketCanvasMap = new Map();
function getVultureBioRocketCanvas(color = '#ff0033') {
  if (typeof document === 'undefined') return null;
  const col = color || '#ff0033';
  let c = _vultureBioRocketCanvasMap.get(col);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = 16;
  c.height = 16;
  const gctx = c.getContext('2d');
  const g = gctx.createRadialGradient(7, 7, 0.5, 8, 8, 4.4);
  g.addColorStop(0.0, '#ffffff');
  g.addColorStop(0.25, '#ff4d6d');
  g.addColorStop(0.70, col);
  g.addColorStop(1.0, '#4c0519');
  gctx.fillStyle = g;
  gctx.beginPath();
  gctx.arc(8, 8, 4.4, 0, Math.PI * 2);
  gctx.fill();
  _vultureBioRocketCanvasMap.set(col, c);
  return c;
}

const BILLOW_PUFF_CONFIGS = Object.freeze([
  { oxFrac: 0, oyFrac: -0.45, rFrac: 0.50, type: 'purple', c: '#d946ef' },
  { oxFrac: -0.35, oyFrac: -0.28, rFrac: 0.42, type: 'purple', c: '#a855f7' },
  { oxFrac: 0.35, oyFrac: -0.28, rFrac: 0.42, type: 'purple', c: '#a855f7' },
  { oxFrac: -0.55, oyFrac: -0.12, rFrac: 0.34, type: 'green', c: '#84cc16' },
  { oxFrac: 0.55, oyFrac: -0.12, rFrac: 0.34, type: 'green', c: '#84cc16' },
  { oxFrac: 0, oyFrac: -0.78, rFrac: 0.32, type: 'white', c: '#ffffff' }
]);

export class ParticleEngine {
  constructor() {
    this.particles = [];
    this.beams = [];
    this.floatingTexts = [];
    this.specialEffects = [];
    this.screenShake = { intensity: 0, duration: 0, x: 0, y: 0 };
    this.nukeFlash = null;
    this.bioPlasmaHush = null;
    this.bioPlasmaAfterglow = null;
    this.zoomPull = null;
    this.bioPlasmaWaveActive = false;
    // Reactive point lights (explosions/muzzle flashes) - short-lived additive
    // glows composited under the particle sprites, and long-lived scorch
    // decals painted onto the ground so bigger blasts leave a mark.
    this.lights = [];
    this.decals = [];
    this._replaceIdx = 0;
    this._particlePool = [];
    this._lightPool = [];
    this._decalPool = [];
    this._additiveQueue = [];
  }

  obtainParticle() {
    return this._particlePool.pop() || {};
  }

  obtainLight() {
    return this._lightPool.pop() || {};
  }

  releaseLight(l) {
    if (this._lightPool.length < 80) this._lightPool.push(l);
  }

  obtainDecal() {
    return this._decalPool.pop() || {};
  }

  releaseDecal(d) {
    if (this._decalPool.length < 80) this._decalPool.push(d);
  }

  // Reset contract for pooled particles (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md
  // §5.4) - covers every property ever written onto a `this.particles`/
  // `this._additiveQueue` entry (verified by source-scanning particles.js
  // itself, see tests/particle_reset_contract_test.mjs, which fails the
  // build if a new particle property is introduced here without being added
  // below). Deliberately does NOT include isBioPlasmaColumn/columnY/
  // isBioPlasmaWave/targetX/targetY/outerColor/coreColor - those belong to
  // this.specialEffects entries (e.g. the Bio-Plasma `bioStrike` object,
  // ~line 4020), a separate array with its own lifecycle, never drawn from
  // _particlePool.
  //
  // Most numeric fields here are already read defensively elsewhere
  // (`p.rot || 0`, `p.groundY !== undefined`, `p.bounces && ...`), so a
  // stale numeric value is a smaller risk than a stale boolean - the
  // `if (p.isDebris) ... else if (p.isRound || p.isBubble) ...` render
  // dispatch has no such fallback, so every type-tag below MUST be false on
  // every reuse or a recycled particle silently renders as the wrong kind.
  resetParticle(p) {
    p.x = 0; p.y = 0; p.vx = 0; p.vy = 0;
    p.radius = 1; p.color = '#ffffff';
    p.alpha = 1.0; p.initialAlpha = 1.0;
    p.life = 1.0; p.maxLife = 1.0;
    p.gravity = 0; p.drag = 0;
    p.expandRate = 1.0; p.settled = false;
    p.delay = 0; p.groundY = undefined;
    p.bounces = 0; p.rot = 0; p.vRot = 0;
    p.swayAmp = 0; p.swayFreq = 0; p.swayPhase = 0;
    p.sparkleOffset = 0; p.waveOffset = 0;
    p.width = undefined; p.height = undefined;
    p.points = null; p.highlightColor = null;

    p.isDust = false; p.isSmoke = false; p.isShard = false;
    p.isEmber = false; p.isDebris = false; p.isHeatShimmer = false;
    p.isAnamorphicFlare = false; p.isBastionFlare = false; p.isVapor = false; p.isBubble = false;
    p.isRound = false; p.isStarGlint = false; p.isGoldDustGlide = false;
    p.isLiquidDroplet = false;
    p.isPixelDroplet = false;
    p.isPixelRicochet = false;
    p.isPixel = false;
    p.isRicochet = false;
    p.isWreckage = false;
    p.isGroundArc = false;
    p.isSlimeGlaze = false;
    p.isAcidSizzle = false;
    p.wreckageType = 0;
    p.team = 'western';
    return p;
  }

  obtainCleanParticle() {
    const p = this._particlePool.pop() || {};
    return this.resetParticle(p);
  }

  addWoundedSmoke(x, y, radius, isHeavy = false) {
    if (!this.canSpawnCosmetic()) return;
    const p = this.obtainParticle();
    p.x = x + (Math.random() * 10 - 5);
    p.y = y - radius * 0.4 + (Math.random() * 6 - 3);
    p.vx = (Math.random() - 0.5) * (isHeavy ? 6 : 5);
    p.vy = isHeavy ? (-14 - Math.random() * 10) : (-10 - Math.random() * 8);
    p.gravity = 0;
    p.drag = 0;
    p.settled = false;
    p.radius = isHeavy ? (2 + Math.random() * 2) : (1.5 + Math.random() * 1.5);
    p.expandRate = isHeavy ? 2.0 : 1.4;
    p.color = isHeavy ? (Math.random() < 0.3 ? '#ef4444' : '#334155') : '#475569';
    p.life = isHeavy ? (0.6 + Math.random() * 0.4) : (0.5 + Math.random() * 0.3);
    p.maxLife = isHeavy ? 1.0 : 0.8;
    p.initialAlpha = isHeavy ? 0.4 : 0.28;
    p.alpha = p.initialAlpha;
    p.isDust = true;
    p.isSmoke = false;
    p.isShard = false;
    p.isEmber = false;
    p.isDebris = false;
    p.isHeatShimmer = false;
    p.isAnamorphicFlare = false;
    p.isVapor = false;
    p.delay = 0;
    this.pushParticle(p);
  }

  addWoundedSpark(x, y, radius) {
    if (!this.canSpawnCosmetic()) return;
    const p = this.obtainParticle();
    p.x = x + (Math.random() * 8 - 4);
    p.y = y - radius * 0.3;
    p.vx = (Math.random() - 0.5) * 40;
    p.vy = -30 - Math.random() * 30;
    p.gravity = 60;
    p.drag = 0;
    p.settled = false;
    p.radius = 1 + Math.random();
    p.expandRate = 0;
    p.color = '#fbbf24';
    p.life = 0.2 + Math.random() * 0.15;
    p.maxLife = 0.35;
    p.initialAlpha = 1.0;
    p.alpha = 1.0;
    p.isDust = false;
    p.isSmoke = false;
    p.isShard = false;
    p.isEmber = false;
    p.isDebris = false;
    p.isHeatShimmer = false;
    p.isAnamorphicFlare = false;
    p.isVapor = false;
    p.delay = 0;
    this.pushParticle(p);
  }

  canSpawnCosmetic() {
    return MAX_PARTICLES > 150 && this.particles.length < MAX_PARTICLES;
  }

  pushParticle(p) {
    if (this.particles.length >= MAX_PARTICLES) {
      if (MAX_PARTICLES <= 150) return;
      this._replaceIdx = (this._replaceIdx + 1) % MAX_PARTICLES;
      this.particles[this._replaceIdx] = p;
      return;
    }
    this.particles.push(p);
  }

  reset() {
    this.particles = [];
    this.beams = [];
    this.floatingTexts = [];
    this.specialEffects = [];
    this.screenShake = { intensity: 0, duration: 0, x: 0, y: 0 };
    this.nukeFlash = null;
    this.bioPlasmaHush = null;
    this.bioPlasmaAfterglow = null;
    this.zoomPull = null;
    this.bioPlasmaWaveActive = false;
    this.lights = [];
    this.decals = [];
    this._replaceIdx = 0;
  }

  addScreenShake(intensity, duration = 0.2) {
    if (intensity > this.screenShake.intensity || this.screenShake.duration <= 0) {
      this.screenShake.intensity = intensity;
      this.screenShake.duration = duration;
    }
    if (intensity >= 18 && this.game?.hud?.triggerImpactJolt) {
      this.game.hud.triggerImpactJolt(intensity);
    }
  }

  update(dt) {
    // 0. Update screen shake
    if (this.screenShake.duration > 0) {
      this.screenShake.duration -= dt;
      const mag = this.screenShake.intensity * Math.min(1.0, this.screenShake.duration / 0.15);
      this.screenShake.x = (Math.random() * 2 - 1) * mag;
      this.screenShake.y = (Math.random() * 2 - 1) * mag;
    } else {
      this.screenShake.x = 0;
      this.screenShake.y = 0;
      this.screenShake.intensity = 0;
    }

    // 0B. Update Nuclear Blinding Flash
    if (this.nukeFlash) {
      this.nukeFlash.duration -= dt;
      if (this.nukeFlash.duration <= 0) {
        this.nukeFlash = null;
      }
    }

    // 0C. Update Bio-Plasma effects (Hush, Afterglow, Zoom-Pull)
    if (this.bioPlasmaHush) {
      this.bioPlasmaHush.duration -= dt;
      if (this.bioPlasmaHush.duration <= 0) {
        this.bioPlasmaHush = null;
      }
    }
    if (this.bioPlasmaAfterglow) {
      this.bioPlasmaAfterglow.duration -= dt;
      if (this.bioPlasmaAfterglow.duration <= 0) {
        this.bioPlasmaAfterglow = null;
      }
    }
    if (this.zoomPull) {
      this.zoomPull.elapsed += dt;
      if (this.zoomPull.elapsed >= this.zoomPull.duration) {
        this.zoomPull = null;
      }
    }

    // 1. Update particles & pixel debris
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      // Delayed particle ignition / appearance (e.g. smoke building up after flame burst)
      if (p.delay && p.delay > 0) {
        p.delay -= dt;
        p.alpha = 0;
        continue;
      }

      if (p.settled) {
        p.life -= dt;
        if (p.isDebris) {
          p.alpha = Math.min(1.0, p.life / 0.8);
        } else {
          p.alpha = Math.max(0, p.life / p.maxLife);
        }
        if (p.life <= 0) {
          if (this._particlePool.length < 600) {
            this._particlePool.push(p);
          }
          const lastIdx = this.particles.length - 1;
          if (i < lastIdx) this.particles[i] = this.particles[lastIdx];
          this.particles.pop();
        }
        continue;
      }

      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.gravity) p.vy += p.gravity * dt;
      if (p.drag) {
        p.vx *= (1 - p.drag * dt);
        p.vy *= (1 - p.drag * dt);
      }

      // Ground Collision & Bouncing / Settling for pixel voxel debris
      if (p.groundY !== undefined && p.y >= p.groundY) {
        p.y = p.groundY;
        if (p.bounces && p.bounces > 0 && Math.abs(p.vy) > 30) {
          p.bounces--;
          p.vy = -p.vy * 0.35;
          p.vx *= 0.6;
          if (p.vRot) p.vRot *= -0.5;
        } else {
          p.settled = true;
          p.vx = 0;
          p.vy = 0;
          p.vRot = 0;
        }
      }

      // Shard & Wreckage Angular Rotation
      if (p.isShard || (p.isWreckage && !p.settled)) {
        p.rot = (p.rot || 0) + (p.vRot || 0) * dt;
      }

      // Settled Wreckage smoke wisps & fade out
      if (p.isWreckage) {
        if (p.settled && p.life > 1.2 && Math.random() < 0.08) {
          this.addWoundedSmoke(p.x, p.y - 3, 2.5);
        }
        if (p.life < 1.0) {
          p.alpha = Math.max(0, p.life / 1.0);
        }
      }

      // Particle Expansion (Smoke & Haze Billowing)
      if (p.expandRate) {
        p.radius += p.expandRate * dt;
      }

      p.life -= dt;
      if (p.isGoldDustGlide) {
        p.x += Math.sin(p.life * (p.swayFreq || 3.8) + (p.swayPhase || 0)) * (p.swayAmp || 14) * dt;
        const progress = Math.max(0, p.life / p.maxLife);
        const sparkle = 0.72 + 0.28 * Math.sin(p.life * 26.0 + (p.sparkleOffset || 0));
        p.alpha = progress * sparkle;
      } else if (p.isEmber) {
        p.x += Math.sin(p.life * (p.swayFreq || 4) + (p.swayPhase || 0)) * (p.swayAmp || 12) * dt;
        p.alpha = Math.max(0, p.life / p.maxLife);
      } else if (p.isHeatShimmer) {
        p.waveOffset = (p.waveOffset || 0) + dt * 8;
        const progress = 1.0 - Math.max(0, p.life / p.maxLife);
        p.alpha = Math.sin(progress * Math.PI) * 0.35;
      } else if (p.isAnamorphicFlare) {
        const progress = 1.0 - Math.max(0, p.life / p.maxLife);
        p.alpha = Math.sin(progress * Math.PI);
      } else if (p.isVapor) {
        const lifeRatio = p.life / p.maxLife;
        p.alpha = (p.initialAlpha || 0.25) * Math.sin(lifeRatio * Math.PI);
      } else if (p.isDebris) {
        p.alpha = Math.min(1.0, p.life / 0.8);
      } else if (p.isGroundArc) {
        p.alpha = Math.max(0, p.life / p.maxLife);
      } else if (p.isSlimeGlaze) {
        p.alpha = (p.initialAlpha || 0.45) * Math.max(0, p.life / p.maxLife);
      } else if (p.isSmoke || p.isDust) {
        // Smoke & dust fade in smoothly, billow to full opacity, then slowly diffuse into atmospheric haze
        const progress = 1.0 - Math.max(0, p.life / p.maxLife);
        const maxA = p.initialAlpha || 0.45;
        if (progress < 0.22) {
          p.alpha = maxA * (progress / 0.22);
        } else {
          const fadeProgress = (progress - 0.22) / 0.78;
          p.alpha = Math.max(0, maxA * (1.0 - fadeProgress * fadeProgress));
        }
      } else {
        p.alpha = Math.max(0, p.life / p.maxLife);
      }

      if (p.life <= 0) {
        if (this._particlePool.length < 600) {
          this._particlePool.push(p);
        }
        const lastIdx = this.particles.length - 1;
        if (i < lastIdx) this.particles[i] = this.particles[lastIdx];
        this.particles.pop();
      }
    }

    // 1B. Update reactive point lights (fast decay - these are muzzle-flash/
    // explosion flicker, not ambient lighting)
    for (let i = this.lights.length - 1; i >= 0; i--) {
      const l = this.lights[i];
      l.life -= dt;
      l.alpha = Math.max(0, l.life / l.maxLife);
      if (l.life <= 0) {
        this.releaseLight(l);
        const lastIdx = this.lights.length - 1;
        if (i < lastIdx) this.lights[i] = this.lights[lastIdx];
        this.lights.pop();
      }
    }

    // 1C. Update ground scorch decals - lingers for a long time, then fades
    // out smoothly over its last second so the battlefield keeps visible
    // battle scars without accumulating forever.
    const decalFadeWindow = 1.5;
    for (let i = this.decals.length - 1; i >= 0; i--) {
      const d = this.decals[i];
      d.life -= dt;
      if (d.type === 'carmine_puddle' && (d.spreadProgress || 0) < 1.0) {
        d.spreadProgress = Math.min(1.0, (d.spreadProgress || 0) + dt * 2.5);
        d.spread = 0.35 + 0.65 * Math.sin(d.spreadProgress * Math.PI * 0.5);
      }
      d.alpha = d.life >= decalFadeWindow ? d.maxAlpha : d.maxAlpha * Math.max(0, d.life / decalFadeWindow);
      if (d.life <= 0) {
        this.releaseDecal(d);
        const lastIdx = this.decals.length - 1;
        if (i < lastIdx) this.decals[i] = this.decals[lastIdx];
        this.decals.pop();
      }
    }

    // 2. Update beams & projectiles
    for (let i = this.beams.length - 1; i >= 0; i--) {
      const b = this.beams[i];
      b.life -= dt;
      if (b.life <= 0) {
        const lastIdx = this.beams.length - 1;
        if (i < lastIdx) this.beams[i] = this.beams[lastIdx];
        this.beams.pop();
      }
    }

    // 3. Update floating damage text (Ballistic trajectory with gentle floor bounce & crit pop)
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      if (ft.vx !== undefined) {
        ft.x += ft.vx * dt;
        ft.y += ft.vy * dt;
        ft.vy += 150 * dt; // gravity pulling down
        // Subtle bounce if falling back down past its origin
        if (ft.vy > 0 && ft.y >= ft.originY + 4 && ft.bounces === 0) {
          ft.vy = -ft.vy * 0.38;
          ft.vx *= 0.6;
          ft.bounces = 1;
        }
      } else {
        ft.y -= 28 * dt;
      }
      ft.life -= dt;
      ft.alpha = Math.max(0, ft.life / ft.maxLife);
      // Elastic scale pop for crits and nukes during first 180ms, or when rolling batch counter updates
      const age = ft.maxLife - ft.life;
      if (ft.popTimer > 0) {
        ft.popTimer -= dt;
        ft.scale = 1.0 + Math.sin(Math.max(0, ft.popTimer / 0.14) * Math.PI) * 0.28;
      } else if ((ft.type === 'crit' || ft.type === 'nuke') && age < 0.18) {
        ft.scale = 1.0 + Math.sin((age / 0.18) * Math.PI) * 0.32;
      } else {
        ft.scale = 1.0;
      }
      if (ft.life <= 0) {
        const lastIdx = this.floatingTexts.length - 1;
        if (i < lastIdx) this.floatingTexts[i] = this.floatingTexts[lastIdx];
        this.floatingTexts.pop();
      }
    }

    // 4. Update special effects (Nukes, Storms, Orbitals, Projectiles)
    for (let i = this.specialEffects.length - 1; i >= 0; i--) {
      const s = this.specialEffects[i];
      s.life -= dt;
      s.update && s.update(dt);
      if (s.life <= 0) {
        s.onComplete && s.onComplete();
        const lastIdx = this.specialEffects.length - 1;
        if (i < lastIdx) this.specialEffects[i] = this.specialEffects[lastIdx];
        this.specialEffects.pop();
      }
    }

    // 5. Budget cap - deliberately last: onComplete above (e.g. a nuke wiping
    // every unit on the field) can synchronously push hundreds of death-shatter
    // particles in one burst, so capping any earlier would miss that spike.
    // Oldest particles (front of the array - they're pushed at the end) are
    // evicted first since they've already had the most time to be seen.
    if (this.particles.length > MAX_PARTICLES) {
      const excess = this.particles.length - MAX_PARTICLES;
      for (let k = 0; k < excess && this._particlePool.length < 600; k++) {
        this._particlePool.push(this.particles[k]);
      }
      this.particles.copyWithin(0, excess);
      this.particles.length = MAX_PARTICLES;
    }
  }

  addDamageText(x, y, text, type = 'normal') {
    let color = '#ffffff';
    let size = 13;
    if (type === 'crit') { color = '#ff2244'; size = 17; }
    else if (type === 'bonus') { color = '#ffb703'; size = 15; }
    else if (type === 'shield') { color = '#00f0ff'; size = 13; }
    else if (type === 'heal') { color = '#22c55e'; size = 14; }
    else if (type === 'nuke') { color = '#ff0055'; size = 24; }
    else if (type === 'deflect') { color = '#38bdf8'; size = 15; }
    else if (type === 'miss') { color = '#e2e8f0'; size = 13; }
    else if (type === 'gold') { color = '#fbbf24'; size = 16; }

    const isCrit = (type === 'crit' || type === 'nuke');
    const isNum = (typeof text === 'number' && Number.isFinite(text)) || (typeof text === 'string' && /^\d+$/.test(text));
    const numVal = isNum ? parseInt(text, 10) : 0;

    // Rolling Damage Batching (AAA Stacking Floating Numbers):
    // If a unit is subjected to rapid-fire impacts (e.g. Gatling, continuous lasers, DoTs),
    // batch consecutive hits of the same type within proximity into a rolling counter
    // instead of spawning dozens of overlapping duplicate numbers.
    if (isNum && numVal > 0) {
      for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
        const existing = this.floatingTexts[i];
        if (existing.isNumeric && existing.type === type && existing.life > 0.15) {
          const dx = existing.x - x;
          const dy = existing.y - y;
          if (dx * dx + dy * dy <= 450) { // ~21px radius
            existing.numericValue = (existing.numericValue || parseInt(existing.text, 10)) + numVal;
            existing.text = existing.numericValue.toString();
            existing.life = Math.min(existing.maxLife, existing.life + 0.35);
            existing.alpha = 1.0;
            existing.popTimer = 0.14; // trigger punchy re-pop
            // Shift color/size slightly towards critical prominence if accumulated damage is high
            if (existing.numericValue > 50 && existing.type === 'normal') {
              existing.color = '#ffedd5';
              existing.size = 15;
            }
            return;
          }
        }
      }
    }

    const maxFloating = (this.quality === 'low' || this.isMobile) ? 14 : 30;
    if (this.floatingTexts.length >= maxFloating) {
      this.floatingTexts.shift();
    }

    // Ballistic horizontal dispersion (arc to left or right)
    const vx = (Math.random() - 0.5) * (isCrit ? 56 : 38);
    // Initial upward pop velocity
    const vy = isCrit ? -75 : -52;

    this.floatingTexts.push({
      x: x + (Math.random() * 12 - 6),
      y: y - 10,
      originY: y - 10,
      vx,
      vy,
      text: text.toString(),
      type,
      color,
      size,
      scale: 1.0,
      bounces: 0,
      life: isCrit ? 1.05 : 0.9,
      maxLife: isCrit ? 1.05 : 0.9,
      alpha: 1.0,
      isNumeric: isNum && numVal > 0,
      numericValue: numVal,
      popTimer: 0
    });
  }

  // Brief additive point light - the visual "illumination" of a flash/blast,
  // separate from its spark/smoke particles. Capped list length since these
  // are cheap but numerous (fired on every shot). Pooled to eliminate GC heap churn.
  addLight(x, y, radius, color, life = 0.15) {
    if (this.lights.length >= 60) {
      const oldest = this.lights[0];
      const lastIdx = this.lights.length - 1;
      this.lights[0] = this.lights[lastIdx];
      this.lights.pop();
      this.releaseLight(oldest);
    }
    const l = this.obtainLight();
    l.x = x;
    l.y = y;
    l.radius = radius;
    l.color = color;
    l.life = life;
    l.maxLife = life;
    l.alpha = 1.0;
    this.lights.push(l);
  }

  // Long-lived scorch/char mark painted onto the ground. Capped so a long
  // match doesn't accumulate an unbounded number of decals. Pooled with O(1) recycling.
  addScorchMark(x, y, radius, color = 'rgba(15, 12, 10, 0.55)') {
    if (this.decals.length >= 40) {
      const oldest = this.decals[0];
      const lastIdx = this.decals.length - 1;
      this.decals[0] = this.decals[lastIdx];
      this.decals.pop();
      this.releaseDecal(oldest);
    }
    const maxAlpha = 0.4 + Math.random() * 0.15;
    const d = this.obtainDecal();
    d.type = undefined;
    d.x = x;
    d.y = y;
    d.radius = radius;
    d.color = color;
    d.life = 20;
    d.maxAlpha = maxAlpha;
    d.alpha = maxAlpha;
    this.decals.push(d);
  }

  // Shadow Footprint / Stalker Trail (Anti Shade): Smoky dark decal with crimson ember
  addShadowFootprint(x, y, dir = 1) {
    if (this.decals.length >= 60) {
      const oldest = this.decals[0];
      const lastIdx = this.decals.length - 1;
      this.decals[0] = this.decals[lastIdx];
      this.decals.pop();
      this.releaseDecal(oldest);
    }
    const maxAlpha = 0.55 + Math.random() * 0.15;
    const d = this.obtainDecal();
    d.type = 'shadow_footprint';
    d.x = x + (Math.random() * 4 - 2);
    d.y = y + (Math.random() * 3 - 1.5);
    d.radius = (6 + Math.random() * 3) * 0.5;
    d.color = Math.random() > 0.4 ? 'rgba(10, 5, 8, 0.65)' : 'rgba(80, 5, 15, 0.55)';
    d.life = 1.6;
    d.maxAlpha = maxAlpha;
    d.alpha = maxAlpha;
    this.decals.push(d);
  }

  // Viscous Carmine Liquid Puddle (Claiming Pillar): Multi-lobed pooling blood puddle with wet specular gloss
  addCarminePuddle(x, y, radius) {
    if (this.decals.length >= 60) {
      const oldest = this.decals[0];
      const lastIdx = this.decals.length - 1;
      this.decals[0] = this.decals[lastIdx];
      this.decals.pop();
      this.releaseDecal(oldest);
    }
    const maxAlpha = 0.75 + Math.random() * 0.15;
    const d = this.obtainDecal();
    d.type = 'carmine_puddle';
    d.x = x;
    d.y = y;
    d.radius = radius;
    d.angle = (Math.random() - 0.5) * 0.6;
    d.spreadProgress = 0;
    d.spread = 0.35;
    d.color = Math.random() > 0.4 ? '#991b1b' : '#b91c1c';
    d.rimColor = '#450a0a';
    d.life = 15.0 + Math.random() * 5.0;
    d.maxAlpha = maxAlpha;
    d.alpha = maxAlpha;
    this.decals.push(d);
  }

  // Ethereal Shadow Smoke (Anti Shade): Subtle rising, billowing dark phantom smoke puff
  addShadowSmoke(x, y, dir = 1) {
    if (this.quality === 'low' || !this.canSpawnCosmetic()) return;
    const life = 0.65 + Math.random() * 0.45;
    const p = this.obtainCleanParticle();
    p.x = x + (Math.random() * 6 - 3);
    p.y = y + (Math.random() * 4 - 2);
    p.vx = -dir * (3 + Math.random() * 5) + (Math.random() * 4 - 2);
    p.vy = -10 - Math.random() * 12; // Ethereal slow upward drift
    p.gravity = 0;
    p.drag = 0.08;
    p.settled = false;
    p.radius = 2.2 + Math.random() * 1.8;
    p.expandRate = 2.0 + Math.random() * 1.2;
    p.color = SHADOW_SMOKE_COLORS[Math.floor(Math.random() * SHADOW_SMOKE_COLORS.length)];
    p.life = life;
    p.maxLife = life;
    p.initialAlpha = 0.35 + Math.random() * 0.15;
    p.alpha = p.initialAlpha;
    p.isSmoke = true;
    p.isRound = true;
    p.delay = 0;
    this.pushParticle(p);
  }

  // 8-Bit Muzzle Flash at weapon tip
  addMuzzleFlash(x, y, color = '#ffea00', size = 5) {
    this.addLight(x, y, size * 5, color, 0.09);
    for (let i = 0; i < 4; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 6 - 3);
      p.y = y + (Math.random() * 6 - 3);
      p.vx = (Math.random() - 0.5) * 30;
      p.vy = (Math.random() - 0.5) * 30;
      p.radius = size * (0.6 + Math.random() * 0.4);
      p.color = Math.random() > 0.4 ? '#ffffff' : color;
      p.life = 0.06;
      p.maxLife = 0.06;
      p.alpha = 1.0;
      this.particles.push(p);
    }
  }

  // Subtle base turret muzzle smoke (ultra-fine post-shot steam/smoke puff)
  addBaseMuzzleSmoke(x, y, vx, vy) {
    if (this.quality === 'low' || !this.canSpawnCosmetic()) return;
    const life = 0.35 + Math.random() * 0.25;
    this.pushParticle({
      x,
      y,
      vx,
      vy,
      radius: 1.1 + Math.random() * 0.4,
      expandRate: 1.04,
      color: '#cbd5e1',
      life,
      maxLife: life,
      initialAlpha: 0.20,
      alpha: 0.20,
      isSmoke: true
    });
  }

  // Industrial module touchdown dust puff (ground contact when cable landing completes)
  addModuleTouchdownDust(x, y) {
    if (this.quality === 'low' || !this.canSpawnCosmetic()) return;
    const count = this.quality === 'medium' ? 6 : 12;
    for (let i = 0; i < count; i++) {
      const dir = (i % 2 === 0 ? 1 : -1);
      const spd = 12 + Math.random() * 28;
      const life = 0.4 + Math.random() * 0.3;
      this.pushParticle({
        x: x + dir * (Math.random() * 20),
        y: y + (Math.random() * 4 - 2),
        vx: dir * spd,
        vy: -4 - Math.random() * 8,
        radius: 2.5 + Math.random() * 3.0,
        expandRate: 1.05,
        color: '#94a3b8',
        life,
        maxLife: life,
        initialAlpha: 0.35,
        alpha: 0.35,
        isDust: true
      });
    }
  }

  // Expanding Kinetic 8-Bit Shockwave Ring with Blast Deceleration & Particle Displacement
  addShockwave(x, y, maxRadius = 50, color = '#00f0ff', duration = 0.25) {
    const sw = {
      x, y,
      radius: 4,
      maxRadius,
      color,
      life: duration,
      maxLife: duration,
      update: function(dt) {
        const progress = 1 - (this.life / this.maxLife);
        // Kinetic blast wave deceleration (Cubic Out): supersonic initial burst that cushions at perimeter
        const blastProgress = 1 - Math.pow(1 - progress, 3);
        this.radius = 4 + blastProgress * (this.maxRadius - 4);
      }
    };
    this.specialEffects.push(sw);

    // Radial kinetic blast impulse on existing ambient smoke, embers and debris
    if (this.particles.length > 0 && maxRadius >= 24) {
      this.disperseNearbySmoke(x, y, maxRadius);
    }
  }

  // Radial blast impulse on existing ambient smoke, dust and debris
  disperseNearbySmoke(x, y, radius = 35) {
    if (this.particles.length === 0 || radius < 14) return;
    const impulseR = Math.min(220, radius * 1.35);
    const impulseRSq = impulseR * impulseR;
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      const dx = p.x - x;
      const dy = (p.y - y) * 2.0; // Lane isometric aspect ratio
      const distSq = dx * dx + dy * dy;
      if (distSq < impulseRSq && distSq > 12) {
        const dist = Math.sqrt(distSq);
        const force = (1.0 - dist / impulseR) * (radius >= 60 ? 55 : 28);
        p.vx += (dx / dist) * force;
        p.vy += (dy / dist) * force * 0.45;
      }
    }
  }

  // Expanding Refractive Shockwave Ring / Distortion Wave
  addDistortionRing(x, y, maxRadius = 45, duration = 0.35, color = '#00f0ff') {
    this.addShockwave(x, y, maxRadius, color, duration);
  }

  // 🌟 Celestial Orbital Drop-In Beam & Shockwave (Modular: usable for Gold Editions & future Slot Machine styles)
  addDropInEffect(x, groundY, radius = 20, style = 'gold') {
    const beamDuration = 0.38;
    const isGold = style === 'gold';
    const outerColor = isGold ? 'rgba(250, 204, 21, 0.45)' : 'rgba(0, 240, 255, 0.45)';
    const shockColor = isGold ? '#facc15' : '#00f0ff';
    const lightColor = isGold ? '#facc15' : '#00f0ff';
    const goldPalette = ['#fde047', '#facc15', '#fbbf24', '#ffffff', '#f59e0b'];

    // 1. Vertical Skybeam from canvas top to ground contact
    this.specialEffects.push({
      isDropInBeam: true,
      x,
      groundY,
      width: Math.max(26, radius * 1.8),
      life: beamDuration,
      maxLife: beamDuration,
      outerColor,
      coreColor: '#ffffff'
    });

    // 2. Ground Shockwave
    this.addShockwave(x, groundY, radius * 2.4, shockColor, 0.32);

    // 3. Dynamic Floor Light
    this.addLight(x, groundY, radius * 3.6, lightColor, 0.28);

    // 4. Outward bursting sparkle motes
    if (this.canSpawnCosmetic()) {
      const sparkCount = 14;
      for (let i = 0; i < sparkCount; i++) {
        const angle = (i / sparkCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const speed = 40 + Math.random() * 80;
        const pLife = 0.35 + Math.random() * 0.35;
        this.pushParticle({
          x: x + (Math.random() * 8 - 4),
          y: groundY + (Math.random() * 6 - 3),
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed * 0.45 - (15 + Math.random() * 35),
          gravity: 260,
          drag: 0.82,
          radius: 1.5 + Math.random() * 1.8,
          color: goldPalette[Math.floor(Math.random() * goldPalette.length)],
          life: pLife,
          maxLife: pLife,
          alpha: 1.0
        });
      }
    }

    // 5. Subtle Camera Impact Thump
    this.addScreenShake(2.2, 0.16);
  }

  // ✨ Subtle & Elegant Golden Midas Trail for moving Gold Edition units
  addMidasTrail(x, groundY, dir = 1, isAir = false) {
    if (!this.canSpawnCosmetic()) return;
    const goldPalette = ['#fbbf24', '#facc15', '#fef08a'];
    const pY = isAir ? groundY - 8 : groundY;
    for (let i = 0; i < 2; i++) {
      const life = 0.4 + Math.random() * 0.3;
      this.pushParticle({
        x: x + (Math.random() * 8 - 4),
        y: pY + (Math.random() * 6 - 3),
        vx: -dir * (6 + Math.random() * 10),
        vy: (Math.random() - 0.5) * 4 - 3, // gentle float
        drag: 0.85,
        radius: 1.2 + Math.random() * 0.9,
        color: goldPalette[Math.floor(Math.random() * goldPalette.length)],
        life,
        maxLife: life,
        alpha: 0.8
      });
    }
  }

  // ✨ Cinematic Anamorphic Horizontal Lens Flare Streak
  addAnamorphicFlare(x, y, width = 160, height = 3.5, duration = 0.16, color = '#38bdf8', options = null) {
    const p = this.obtainCleanParticle();
    p.x = x;
    p.y = y;
    p.vx = 0;
    p.vy = 0;
    p.width = width;
    p.height = height;
    p.color = color;
    p.life = duration;
    p.maxLife = duration;
    p.alpha = 1.0;
    p.isAnamorphicFlare = true;
    if (options?.isBastionFlare) {
      p.isBastionFlare = true;
    }
    this.pushParticle(p);
  }

  // 🔥 Glimmering Rising Thermal Embers & Ash Drift
  addRisingEmbers(x, y, count = 4, spreadX = 20) {
    if (this.quality === 'low') return;
    const emberColors = ['#fef08a', '#fbbf24', '#f97316', '#ef4444', '#f59e0b'];
    for (let i = 0; i < count; i++) {
      const life = 0.8 + Math.random() * 1.2;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * spreadX * 2 - spreadX);
      p.y = y + (Math.random() * 8 - 4);
      p.vx = (Math.random() - 0.5) * 14;
      p.vy = -22 - Math.random() * 32;
      p.swayFreq = 3.0 + Math.random() * 3.0;
      p.swayAmp = 10 + Math.random() * 10;
      p.swayPhase = Math.random() * Math.PI * 2;
      p.radius = 1.2 + Math.random() * 1.2;
      p.color = emberColors[Math.floor(Math.random() * emberColors.length)];
      p.life = life;
      p.maxLife = life;
      p.alpha = 1.0;
      p.isEmber = true;
      this.pushParticle(p);
    }
  }

  // ♨️ Atmospheric Heat Shimmer & Thermal Air Mirage
  addHeatShimmer(x, y, width = 24, height = 18, duration = 0.65) {
    if (this.quality === 'low') return;
    let activeShimmers = 0;
    for (let i = 0; i < this.particles.length; i++) {
      if (this.particles[i].isHeatShimmer) activeShimmers++;
    }
    if (activeShimmers >= 6) return;
    const p = this.obtainCleanParticle();
    p.x = x;
    p.y = y;
    p.vx = 0;
    p.vy = -18;
    p.width = width;
    p.height = height;
    p.waveOffset = Math.random() * Math.PI * 2;
    p.color = 'rgba(254, 215, 170, 0.22)';
    p.life = duration;
    p.maxLife = duration;
    p.alpha = 0.35;
    p.isHeatShimmer = true;
    this.pushParticle(p);
  }

  // ☁️ Soft Low-Lying Rising Vapor & Evaporation Cloud
  addVaporPuff(x, y, radius = 12, alpha = 0.25) {
    const life = 1.2 + Math.random() * 1.0;
    const p = this.obtainCleanParticle();
    p.x = x + (Math.random() * 10 - 5);
    p.y = y + (Math.random() * 6 - 3);
    p.vx = (Math.random() - 0.5) * 8;
    p.vy = -12 - Math.random() * 16;
    p.radius = radius;
    p.expandRate = 6.0;
    p.color = 'rgba(224, 242, 254, 0.25)';
    p.life = life;
    p.maxLife = life;
    p.initialAlpha = alpha;
    p.alpha = alpha;
    p.isVapor = true;
    this.pushParticle(p);
  }

  // 💨 Rolling Ground Dust & Track / Downwash Kickup
  addGroundDustKickup(x, y, dir = 1, size = 6) {
    const dustColors = ['#475569', '#334155', '#1e293b', '#64748b', '#78350f'];
    for (let i = 0; i < 2; i++) {
      const life = 0.35 + Math.random() * 0.35;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 6 - 3);
      p.y = y + (Math.random() * 4 - 2);
      p.vx = -dir * (14 + Math.random() * 24);
      p.vy = -6 - Math.random() * 12;
      p.radius = size * (0.6 + Math.random() * 0.6);
      p.expandRate = 4.5;
      p.drag = 1.2;
      p.color = dustColors[Math.floor(Math.random() * dustColors.length)];
      p.life = life;
      p.maxLife = life;
      p.initialAlpha = 0.38;
      p.alpha = 0.38;
      p.isDust = true;
      this.pushParticle(p);
    }
  }

  // Spectacular 8-Bit Hit Sparks & Debris
  addHitSparks(x, y, type = 'metal', count = 8, dirX = 0) {
    for (let i = 0; i < count; i++) {
      const spreadAngle = (dirX !== 0 ? (dirX < 0 ? Math.PI : 0) : Math.random() * Math.PI * 2) + (Math.random() - 0.5) * 1.6;
      const speed = 60 + Math.random() * 180;
      const life = 0.2 + Math.random() * 0.25;
      
      let colors;
      let gravity = 80;
      let size = 2 + Math.random() * 2.5;

      if (type === 'shield') {
        colors = ['#ffffff', '#00f0ff', '#38bdf8', '#0284c7'];
        gravity = 0;
      } else if (type === 'bio') {
        colors = ['#22c55e', '#84cc16', '#ef4444', '#991b1b', '#15803d'];
        gravity = 140;
      } else if (type === 'plasma') {
        colors = ['#ffffff', '#a855f7', '#00f0ff', '#eab308'];
        gravity = -20; // Float slightly
      } else {
        // Metal / standard
        colors = ['#ffffff', '#fef08a', '#f59e0b', '#ef4444', '#94a3b8'];
        gravity = 120;
      }

      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 6 - 3);
      p.y = y + (Math.random() * 6 - 3);
      p.vx = Math.cos(spreadAngle) * speed;
      p.vy = Math.sin(spreadAngle) * speed - 30;
      p.gravity = gravity;
      p.drag = 2.0;
      p.radius = size;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.life = life;
      p.maxLife = life;
      p.alpha = 1.0;
      if (type === 'metal') {
        p.groundY = y + 10 + (Math.random() * 8 - 4);
        p.bounces = 1;
        p.gravity = 240;
        p.drag = 1.2;
      }
      this.particles.push(p);
    }

    // Spawn Voronoi armor shards on metal hits
    if (type === 'metal') {
      this.addArmorChipping(x, y, '#64748b', dirX < 0 ? 'eastern' : 'western', Math.min(6, Math.floor(count * 0.6)));
    }
  }

  // 💥 Procedural Voronoi Armor Chipping & Metal Debris (Micro-Scale Physical Shards)
  addArmorChipping(x, y, color = '#64748b', team = 'western', count = 2) {
    const isWest = team === 'western';
    const hlColor = isWest ? '#38bdf8' : '#f87171';
    const numShards = Math.min(count, 3);
    for (let i = 0; i < numShards; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 35 + Math.random() * 110;
      // Irregular micro-scale shard geometry (Subtle, crisp pixel fragments)
      const sz = 0.75 + Math.random() * 1.1;
      const pts = [
        { x: -sz * (0.8 + Math.random() * 0.4), y: -sz * (0.8 + Math.random() * 0.4) },
        { x: sz * (0.8 + Math.random() * 0.4), y: -sz * (0.4 + Math.random() * 0.4) },
        { x: sz * (0.4 + Math.random() * 0.6), y: sz * (0.8 + Math.random() * 0.4) },
        { x: -sz * (0.5 + Math.random() * 0.5), y: sz * (0.6 + Math.random() * 0.4) }
      ];
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 6 - 3);
      p.y = y + (Math.random() * 6 - 3);
      p.vx = Math.cos(ang) * spd;
      p.vy = Math.sin(ang) * spd - 35;
      p.gravity = 240;
      p.drag = 0.8;
      p.groundY = y + 4 + Math.random() * 10;
      p.bounces = 2;
      p.isDebris = true;
      p.isShard = true;
      p.points = pts;
      p.rot = Math.random() * Math.PI * 2;
      p.vRot = (Math.random() - 0.5) * 14;
      p.color = Math.random() > 0.4 ? color : (isWest ? '#0284c7' : '#991b1b');
      p.highlightColor = hlColor;
      p.life = 1.1 + Math.random() * 0.6;
      p.maxLife = 1.7;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // Deflection Rating: a Bullet/Rocket/Grenade that struck a target but
  // bounced off (target's Deflection Rating roll succeeded) deals zero
  // damage. Kosmetisch auf dem Schlachtfeld animiert (Abprallen, zu Boden fallen,
  // Detonation an Panzerung je nach Projectile Type).
  addDeflection(x, y, dirX = 1, projectileType = 'Bullet', attacker = null) {
    const isHumanShooter = !!(
      attacker?.race === 'Human' ||
      attacker?.cardData?.race === 'Human' ||
      attacker?.faction === 'Human' ||
      attacker?.cardData?.faction === 'Human' ||
      attacker?.isHumanShooter
    );
    if (this.audio?.playDeflected) {
      this.audio.playDeflected({ x, isHumanShooter, attacker });
    } else {
      this.audio?.playRicochet?.({ x, isHumanShooter, attacker });
    }
    this.addLight(x, y, 36, '#38bdf8', 0.12);
    this.addShockwave(x, y, 22, '#38bdf8', 0.14);

    if (projectileType === 'Bullet') {
      // 1. Bright metallic ricochet sparks fan
      this.addHitSparks(x, y, 'metal', 10, dirX);

      // 2. Physical deflected bullet tracers bouncing off armor & falling to ground
      for (let b = 0; b < 3; b++) {
        const bAngle = (dirX < 0 ? 0.3 : Math.PI - 0.3) + (Math.random() - 0.5) * 0.8;
        const bSpeed = 130 + Math.random() * 100;
        const p = this.obtainCleanParticle();
        p.x = x + (Math.random() * 4 - 2);
        p.y = y + (Math.random() * 4 - 2);
        p.vx = Math.cos(bAngle) * bSpeed;
        p.vy = -Math.abs(Math.sin(bAngle) * bSpeed) - 40;
        p.gravity = 340;
        p.drag = 0.85;
        p.bounces = 2;
        p.groundY = y + (Math.random() * 10 + 6);
        p.radius = 2;
        p.color = Math.random() > 0.4 ? '#ffffff' : '#facc15';
        p.life = 0.45 + Math.random() * 0.2;
        p.maxLife = 0.65;
        p.alpha = 1.0;
        p.isDebris = true;
        this.pushParticle(p);
      }

      // 3. Rare high-velocity physical ricochet tracer (purely cosmetic, ~25% chance, rate-limited)
      const now = (typeof performance !== 'undefined') ? performance.now() : 0;
      if (Math.random() < 0.28 && (!this._lastRicochetTime || now - this._lastRicochetTime > 1800)) {
        this._lastRicochetTime = now;
        const rTrace = this.obtainCleanParticle();
        rTrace.x = x;
        rTrace.y = y;
        const ricAngle = (dirX < 0 ? 0.35 : Math.PI - 0.35) + (Math.random() - 0.5) * 0.45;
        const ricSpeed = 240 + Math.random() * 120;
        rTrace.vx = Math.cos(ricAngle) * ricSpeed;
        rTrace.vy = -Math.abs(Math.sin(ricAngle) * ricSpeed) - 90;
        rTrace.gravity = 620;
        rTrace.bounces = 1;
        rTrace.groundY = y + 12;
        rTrace.color = '#ffffff';
        rTrace.highlightColor = '#fbbf24';
        rTrace.isRicochet = true;
        rTrace.life = 0.35;
        rTrace.maxLife = 0.35;
        rTrace.alpha = 1.0;
        this.pushParticle(rTrace);
      }
    } else if (projectileType === 'Rocket') {
      // Rocket glances off armor hull and detonates outward harmlessly
      this.addExplosion(x + dirX * 10, y, 18, '#ff8800', 12);
      this.addShockwave(x, y, 26, '#f97316', 0.16);
      this.addScorchMark(x, y, 10);

      // Deflected fiery rocket fragments bouncing away
      for (let i = 0; i < 4; i++) {
        const angle = (dirX < 0 ? 0.2 : Math.PI - 0.2) + (Math.random() - 0.5) * 1.0;
        const spd = 70 + Math.random() * 90;
        const p = this.obtainCleanParticle();
        p.x = x + dirX * 6;
        p.y = y;
        p.vx = Math.cos(angle) * spd;
        p.vy = -Math.abs(Math.sin(angle) * spd) - 30;
        p.gravity = 280;
        p.drag = 0.8;
        p.bounces = 2;
        p.groundY = y + 10;
        p.radius = 2 + Math.random() * 1.5;
        p.color = ['#f97316', '#ef4444', '#facc15'][Math.floor(Math.random() * 3)];
        p.life = 0.35 + Math.random() * 0.2;
        p.maxLife = 0.55;
        p.alpha = 1.0;
        p.isDebris = true;
        this.pushParticle(p);
      }
    } else if (projectileType === 'Grenade') {
      // Heavy artillery shell glances off plating with heavy kinetic shock & fragments
      this.addShockwave(x, y, 30, '#f59e0b', 0.2);
      this.addExplosion(x + dirX * 12, y, 22, '#facc15', 16);
      this.addScorchMark(x, y, 12);

      // Deflected spinning heavy shell shards tumbling to the ground
      for (let i = 0; i < 5; i++) {
        const angle = (dirX < 0 ? 0.3 : Math.PI - 0.3) + (Math.random() - 0.5) * 0.9;
        const spd = 60 + Math.random() * 80;
        const p = this.obtainCleanParticle();
        p.x = x + dirX * 8;
        p.y = y;
        p.vx = Math.cos(angle) * spd;
        p.vy = -Math.abs(Math.sin(angle) * spd) - 40;
        p.gravity = 320;
        p.drag = 0.75;
        p.bounces = 2;
        p.groundY = y + 12;
        p.radius = 2.5 + Math.random() * 1.5;
        p.color = ['#cbd5e1', '#94a3b8', '#f59e0b', '#fde047'][Math.floor(Math.random() * 4)];
        p.life = 0.5 + Math.random() * 0.25;
        p.maxLife = 0.75;
        p.alpha = 1.0;
        p.isDebris = true;
        this.pushParticle(p);
      }
    }

    this.addDamageText(x, y, 'DEFLECTED', 'deflect');
  }

  // Accuracy: a shot that misses strikes the ground in front of, behind,
  // or beside the target, animated according to its projectileType.
  addProjectileMiss(x, y, projectileType = 'Bullet') {
    if (projectileType === 'Rocket') {
      this.addExplosion(x, y, 20, '#ff8800', 12);
      this.addShockwave(x, y, 28, 'rgba(255, 150, 50, 0.4)', 0.18);
      this.addScorchMark(x, y, 11);
      for (let i = 0; i < 6; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = 30 + Math.random() * 60;
        const p = this.obtainCleanParticle();
        p.x = x;
        p.y = y;
        p.vx = Math.cos(angle) * spd;
        p.vy = -Math.abs(Math.sin(angle) * spd) - 25;
        p.radius = 1.5 + Math.random() * 2;
        p.color = ['#ffffff', '#f97316', '#78350f'][Math.floor(Math.random() * 3)];
        p.life = 0.25 + Math.random() * 0.2;
        p.maxLife = 0.45;
        p.alpha = 1.0;
        this.pushParticle(p);
      }
    } else if (projectileType === 'Grenade') {
      this.addExplosion(x, y, 26, '#facc15', 16);
      this.addShockwave(x, y, 34, '#f97316', 0.22);
      this.addScorchMark(x, y, 14);

      // Dirt and shrapnel kicking up from ground crater
      for (let i = 0; i < 8; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = 35 + Math.random() * 75;
        const p = this.obtainCleanParticle();
        p.x = x;
        p.y = y;
        p.vx = Math.cos(angle) * spd;
        p.vy = -Math.abs(Math.sin(angle) * spd) - 30;
        p.radius = 2.0 + Math.random() * 2;
        p.color = ['#ffffff', '#facc15', '#f97316', '#78350f', '#451a03'][Math.floor(Math.random() * 5)];
        p.life = 0.3 + Math.random() * 0.2;
        p.maxLife = 0.5;
        p.alpha = 1.0;
        this.pushParticle(p);
      }
    } else if (projectileType === 'Energy') {
      this.addHitSparks(x, y, 'plasma', 8);
      this.addShockwave(x, y, 18, '#38bdf8', 0.14);
      this.addScorchMark(x, y, 8, 'rgba(56, 189, 248, 0.4)');
    } else {
      // Bullet: sparks and ground dust puff kicking up
      this.addHitSparks(x, y, 'metal', 7);
      this.addShockwave(x, y, 16, 'rgba(226, 232, 240, 0.4)', 0.12);
      for (let i = 0; i < 6; i++) {
        const p = this.obtainCleanParticle();
        p.x = x;
        p.y = y;
        p.vx = (Math.random() - 0.5) * 44;
        p.vy = -20 - Math.random() * 35;
        p.gravity = 280;
        p.radius = 1.5 + Math.random() * 1.5;
        p.color = ['#78350f', '#a16207', '#475569', '#d97706'][Math.floor(Math.random() * 4)];
        p.life = 0.3 + Math.random() * 0.15;
        p.maxLife = 0.45;
        p.alpha = 0.9;
        p.isDust = true;
        this.pushParticle(p);
      }
      this.addScorchMark(x, y, 7);
    }
  }

  // Renders a smooth, tapered ribbon trail along a point history buffer (Canvas 2D zero-alloc)
  _renderTaperedRibbonTrail(ctx, trailHistory, color = '#f97316', maxWidth = 3.8) {
    if (!trailHistory || trailHistory.length < 2) return;
    ctx.save();
    const count = trailHistory.length;
    for (let i = 0; i < count - 1; i++) {
      const p0 = trailHistory[i];
      const p1 = trailHistory[i + 1];
      const frac0 = 1 - (i / count);
      const frac1 = 1 - ((i + 1) / count);
      const w0 = Math.max(0.6, maxWidth * frac0);
      const w1 = Math.max(0.4, maxWidth * frac1);
      const alpha = frac0 * 0.72;

      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;

      ctx.beginPath();
      ctx.moveTo(p0.x + nx * w0, p0.y + ny * w0);
      ctx.lineTo(p1.x + nx * w1, p1.y + ny * w1);
      ctx.lineTo(p1.x - nx * w1, p1.y - ny * w1);
      ctx.lineTo(p0.x - nx * w0, p0.y - ny * w0);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha;
      ctx.fill();
    }
    ctx.restore();
  }

  // Ejecting 8-Bit Bouncing Brass Shell Casings
  addShellCasing(x, y, dirX = 1) {
    const angle = (dirX < 0 ? 0.3 : Math.PI - 0.3) + (Math.random() - 0.5) * 0.4;
    const speed = 70 + Math.random() * 60;
    const p = this.obtainCleanParticle();
    p.x = x;
    p.y = y;
    p.vx = Math.cos(angle) * speed;
    p.vy = -Math.sin(angle) * speed;
    p.gravity = 280;
    p.drag = 0.6;
    p.groundY = y + 14 + (Math.random() * 8 - 4);
    p.bounces = 2;
    p.radius = 1.8;
    p.color = '#facc15';
    p.life = 0.85;
    p.maxLife = 0.85;
    p.alpha = 1.0;
    this.pushParticle(p);
  }

  // Scorch mark left by heavy splash-damage impacts (Siege Tank, Ultralisk, etc.)
  addImpactCrater(x, y, radius = 16) {
    const r = Math.min(18, Math.max(8, radius));
    this.addScorchMark(x, y, r);
    this.addArtillerySpall(x, y);
  }

  // A handful of large angular metal/concrete chunks for a structure's death,
  // instead of the many small organic pixel shards used for living units.
  // Reuses the same gravity/bounce/settle physics as addUnitDeathShatter.
  addStructureDebris(x, y, radius = 24) {
    const colors = ['#1e293b', '#334155', '#475569', '#0f172a'];
    const groundLevel = y + radius * 0.3;
    for (let i = 0; i < 9; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 70;
      const life = 4.0 + Math.random() * 2.0;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * radius - radius / 2);
      p.y = y + (Math.random() * radius - radius / 2);
      p.vx = Math.cos(angle) * speed;
      p.vy = -50 - Math.random() * 60;
      p.gravity = 340;
      p.drag = 0.65;
      p.groundY = groundLevel + (Math.random() * 10 - 5);
      p.bounces = 1;
      p.settled = false;
      p.radius = 3 + Math.random() * 3.5;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.isDebris = true;
      p.life = life;
      p.maxLife = life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // 💥 Heavy Vehicle Dynamic Wreckage Debris (Overkill kills >= 25 dmg, 1 piece, 3 random archetypes)
  addVehicleWreckage(x, y, team = 'western', dirX = 1) {
    if (this.quality === 'low') return;
    // Global limit: max 2 active wreckage pieces across entire battlefield
    let activeWreckage = 0;
    for (let i = 0; i < this.particles.length; i++) {
      if (this.particles[i].isWreckage) activeWreckage++;
    }
    if (activeWreckage >= 2) return;

    const p = this.obtainCleanParticle();
    p.x = x;
    p.y = y;
    p.vx = (Math.random() - 0.5) * 80 - dirX * (50 + Math.random() * 50);
    p.vy = -(140 + Math.random() * 80);
    p.gravity = 380;
    p.drag = 0.72;
    p.groundY = y + 10 + (Math.random() * 6 - 3);
    p.bounces = 2;
    p.settled = false;
    p.rot = Math.random() * Math.PI * 2;
    p.vRot = (Math.random() - 0.5) * 12;
    p.life = 4.0;
    p.maxLife = 4.0;
    p.alpha = 1.0;
    p.team = team;
    p.isWreckage = true;
    p.wreckageType = Math.floor(Math.random() * 3); // 0: wheel/tread, 1: team armor shard, 2: severed barrel
    this.pushParticle(p);
  }

  // 🧬 Bio-Lumineszente Sul-Sporen & Chitin-Splatter (dezent bei schweren Treffern oder Tod)
  addSulBioSplatter(x, y, dirX = 1) {
    if (this.quality === 'low') return;
    const count = 3 + Math.floor(Math.random() * 3); // 3 to 5 micro droplets
    const isViolet = Math.random() < 0.5;
    const color = isViolet ? '#f5d0fe' : '#bef264';
    const hlColor = '#ffffff';

    for (let i = 0; i < count; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 8 - 4);
      p.y = y + (Math.random() * 8 - 4);
      const angle = (dirX < 0 ? 0.3 : Math.PI - 0.3) + (Math.random() - 0.5) * 1.2;
      const speed = 40 + Math.random() * 65;
      p.vx = Math.cos(angle) * speed;
      p.vy = -Math.abs(Math.sin(angle) * speed) - 35;
      p.gravity = 320;
      p.drag = 0.85;
      p.groundY = y + 8 + (Math.random() * 8 - 4);
      p.bounces = 1;
      p.settled = false;
      p.radius = 1.3 + Math.random() * 0.5;
      p.color = color;
      p.highlightColor = hlColor;
      p.isLiquidDroplet = true;
      p.life = 0.85 + Math.random() * 0.35; // 0.85 to 1.2s total
      p.maxLife = p.life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // 💥 Kinetic Artillery Spall Debris (High-speed flat shrapnel with deck rebound)
  addArtillerySpall(x, y) {
    if (this.quality === 'low') return;
    const count = 4 + Math.floor(Math.random() * 3); // 4-6 particles
    const groundLevel = y + 8 + (Math.random() * 8 - 4);
    for (let i = 0; i < count; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 8 - 4);
      p.y = y + (Math.random() * 4 - 2);
      const dir = Math.random() < 0.5 ? -1 : 1;
      p.vx = dir * (180 + Math.random() * 80); // 180..260 px/s flat lateral spray
      p.vy = -(20 + Math.random() * 30);      // -20..-50 px/s flat trajectory
      p.gravity = 500;
      p.drag = 0.4;
      p.groundY = groundLevel + (Math.random() * 6 - 3);
      p.bounces = 1;
      p.settled = false;
      p.radius = 1.2 + Math.random() * 0.8;
      p.color = Math.random() < 0.35 ? '#ea580c' : (Math.random() < 0.5 ? '#1e293b' : '#0f172a');
      p.isDebris = true;
      p.life = 1.3;
      p.maxLife = 1.3;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // 💨 Vehicle Exhaust Puff & Tread Dust on Acceleration from Standstill
  addTreadKickup(x, y, dirX = 1) {
    if (this.quality === 'low') return;
    // 1. Rear exhaust smoke ball
    const pSmoke = this.obtainCleanParticle();
    pSmoke.x = x - dirX * 18 + (Math.random() * 4 - 2);
    pSmoke.y = y - 6 + (Math.random() * 4 - 2);
    pSmoke.vx = -dirX * (20 + Math.random() * 15);
    pSmoke.vy = -10 - Math.random() * 8;
    pSmoke.radius = 2.5;
    pSmoke.expandRate = 3.5;
    pSmoke.color = '#334155';
    pSmoke.life = 0.4;
    pSmoke.maxLife = 0.4;
    pSmoke.initialAlpha = 0.55;
    pSmoke.alpha = 0.55;
    pSmoke.isSmoke = true;
    this.pushParticle(pSmoke);

    // 2. One-frame exhaust ignition spark
    const pSpark = this.obtainCleanParticle();
    pSpark.x = x - dirX * 18;
    pSpark.y = y - 6;
    pSpark.vx = -dirX * (35 + Math.random() * 20);
    pSpark.vy = -5 - Math.random() * 10;
    pSpark.radius = 1.5;
    pSpark.color = '#f97316';
    pSpark.life = 0.05;
    pSpark.maxLife = 0.05;
    pSpark.alpha = 0.9;
    this.pushParticle(pSpark);

    // 3. Dual flat tread dust puffs
    for (let i = 0; i < 2; i++) {
      const pDust = this.obtainCleanParticle();
      pDust.x = x - dirX * (12 + i * 5) + (Math.random() * 4 - 2);
      pDust.y = y + 7 + (Math.random() * 3 - 1);
      pDust.vx = -dirX * (10 + Math.random() * 15);
      pDust.vy = -4 - Math.random() * 6;
      pDust.radius = 2.0;
      pDust.expandRate = 2.0;
      pDust.color = '#94a3b8';
      pDust.life = 0.35 + Math.random() * 0.15;
      pDust.maxLife = pDust.life;
      pDust.initialAlpha = 0.4;
      pDust.alpha = 0.4;
      pDust.isDust = true;
      this.pushParticle(pDust);
    }
  }

  // ⚡ Short-Circuit Electric Arc Sparks for critical HP mechanical units (< 30% HP)
  addShortCircuitSpark(x, y) {
    if (this.quality === 'low') return;
    for (let i = 0; i < 2; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 6 - 3);
      p.y = y + (Math.random() * 6 - 3);
      p.vx = (Math.random() - 0.5) * 70;
      p.vy = -15 - Math.random() * 35;
      p.radius = 1.2;
      p.color = Math.random() < 0.5 ? '#38bdf8' : '#fef08a';
      p.life = 0.08 + Math.random() * 0.06;
      p.maxLife = p.life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
    const pSmoke = this.obtainCleanParticle();
    pSmoke.x = x + (Math.random() * 4 - 2);
    pSmoke.y = y;
    pSmoke.vx = (Math.random() - 0.5) * 10;
    pSmoke.vy = -12 - Math.random() * 8;
    pSmoke.radius = 1.8;
    pSmoke.expandRate = 1.5;
    pSmoke.color = '#475569';
    pSmoke.life = 0.35;
    pSmoke.maxLife = 0.35;
    pSmoke.initialAlpha = 0.45;
    pSmoke.alpha = 0.45;
    pSmoke.isSmoke = true;
    this.pushParticle(pSmoke);
  }

  // ⚡ Ground Arc Creep for Tesla strikes (branching high-voltage arcs skittering across deck)
  addTeslaGroundArcs(x, groundY) {
    if (this.quality === 'low') return;
    for (let i = 0; i < 2; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 8 - 4);
      p.y = groundY + (Math.random() * 4 - 2);
      p.isGroundArc = true;
      p.life = 0.14;
      p.maxLife = 0.14;
      p.alpha = 1.0;
      const spreadDir = i === 0 ? -1 : 1;
      const totalLen = (14 + Math.random() * 6) * spreadDir;
      const pts = [{ x: 0, y: 0 }];
      for (let s = 1; s <= 3; s++) {
        const segProgress = s / 3;
        pts.push({
          x: totalLen * segProgress + (Math.random() * 4 - 2),
          y: (Math.random() * 6 - 3)
        });
      }
      p.points = pts;
      p.color = '#00f0ff';
      p.highlightColor = '#ffffff';
      this.pushParticle(p);
    }
  }

  // 🧪 Sul Crawling Swarm Slime Glaze (organic wet ground sheen trailing loaves & pulp)
  addSulSlimeGlaze(x, y) {
    if (this.quality === 'low') return;
    let activeGlaze = 0;
    for (let i = 0; i < this.particles.length; i++) {
      if (this.particles[i].isSlimeGlaze) activeGlaze++;
    }
    if (activeGlaze >= 8) return;
    const p = this.obtainCleanParticle();
    p.x = x + (Math.random() * 4 - 2);
    p.y = y + (Math.random() * 2 - 1);
    p.vx = (Math.random() - 0.5) * 2;
    p.vy = 0;
    p.drag = 3.0;
    p.radius = 2.4 + Math.random() * 1.0;
    p.color = Math.random() < 0.6 ? '#65a30d' : '#4d7c0f';
    p.highlightColor = '#bef264';
    p.isSlimeGlaze = true;
    p.life = 1.35;
    p.maxLife = 1.35;
    p.initialAlpha = 0.45;
    p.alpha = 0.45;
    this.pushParticle(p);
  }

  addAcidSplash(x, y, count = 12) {
    const acidColors = ['#bef264', '#84cc16', '#a3e635', '#ecfccb', '#ffffff'];
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 90;
      const life = 0.3 + Math.random() * 0.3;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 8 - 4);
      p.y = y + (Math.random() * 8 - 4);
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed - 20;
      p.gravity = 120;
      p.drag = 2.0;
      p.radius = 2 + Math.random() * 2.5;
      p.color = acidColors[Math.floor(Math.random() * acidColors.length)];
      p.life = life;
      p.maxLife = life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
    // Lingering chemical reaction & steam sizzle at splash origin
    this.addAcidSizzle(x, y);
  }

  // 🧪 Sul Acid Sizzle & Chemical Steam Vents (boiling acidic reaction on armor & ground)
  addAcidSizzle(x, y, onUnit = false) {
    if (this.quality === 'low') return;
    let activeSizzle = 0;
    for (let i = 0; i < this.particles.length; i++) {
      if (this.particles[i].isAcidSizzle) activeSizzle++;
    }
    if (activeSizzle >= 10) return;

    // 1. Rising Chemical Steam / Vapor Wisp (pale lime to white acidic smoke)
    const pVapor = this.obtainCleanParticle();
    pVapor.x = x + (Math.random() * 8 - 4);
    pVapor.y = y + (onUnit ? (Math.random() * 6 - 3) : (Math.random() * 4 - 2));
    pVapor.vx = (Math.random() - 0.5) * 6;
    pVapor.vy = -10 - Math.random() * 12;
    pVapor.drag = 0.5;
    pVapor.radius = 2.0 + Math.random() * 1.5;
    pVapor.expandRate = 1.4;
    pVapor.color = Math.random() < 0.6 ? '#d9f99d' : '#ecfccb';
    pVapor.isVapor = true;
    pVapor.isAcidSizzle = true;
    pVapor.life = 0.65;
    pVapor.maxLife = 0.65;
    pVapor.initialAlpha = 0.38;
    pVapor.alpha = 0.0;
    this.pushParticle(pVapor);

    // 2. Boiling Micro-Bubble / Chemical Sizzle Specks
    const bubbleCount = onUnit ? 1 : 2;
    for (let b = 0; b < bubbleCount; b++) {
      if (activeSizzle + 1 + b >= 10) break;
      const pBubble = this.obtainCleanParticle();
      pBubble.x = x + (Math.random() * 8 - 4);
      pBubble.y = y + (onUnit ? (Math.random() * 8 - 4) : (Math.random() * 4 - 2));
      pBubble.vx = (Math.random() - 0.5) * 4;
      pBubble.vy = -3 - Math.random() * 4;
      pBubble.radius = 1.0 + Math.random() * 0.8;
      pBubble.color = '#bef264';
      pBubble.isAcidSizzle = true;
      pBubble.isBubble = true;
      pBubble.life = 0.4;
      pBubble.maxLife = 0.4;
      pBubble.initialAlpha = 0.75;
      pBubble.alpha = 0.75;
      this.pushParticle(pBubble);
    }
  }

  // 💥 Bastion Lock: Hot Molten Metal Burst & Light Smoke
  // Fires hot molten metal bullets that burst when hitting, spraying high-speed molten flecks
  // across a 28px radius and kicking up light smoke puffs.
  addBastionBurstingParticles(x, y, radius = 28) {
    const isLow = this.quality === 'low';
    const isMed = this.quality === 'medium';

    // 1. Viscous Molten Metal Droplets (Restrained pixel-art droplets with white-hot core, arcing & bouncing)
    // 5 on High, 3 on Med, 2 on Low
    const dropletCount = isLow ? 2 : (isMed ? 3 : 5);
    const moltenPalette = BASTION_MOLTEN_PALETTE;

    for (let i = 0; i < dropletCount; i++) {
      const angle = (i / dropletCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.45;
      const speed = 110 + Math.random() * 85;
      const life = 0.35 + Math.random() * 0.22;
      const pal = moltenPalette[i % moltenPalette.length];

      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 4 - 2);
      p.y = y + (Math.random() * 4 - 2);
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed * 0.85 - 20;
      p.gravity = 280;
      p.drag = 0.86;
      p.groundY = y + (Math.random() * 10 - 5);
      p.bounces = 1;
      p.settled = false;
      p.radius = 1.5;
      p.color = pal.base;
      p.highlightColor = pal.hl;
      p.isPixelDroplet = true;
      p.life = life;
      p.maxLife = life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }

    // 2. High-Speed Incandescent Ricochet Flecks (Searing pixel shrapnel streaks reaching full splash boundary)
    // 4 on High, 2 on Med, 0 on Low
    if (!isLow) {
      const ricochetCount = isMed ? 2 : 4;
      for (let r = 0; r < ricochetCount; r++) {
        const rAngle = (r / ricochetCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
        const rSpeed = 180 + Math.random() * 80;
        const rLife = 0.20 + Math.random() * 0.12;
        const pR = this.obtainCleanParticle();
        pR.x = x + (Math.random() * 2 - 1);
        pR.y = y + (Math.random() * 2 - 1);
        pR.vx = Math.cos(rAngle) * rSpeed;
        pR.vy = Math.sin(rAngle) * rSpeed * 0.9 - 10;
        pR.gravity = 160;
        pR.drag = 1.6;
        pR.radius = 1.5;
        pR.color = (r % 2 === 0) ? '#ffffff' : '#fbbf24';
        pR.highlightColor = '#38bdf8';
        pR.isPixelRicochet = true;
        pR.life = rLife;
        pR.maxLife = rLife;
        pR.alpha = 1.0;
        this.pushParticle(pR);
      }
    }

    // 3. Billowing Light Smoke Puff ("and cause light smoke" — 1 discreet pixel puff)
    if (!isLow) {
      const smokeColors = BASTION_SMOKE_COLORS;
      const pS = this.obtainCleanParticle();
      pS.x = x + (Math.random() * 6 - 3);
      pS.y = y + (Math.random() * 4 - 2);
      pS.vx = (Math.random() - 0.5) * 16;
      pS.vy = -18 - Math.random() * 14;
      pS.gravity = -15; // gentle buoyant rise
      pS.drag = 1.1;
      pS.radius = 2.8 + Math.random() * 1.5;
      pS.expandRate = 4.0;
      pS.color = smokeColors[Math.floor(Math.random() * smokeColors.length)];
      pS.life = 0.40 + Math.random() * 0.20;
      pS.maxLife = pS.life;
      pS.initialAlpha = 0.40;
      pS.alpha = 0.40;
      pS.isVapor = true;
      pS.isPixel = true;
      this.pushParticle(pS);
    }

    // 4. Subtle Expanding 28px Shockwave Ring telegraphing exact blast zone
    const waveRadius = radius || 28;
    this.addShockwave?.(x, y, waveRadius, 'rgba(56, 189, 248, 0.35)', 0.16);

    // 5. Molten Core Flare (compact flash)
    if (!isLow) {
      this.addAnamorphicFlare?.(x, y, 22, 1.8, 0.08, '#f97316');
      this.addLight?.(x, y, 22, '#38bdf8', 0.20);
    }
  }

  // 🛡️ Bastion Lock: Kinetic Shield Ricochet Pixels
  // Tiny pixel-art metal sparks/slugs glancing off the heavy chitin-alloy shields.
  addBastionShieldRicochet(x, y, dirX = 1, count = 2) {
    const isLow = this.quality === 'low';
    const num = isLow ? 1 : Math.max(1, count);
    for (let i = 0; i < num; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 4 - 2);
      p.y = y + (Math.random() * 6 - 3);

      // Bounce angle: ricochets glance off the forward shield face back toward incoming fire, arcing upward
      const bounceAngle = (dirX < 0 ? 0.35 : Math.PI - 0.35) + (Math.random() - 0.5) * 0.85;
      const spd = 160 + Math.random() * 140;
      p.vx = Math.cos(bounceAngle) * spd;
      p.vy = -Math.abs(Math.sin(bounceAngle) * spd) - (35 + Math.random() * 55);

      p.gravity = 380;
      p.drag = 1.1;
      p.radius = 1.5;
      p.color = (i % 2 === 0) ? '#ffffff' : '#fef08a';
      p.highlightColor = Math.random() > 0.4 ? '#facc15' : '#38bdf8';
      p.isPixelRicochet = true;
      p.life = 0.28 + Math.random() * 0.18;
      p.maxLife = p.life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // Branching 8-Bit Electric Plasma Arcs
  addElectricShock(x, y, radius = 35, color = '#00f0ff') {
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = radius * (0.3 + Math.random() * 0.7);
      const p = this.obtainCleanParticle();
      p.x = x + Math.cos(angle) * dist;
      p.y = y + Math.sin(angle) * dist;
      p.vx = (Math.random() - 0.5) * 40;
      p.vy = (Math.random() - 0.5) * 40;
      p.radius = 2.0;
      p.color = Math.random() > 0.4 ? '#ffffff' : color;
      p.life = 0.12;
      p.maxLife = 0.12;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // Rising Healing & Buff Sparkles
  addHealingSparkles(x, y, count = 8) {
    const healColors = ['#22c55e', '#86efac', '#4ade80', '#ffffff'];
    for (let i = 0; i < count; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 20 - 10);
      p.y = y + (Math.random() * 16 - 8);
      p.vx = (Math.random() - 0.5) * 20;
      p.vy = -35 - Math.random() * 45; // Float upward
      p.radius = 2.2;
      p.color = healColors[Math.floor(Math.random() * healColors.length)];
      p.life = 0.45 + Math.random() * 0.3;
      p.maxLife = 0.7;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // Disassembles a dying unit into its exact 8-bit pixel matrix shards that settle and lie on the ground!
  addUnitDeathShatter(unit) {
    if (!unit) return;

    // Sul Bio-Plasma Resonance Wave cosmetic disintegration (docs/BIO_PLASMA_PLAN.md §6):
    // Turns dissolving units into fine spore dust instead of pixel shards.
    if (this.bioPlasmaWaveActive) {
      if (!this.canSpawnCosmetic()) return;
      const count = this.quality === 'low' ? 8 : 20;
      const originX = unit.x;
      const originY = unit.isAir ? unit.y - 18 : unit.y;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = 8 + Math.random() * 32;
        this.pushParticle({
          x: originX + (Math.random() * 16 - 8),
          y: originY + (Math.random() * 16 - 8),
          vx: Math.cos(angle) * spd,
          vy: -20 - Math.random() * 40,
          radius: 1.5 + Math.random() * 2.5,
          color: ['#a855f7', '#c084fc', '#d946ef', '#475569', '#64748b'][i % 5],
          life: 0.8 + Math.random() * 0.6,
          maxLife: 1.4,
          alpha: 0.85
        });
      }
      return;
    }

    const unitType = unit.cardData?.unitType || unit.unitType || unit.name;
    if (!unitType) return;

    // Sul Pulp Pencil Trick Vanish: Rams scepter, vanishes in violet smoke & confetti sparks
    if (unitType === 'Pulp') {
      const count = this.quality === 'low' ? 10 : 24;
      const originX = unit.x;
      const originY = unit.y;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = 14 + Math.random() * 45;
        this.pushParticle({
          x: originX + (Math.random() * 12 - 6),
          y: originY + (Math.random() * 12 - 6),
          vx: Math.cos(angle) * spd,
          vy: -22 - Math.random() * 38,
          radius: 1.5 + Math.random() * 2.2,
          color: ['#7c3aed', '#a855f7', '#65a30d', '#f59e0b', '#dc2626'][i % 5],
          life: 0.6 + Math.random() * 0.4,
          maxLife: 1.0,
          alpha: 0.95
        });
      }
      this.addDistortionRing?.(originX, originY, 22, 0.25, '#a855f7');
      return;
    }

    const spriteData = getSpriteGrid(unitType, unit.race);
    if (!spriteData || !spriteData.grid) return;

    const r = unit.radius || 20;
    const pSize = (r * 2.2) / spriteData.gridSize;
    const teamBadgeColor = unit.team === 'western' ? '#00f0ff' : '#ff003c';
    const isWestern = unit.team === 'western';
    const originX = unit.x;
    const originY = unit.isAir ? unit.y - 18 : unit.y;
    const groundLevel = unit.y + (unit.isAir ? 24 : 6);
    const startX = originX - r * 1.1;
    const startY = originY - r * 1.1;

    const isLargeGrid = spriteData.gridSize >= 16;
    for (let gy = 0; gy < spriteData.gridSize; gy++) {
      for (let gx = 0; gx < spriteData.gridSize; gx++) {
        // High-density grids (20x20): checkerboard subsampling keeps visual coverage while cutting particle count by 50%
        if (isLargeGrid && ((gy + gx) % 2 !== 0)) continue;

        const col = isWestern ? gx : (spriteData.gridSize - 1 - gx);
        let pixelColor = spriteData.grid[gy][col];
        if (pixelColor) {
          if (pixelColor === 'TEAM') pixelColor = teamBadgeColor;

          const px = startX + gx * pSize;
          const py = startY + gy * pSize;

          const dx = px - originX;
          const dy = py - originY;
          const angle = Math.atan2(dy, dx) + (Math.random() - 0.5) * 0.9;
          const speed = 35 + Math.random() * 90;
          const pixelGround = groundLevel + (Math.random() * 16 - 8);
          const life = 1.8 + Math.random() * 1.2; // Stays on ground for 1.8 to 3.0 seconds

          const p = this.obtainCleanParticle();
          p.x = px;
          p.y = py;
          p.vx = Math.cos(angle) * speed + (Math.random() - 0.5) * 20;
          p.vy = -35 - Math.random() * 75;
          p.gravity = 360;
          p.drag = 0.7;
          p.groundY = pixelGround;
          p.bounces = 2;
          p.settled = false;
          p.radius = Math.max(1.4, pSize * (isLargeGrid ? 0.62 : 0.48));
          p.color = pixelColor;
          p.isDebris = true;
          p.life = life;
          p.maxLife = life;
          p.alpha = 1.0;
          this.particles.push(p);
        }
      }
    }
  }

  // 👑 Spectacular Midas Shatter death sequence for Gold Edition units
  addGoldUnitDeath(unit) {
    if (!unit) return;
    const unitType = unit.cardData?.unitType || unit.unitType || unit.name;
    if (!unitType) return;
    const spriteData = getSpriteGrid(unitType, unit.race);
    if (!spriteData || !spriteData.grid) return;

    const r = unit.radius || 20;
    const pSize = (r * 2.2) / spriteData.gridSize;
    const originX = unit.x;
    const originY = unit.isAir ? unit.y - 18 : unit.y;
    const groundLevel = unit.y + (unit.isAir ? 24 : 6);
    const startX = originX - r * 1.1;
    const startY = originY - r * 1.1;

    // Glistening gold & specular crystalline palette
    const goldShards = ['#fde047', '#facc15', '#fbbf24', '#ffffff', '#f59e0b', '#fef08a'];

    const isLargeGrid = spriteData.gridSize >= 16;
    for (let gy = 0; gy < spriteData.gridSize; gy++) {
      for (let gx = 0; gx < spriteData.gridSize; gx++) {
        if (isLargeGrid && ((gy + gx) % 2 !== 0)) continue;

        if (spriteData.grid[gy][gx]) {
          const px = startX + gx * pSize;
          const py = startY + gy * pSize;
          const dx = px - originX;
          const dy = py - originY;
          const angle = Math.atan2(dy, dx) + (Math.random() - 0.5) * 0.8;
          const speed = 40 + Math.random() * 95;
          const pixelGround = groundLevel + (Math.random() * 16 - 8);
          const life = 2.0 + Math.random() * 1.2;

          this.pushParticle({
            x: px,
            y: py,
            vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 20,
            vy: -40 - Math.random() * 80,
            gravity: 340,
            drag: 0.72,
            groundY: pixelGround,
            bounces: 2,
            settled: false,
            radius: Math.max(1.4, pSize * (isLargeGrid ? 0.62 : 0.48)),
            color: goldShards[Math.floor(Math.random() * goldShards.length)],
            isDebris: true,
            life,
            maxLife: life,
            alpha: 1.0
          });
        }
      }
    }

    // Expanding Golden Shockwave & Dynamic Ground Light
    this.addShockwave(originX, groundLevel, r * 2.6, '#facc15', 0.35);
    this.addLight(originX, groundLevel, r * 3.8, '#facc15', 0.3);

    // Ascension Light Motes: Golden essence ascending toward the sky
    if (this.canSpawnCosmetic()) {
      for (let i = 0; i < 14; i++) {
        const moteLife = 0.6 + Math.random() * 0.5;
        this.pushParticle({
          x: originX + (Math.random() * r * 1.4 - r * 0.7),
          y: groundLevel + (Math.random() * 10 - 5),
          vx: (Math.random() - 0.5) * 25,
          vy: -60 - Math.random() * 70,
          drag: 0.6,
          radius: 1.4 + Math.random() * 1.6,
          color: goldShards[Math.floor(Math.random() * goldShards.length)],
          life: moteLife,
          maxLife: moteLife,
          alpha: 0.95
        });
      }
    }

    this.addScreenShake(3.2, 0.2);
  }

  // ✨ Sul Looting Vulture: Ambient cascading gold dust drifting beneath the creature
  addGoldDust(x, y, count = 2) {
    if (this.quality === 'low') return;
    const goldColors = ['#fde047', '#facc15', '#eab308', '#d97706'];
    for (let i = 0; i < count; i++) {
      const life = 0.8 + Math.random() * 0.7;
      this.pushParticle({
        x: x + (Math.random() - 0.5) * 28,
        y: y + (Math.random() - 0.5) * 16,
        vx: (Math.random() - 0.5) * 14,
        vy: 16 + Math.random() * 24, // drifts gently downward
        gravity: 6,
        drag: 0.95,
        radius: 0.8 + Math.random() * 1.4,
        color: goldColors[(Math.random() * goldColors.length) | 0],
        life,
        maxLife: life,
        alpha: 0.85
      });
    }
  }

  // 🏛️ Sul Looting Vulture: One-time Midas Transmutation Beam
  addMidasTransmutationBeam(sx, sy, tx, ty, onHit = null) {
    this.addTracer(sx, sy, tx, ty, '#fde047', 0.28);
    this.addTracer(sx, sy, tx, ty, '#ffffff', 0.16);
    this.addLight(sx, sy, 50, '#facc15', 0.4);
    this.addShockwave(tx, ty, 38, '#fde047', 0.26);
    this.addHitSparks(tx, ty, 'gold', 18);
    this.addDamageText(tx, ty - 25, 'MIDAS MONUMENT', 'gold');
    this.addScreenShake(2.2, 0.16);
    if (onHit) onHit();
  }

  // 💥 Golden Statue: Heavy metallic ground crash when an airborne statue lands
  addStatueGroundCrash(x, y) {
    this.addShockwave(x, y, 52, '#fbbf24', 0.38);
    this.addShockwave(x, y, 28, '#ffffff', 0.22);
    this.addHitSparks(x, y, 'gold', 22);
    this.addStructureDebris(x, y, 26);
    this.addScreenShake(4.6, 0.26);
  }

  // ⚡ Stickman: Crackling electrical & friction sparks at the articulated joints
  addStickmanJointSparks(x, y, team = 'western') {
    const dir = team === 'western' ? 1 : -1;
    const joints = [
      { ox: -dir * 4, oy: -15 }, // Neck
      { ox: -dir * 6, oy: -11 }, // Shoulder
      { ox: -dir * 5, oy: 0 },   // Hip
      { ox: dir * 11, oy: 12 },  // Near Knee
      { ox: -dir * 10, oy: 12 }, // Far Knee
      { ox: dir * 1, oy: 23 }    // Ankle
    ];
    const colors = ['#ffffff', '#00e5ff', '#38bdf8', '#fbbf24', '#f59e0b', '#ea580c'];
    const count = this.quality === 'low' ? 2 : 4;
    // Pick 2 random joints per burst
    for (let j = 0; j < 2; j++) {
      const joint = joints[Math.floor(Math.random() * joints.length)];
      for (let i = 0; i < count; i++) {
        const p = this.obtainCleanParticle();
        const ang = Math.random() * Math.PI * 2;
        const spd = 20 + Math.random() * 45;
        p.x = x + joint.ox + (Math.random() - 0.5) * 3;
        p.y = y + joint.oy + (Math.random() - 0.5) * 3;
        p.vx = Math.cos(ang) * spd;
        p.vy = Math.sin(ang) * spd - 12;
        p.gravity = 60;
        p.drag = 0.93;
        p.radius = 1.0 + Math.random() * 0.9;
        p.color = colors[Math.floor(Math.random() * colors.length)];
        p.isEmber = true;
        p.life = 0.16 + Math.random() * 0.22;
        p.maxLife = p.life;
        p.alpha = 1.0;
        this.pushParticle(p);
      }
    }
  }

  // ✨ Stickman: Delayed, ultra-fine spark rain & particle shower spraying from ruptured shoulder joint
  addStickmanShoulderSparkShower(x, y, team = 'western', intensity = 1.0) {
    const dir = team === 'western' ? 1 : -1;
    const colors = ['#ffffff', '#e0f2fe', '#00e5ff', '#38bdf8', '#fef08a', '#facc15', '#f59e0b', '#ea580c'];
    const count = this.quality === 'low' ? 1 : (Math.random() < 0.65 ? 2 : 3);
    for (let i = 0; i < count; i++) {
      const p = this.obtainCleanParticle();
      // Upward fountain spray with slight bias outwards and backwards from shoulder joint
      const ang = (dir > 0 ? (Math.PI * 0.72 + Math.random() * 0.75) : (Math.PI * 0.28 - Math.random() * 0.75));
      const spd = (24 + Math.random() * 55) * intensity;
      p.x = x + (Math.random() - 0.5) * 4;
      p.y = y + (Math.random() - 0.5) * 4;
      p.vx = Math.cos(ang) * spd + (Math.random() - 0.5) * 16;
      p.vy = -28 - Math.random() * 48; // Upward fountain arc
      p.gravity = 115 + Math.random() * 30; // Heavy gravity curves them down into raining arcs
      p.drag = 0.94;
      p.radius = 0.5 + Math.random() * 0.65; // Ultra-fine micro-sparks
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.isEmber = true;
      p.swayFreq = 10 + Math.random() * 12;
      p.swayAmp = 6 + Math.random() * 6;
      p.life = 0.40 + Math.random() * 0.45;
      p.maxLife = p.life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }
  }

  // 💨 Stickman: Rising ground dust cloud when knees buckle and colossal chassis hits ground
  addStickmanDeathDustCloud(x, groundY, forwardDir = 1, size = 64) {
    const dustColors = ['#cbd5e1', '#94a3b8', '#a8a29e', '#64748b', '#78716c'];
    const count = this.quality === 'low' ? 7 : 16;
    for (let i = 0; i < count; i++) {
      const p = this.obtainCleanParticle();
      const offsetX = (Math.random() - 0.5) * (size * 0.75);
      p.x = x + offsetX;
      p.y = groundY - 2 + (Math.random() * 4 - 2);
      p.vx = (offsetX / (size * 0.35)) * (6 + Math.random() * 10);
      p.vy = -12 - Math.random() * 22; // Upward billow draft
      p.drag = 0.88;
      p.gravity = -2; // Gentle thermal rise
      p.radius = 4.5 + Math.random() * 4.0;
      p.expandRate = 3.6 + Math.random() * 2.4;
      p.color = dustColors[Math.floor(Math.random() * dustColors.length)];
      p.life = 2.4 + Math.random() * 1.4;
      p.maxLife = p.life;
      p.initialAlpha = 0.38 + Math.random() * 0.12;
      p.alpha = 0.0;
      p.isDust = true;
      this.pushParticle(p);
    }
    this.addShockwave(x, groundY, 36, 'rgba(148, 163, 184, 0.45)', 0.35);
  }

  // 💥 Stickman: Kinetic sparks erupting where the heavy railgun lance slams into the bedrock
  addStickmanWeaponImpactSparks(x, groundY, forwardDir = 1) {
    const sparkColors = ['#ffffff', '#fef08a', '#facc15', '#f59e0b', '#ea580c', '#ef4444'];
    const count = this.quality === 'low' ? 5 : 12;
    for (let i = 0; i < count; i++) {
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() - 0.5) * 6;
      p.y = groundY - 2 - Math.random() * 4;
      // High-velocity ricochet fan: spraying forward and upward
      const angle = (forwardDir > 0) ? (-Math.PI * (0.15 + Math.random() * 0.45)) : (-Math.PI * (0.40 + Math.random() * 0.45));
      const spd = 45 + Math.random() * 85;
      p.vx = Math.cos(angle) * spd;
      p.vy = Math.sin(angle) * spd;
      p.drag = 0.92;
      p.gravity = 180;
      p.radius = 0.8 + Math.random() * 1.4;
      p.color = sparkColors[Math.floor(Math.random() * sparkColors.length)];
      p.life = 0.35 + Math.random() * 0.35;
      p.maxLife = p.life;
      p.initialAlpha = 1.0;
      p.alpha = 1.0;
      p.isEmber = true;
      this.pushParticle(p);
    }
  }

  // 🪨 Stickman: Oldest dead kneeling obstacle crumbles into metallic debris & dust when a 4th Stickman dies
  addStickmanObstacleCrumble(unit) {
    if (!unit) return;
    const originX = unit.x;
    const originY = unit.y;
    const r = unit.radius || 24;
    const groundLevel = originY + 24;
    const isWestern = (unit.team || 'western') === 'western';
    const teamBadgeColor = isWestern ? '#00f0ff' : '#ff003c';
    const deathPose = unit.deathPose || 1;
    const obsKey = (deathPose === 2) ? 'Stickman_Obstacle_2' : ((deathPose === 3) ? 'Stickman_Obstacle_3' : 'Stickman_Obstacle');
    const hdData = typeof getHDSpriteGrid === 'function' ? getHDSpriteGrid(obsKey) : null;

    if (hdData && hdData.grid) {
      const gridSize = hdData.gridSize || 48;
      const drawSize = 98;
      const pSize = drawSize / gridSize;
      const startX = originX - drawSize * 0.5;
      const startY = originY + 24 - drawSize;
      const step = this.quality === 'low' ? 3 : 2;

      for (let gy = 0; gy < gridSize; gy += step) {
        for (let gx = 0; gx < gridSize; gx += step) {
          const col = isWestern ? gx : (gridSize - 1 - gx);
          let pixelColor = hdData.grid[gy]?.[col];
          if (!pixelColor) continue;
          if (pixelColor === 'TEAM') pixelColor = teamBadgeColor;

          const px = startX + gx * pSize;
          const py = startY + gy * pSize;
          const dx = px - originX;
          const life = 1.4 + Math.random() * 1.1;

          const p = this.obtainCleanParticle();
          p.x = px;
          p.y = py;
          p.vx = (dx * 0.8) + (Math.random() - 0.5) * 38;
          p.vy = -15 - Math.random() * 45;
          p.gravity = 380;
          p.drag = 0.72;
          p.groundY = groundLevel + (Math.random() * 12 - 6);
          p.bounces = 2;
          p.settled = false;
          p.radius = Math.max(1.5, pSize * 0.75);
          p.color = pixelColor;
          p.isDebris = true;
          p.life = life;
          p.maxLife = life;
          p.alpha = 1.0;
          this.pushParticle(p);
        }
      }
    }

    this.addStructureDebris(originX, originY + 8, r);
    for (let i = 0; i < 3; i++) {
      this.addVaporPuff?.(originX + (Math.random() * 24 - 12), originY + 6 + (Math.random() * 12 - 6), 16, 0.45);
    }
  }

  // ⚡ Human Hacker Death: Cybernetic overload, visor fracture, debris shards & SYSTEM FAILED glitch
  addHackerDeath(unit, audio = null) {
    if (!unit) return;
    if (!unit.name) unit.name = 'Hacker';
    if (!unit.race) unit.race = 'Human';
    const originX = unit.x;
    const originY = unit.y;

    // 1. SYSTEM FAILED Glitch / Text alert
    this.addDamageText?.(originX, originY - 24, 'SYSTEM FAILED', 'crit');
    this.addShockwave?.(originX, originY, 40, '#00f0ff', 0.35);

    // 2. Visor burst & Electric sparks
    this.addHitSparks?.(originX, originY - 8, 'plasma', 16);
    this.addHitSparks?.(originX, originY, 'metal', 12);

    // 3. Lingering Cyan/Dark smoke plume
    this.addLingeringSmokeColumn?.(originX, originY, 2.5, 1.0);

    // 4. Standard pixel shatter debris of cyber parts and saffron robe voxels
    this.addUnitDeathShatter(unit);
  }

  // 🐉 Sul Looting Vulture: Majestic celestial death choreography
  // Phase 1 (0.0s - 0.88s): Vertical 360-degree aerodynamic loop in the air with golden slipstream
  // Phase 2 (0.88s - 1.42s): Snaps straight vertically into zenith, body wave oscillation straightens taut
  // Phase 3 (1.42s - 1.80s): Freezes motion at apex, completely straight and vertical, solar corona flares up
  // Phase 4 (1.80s): Shatters into dense glittering golden stardust that gently glides and flutters to the ground
  addLootingVultureDeath(unit, audio = null) {
    if (!unit) return;
    const unitType = unit.cardData?.unitType || unit.name || 'LootingVulture';
    const renderRace = unit.race || 'Sul';
    const team = unit.team || 'western';
    const isWestern = team === 'western';
    const forwardDir = isWestern ? 1 : -1;
    const startX = unit.x;
    const startY = unit.isAir ? unit.y - 18 : unit.y;
    const groundLevel = unit.y + 24;
    const q = this.quality || 'high';
    const engine = this;
    const soundEngine = audio || this.audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);

    const flightDuration = 1.80; // Total active dragon sequence before disintegration
    const drawSize = (unit.radius || 34) * 2.1;
    const loopRadius = 36;
    const climbHeight = 115;

    // Sprite modular bitmaps
    const bodyBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('LootingVulture_Body', renderRace, team) : null;
    const pyramidBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('LootingVulture_Pyramid', renderRace, team) : null;
    const headBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('LootingVulture_Head', renderRace, team) : null;
    const tailBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('LootingVulture_Tail', renderRace, team) : null;
    const wholeBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('LootingVulture', renderRace, team) : null;
    const spriteData = getSpriteGrid('LootingVulture', renderRace);

    const vultureDeath = {
      isLootingVultureDeath: true,
      life: flightDuration,
      maxLife: flightDuration,
      x: startX,
      y: startY,
      startX: startX,
      startY: startY,
      apexX: startX + forwardDir * 28,
      apexY: startY - climbHeight,
      groundLevel: groundLevel,
      angle: 0,
      waveScale: 1.0,
      shiver: 0,
      isFrozen: false,
      freezeTimer: 0,
      phase: 1, // 1: Looping, 2: Vertical Surge, 3: Freeze
      sparkTimer: 0,
      stretchProg: 0,
      headUnwind: 0,
      pyramidUnwind: 0,
      bodyUnwind: 0,
      tailUnwind: 0,
      untwistPhase: 0,
      hasSnapped: false,
      forwardDir: forwardDir,
      isWestern: isWestern,
      team: team,
      drawSize: drawSize,
      bodyBitmap: bodyBitmap,
      pyramidBitmap: pyramidBitmap,
      headBitmap: headBitmap,
      tailBitmap: tailBitmap,
      wholeBitmap: wholeBitmap,
      spriteData: spriteData,

      update: function(dt) {
        const elapsed = this.maxLife - this.life;

        if (elapsed < 0.88) {
          // ── Phase 1 (0.0s - 0.88s): Vertical 360° Death Loop-de-loop ──
          this.phase = 1;
          this.isFrozen = false;
          this.waveScale = 1.0;
          this.stretchProg = 0;
          this.headUnwind = 0;
          this.pyramidUnwind = 0;
          this.bodyUnwind = 0;
          this.tailUnwind = 0;
          this.untwistPhase = 0;
          const p1 = Math.min(1.0, elapsed / 0.88);
          const phi = p1 * Math.PI * 2;

          // Trajectory: 360° circle in air with forward drift
          const driftX = this.forwardDir * (p1 * 24 + loopRadius * Math.sin(phi));
          const driftY = -loopRadius * (1 - Math.cos(phi));
          this.x = this.startX + driftX;
          this.y = this.startY + driftY;

          // Aerodynamic tangent angle (nose leading in direction of travel)
          this.angle = -phi * this.forwardDir;

          // Golden speed motes trailing in slipstream
          this.sparkTimer += dt;
          if (this.sparkTimer >= 0.035 && engine.canSpawnCosmetic()) {
            this.sparkTimer = 0;
            engine.pushParticle({
              x: this.x + (Math.random() - 0.5) * 16,
              y: this.y + (Math.random() - 0.5) * 16,
              vx: -Math.cos(this.angle) * 18 + (Math.random() - 0.5) * 12,
              vy: -Math.sin(this.angle) * 18 + (Math.random() - 0.5) * 12,
              gravity: 12,
              drag: 0.92,
              radius: 1.2 + Math.random() * 1.4,
              color: Math.random() > 0.4 ? '#fde047' : '#ffffff',
              isEmber: true,
              swayFreq: 5,
              swayAmp: 10,
              life: 0.5 + Math.random() * 0.3,
              maxLife: 0.8,
              alpha: 0.85
            });
          }
        } else if (elapsed < 1.42) {
          // ── Phase 2 (0.88s - 1.42s): Rocket Vertical Surge, Unwinding & Full Extension ──
          // "dass der drache sich dabei entwindet und gerade ausstreckt"
          this.phase = 2;
          this.isFrozen = false;
          const p2 = Math.min(1.0, (elapsed - 0.88) / 0.54);

          // 1. Dynamic Longitudinal Elongation (Stretching straight out)
          // As it climbs, the spine expands outward from coiled posture to full needle extension
          const easeOut = Math.sin(p2 * Math.PI * 0.5);
          this.stretchProg = easeOut; // 0.0 -> 1.0 (doubles distance between segments)

          // 2. Sequential Unwinding Wave (Traveling from head -> pyramid -> body -> tail)
          // Head uncoils first, tail uncurls and snaps last
          this.headUnwind = Math.min(1.0, p2 / 0.38);
          this.pyramidUnwind = Math.min(1.0, Math.max(0, (p2 - 0.10) / 0.40));
          this.bodyUnwind = Math.min(1.0, Math.max(0, (p2 - 0.22) / 0.44));
          this.tailUnwind = Math.min(1.0, Math.max(0, (p2 - 0.36) / 0.52));

          // Overall wave scale dampens as segments unwind
          this.waveScale = Math.max(0, 1.0 - p2);

          // 3. Axial Corkscrew Untwist (Releasing coiled torsion)
          // 360-degree axial unrolling around its spine
          this.untwistPhase = (1.0 - p2) * Math.PI * 2.0;

          // Pitch snaps smoothly straight up to zenith (-90 degrees)
          const pitchProg = Math.min(1.0, p2 * 2.8);
          const targetAngle = -Math.PI / 2 * this.forwardDir;
          this.angle = targetAngle * pitchProg;

          // Rocket surge climb with smooth decelerating ease
          const loopExitX = this.startX + this.forwardDir * 24;
          const loopExitY = this.startY;
          this.x = loopExitX + this.forwardDir * 4 * easeOut;
          this.y = loopExitY - climbHeight * easeOut;

          // Tension Snap: when tail uncurls into full lock (p2 > 0.90)
          if (p2 > 0.90 && !this.hasSnapped) {
            this.hasSnapped = true;
            engine.addShockwave(this.x, this.y, 28, '#ffffff', 0.18);
            soundEngine?.playSample?.('more/motions_and_transitions/swish_1.m4a', { volume: 0.6 });
          }

          // Golden thrust jets shooting downward from dragon flanks
          this.sparkTimer += dt;
          if (this.sparkTimer >= 0.025 && engine.canSpawnCosmetic()) {
            this.sparkTimer = 0;
            engine.pushParticle({
              x: this.x + (Math.random() - 0.5) * 18,
              y: this.y + 12 + Math.random() * 10,
              vx: (Math.random() - 0.5) * 20,
              vy: 60 + Math.random() * 50,
              gravity: 40,
              drag: 0.85,
              radius: 1.4 + Math.random() * 1.6,
              color: Math.random() > 0.5 ? '#facc15' : '#fef08a',
              isEmber: true,
              life: 0.35 + Math.random() * 0.2,
              maxLife: 0.55,
              alpha: 0.95
            });
          }
        } else {
          // ── Phase 3 (1.42s - 1.80s): Golden Freeze Moment (Fully Stretched & Straight) ──
          this.phase = 3;
          this.isFrozen = true;
          this.freezeTimer += dt;
          this.waveScale = 0; // completely taut and straight
          this.stretchProg = 1.0; // 100% elongated straight posture
          this.headUnwind = 1.0;
          this.pyramidUnwind = 1.0;
          this.bodyUnwind = 1.0;
          this.tailUnwind = 1.0;
          this.untwistPhase = 0;
          this.angle = -Math.PI / 2 * this.forwardDir; // strictly vertical
          this.x = this.startX + this.forwardDir * 28;
          this.y = this.startY - climbHeight;

          // Pre-shatter crystalline vibration in final 150ms
          if (elapsed > 1.65) {
            this.shiver = (Math.random() - 0.5) * 1.6;
          } else {
            this.shiver = 0;
          }
        }
      },

      onComplete: function() {
        // ── Phase 4: Golden Stardust Disintegration & Ground Glide ──
        const apexX = this.x;
        const apexY = this.y;
        const gLevel = this.groundLevel || (this.startY + 24);

        // 1. Audio: Celestial transmute chime / golden crystallization shatter
        soundEngine?.playSample?.('even more sounds/Hero_Kill_001.m4a', {
          volume: 0.85,
          category: 'special'
        }) || soundEngine?.playUnitSound?.('LootingVulture', 'death');

        // 2. Solar shockwave ring, radiant light & screen shake
        engine.addShockwave(apexX, apexY, 62, '#fde047', 0.35);
        engine.addShockwave(apexX, apexY, 32, '#ffffff', 0.22);
        if (q !== 'low') {
          engine.addLight?.(apexX, apexY, 95, '#facc15', 0.45);
        }
        engine.addScreenShake?.(3.4, 0.22);

        // 3. Massive, dense glittering golden stardust cloud (drifting and fluttering to ground)
        const dustCount = q === 'ultra' ? 160 : (q === 'high' ? 120 : (q === 'medium' ? 65 : 28));
        const goldColors = ['#ffffff', '#fef08a', '#fde047', '#facc15', '#eab308', '#ca8a04', '#d97706', '#fbbf24'];

        // Dragon length spans ~110px vertically (from apexY - 55 to apexY + 55)
        for (let i = 0; i < dustCount; i++) {
          const u = Math.random();
          const spineY = apexY - 55 + u * 110 + (Math.random() - 0.5) * 8;
          const spineX = apexX + (Math.random() - 0.5) * 18;

          // Radial dispersion with buoyant fluttering physics
          const burstAngle = Math.random() * Math.PI * 2;
          const burstSpeed = 10 + Math.random() * 45;
          const vx = Math.cos(burstAngle) * burstSpeed;
          const vy = Math.sin(burstAngle) * burstSpeed * 0.5 - 6;

          const pLife = 2.0 + Math.random() * 1.6; // 2.0s - 3.6s graceful floating glide
          const col = goldColors[Math.floor(Math.random() * goldColors.length)];

          engine.pushParticle({
            x: spineX,
            y: spineY,
            vx: vx,
            vy: vy,
            gravity: 24 + Math.random() * 20, // gentle gliding gravity towards ground
            drag: 0.88 + Math.random() * 0.07, // buoyant air resistance
            radius: 0.9 + Math.random() * 1.9,
            color: col,
            isEmber: true,
            isGoldDustGlide: true,
            swayFreq: 3.2 + Math.random() * 3.8, // fluttering leaf sway
            swayAmp: 14 + Math.random() * 18,
            swayPhase: Math.random() * Math.PI * 2,
            sparkleOffset: Math.random() * Math.PI * 2,
            isStarGlint: Math.random() < 0.28,
            groundY: gLevel + (Math.random() * 14 - 7),
            bounces: 0,
            settled: false,
            life: pLife,
            maxLife: pLife,
            alpha: 0.95
          });
        }
      }
    };

    this.specialEffects.push(vultureDeath);
  }

  // 💣 Human Hyper Panzer: Catastrophic ammunition cookoff, radius-5 fiery shockwave & diesel smog column
  addHyperPanzerDeath(unit, audio = null) {
    if (!unit) return;
    const originX = unit.x;
    const originY = unit.y;
    const r = unit.radius || 26;

    // 1. Core pixel voxel disassembly & heavy armor debris
    this.addUnitDeathShatter(unit);
    this.addStructureDebris(originX, originY, r * 1.4);

    // 2. Dual shockwaves: inner white blast + outer fiery perimeter (120px)
    this.addShockwave(originX, originY, 120, '#ff4400', 0.45);
    this.addShockwave(originX, originY, 65, '#ffffff', 0.25);
    if (this.quality !== 'low') {
      this.addDistortionRing?.(originX, originY, 80, 0.4, '#f97316');
    }

    // 3. Multi-cluster catastrophic fireball explosions
    this.addExplosion(originX, originY, 70, '#ff6600', this.quality === 'low' ? 18 : 36, 5);
    if (this.quality !== 'low') {
      this.addExplosion(originX - 18, originY - 8, 45, '#fbbf24', 20, 3);
      this.addExplosion(originX + 18, originY + 6, 45, '#ef4444', 20, 3);
    }

    // 4. Lingering heavy diesel smoke column & blast scorch
    this.addLingeringSmokeColumn?.(originX, originY, 5.0, 2.2);
    this.addScorchMark?.(originX, originY, 32);

    // 5. Heavy screen shake & catastrophic blast sound
    this.addScreenShake?.(6.0, 0.35);
    audio?.playExplosion?.('large', { x: originX });
  }

  // 🦅 Sul Parasite Vulture: Fleshy Bio-Burst, Feathers & Sul Death Screech
  addParasiteVultureDeath(unit, audio = null) {
    if (!unit) return;
    const originX = unit.x;
    const originY = unit.y;

    // 1. Core pixel voxel disassembly
    this.addUnitDeathShatter(unit);

    // 2. Audio: Sul scream / bio death burst
    audio?.playUnitSound?.('ParasiteVulture', 'death');

    // 3. Shockwave & Crimson Bio blast
    this.addShockwave(originX, originY, 50, '#dc2626', 0.35);
    this.addShockwave(originX, originY, 26, '#ffffff', 0.20);
    this.addLingeringSmokeColumn?.(originX, originY, 3.5, 1.5);

    // 4. Feathers and bio viscera (Pooled zero-alloc execution)
    const featherCount = this.quality === 'low' ? 8 : 18;
    for (let i = 0; i < featherCount; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 25 + Math.random() * 70;
      const p = this.obtainCleanParticle();
      p.x = originX + (Math.random() - 0.5) * 16;
      p.y = originY + (Math.random() - 0.5) * 16;
      p.vx = Math.cos(ang) * spd;
      p.vy = Math.sin(ang) * spd - 35;
      p.gravity = 30;
      p.drag = 0.92;
      p.radius = 1.8 + Math.random() * 1.8;
      p.color = FEATHER_COLORS[Math.floor(Math.random() * FEATHER_COLORS.length)];
      p.isEmber = true;
      p.swayFreq = 3.0 + Math.random() * 2.0;
      p.swayAmp = 12 + Math.random() * 10;
      const l = 1.2 + Math.random() * 0.8;
      p.life = l;
      p.maxLife = 2.0;
      p.alpha = 0.9;
      this.pushParticle(p);
    }

    // 5. Visceral bio droplets (Pooled zero-alloc execution)
    const bioCount = this.quality === 'low' ? 10 : 22;
    for (let i = 0; i < bioCount; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 40 + Math.random() * 90;
      const p = this.obtainCleanParticle();
      p.x = originX + (Math.random() - 0.5) * 12;
      p.y = originY + (Math.random() - 0.5) * 12;
      p.vx = Math.cos(ang) * spd;
      p.vy = Math.sin(ang) * spd - 20;
      p.gravity = 60;
      p.radius = 2.2 + Math.random() * 2.2;
      p.color = Math.random() > 0.4 ? '#991b1b' : '#dc2626';
      const l = 0.6 + Math.random() * 0.5;
      p.life = l;
      p.maxLife = 1.1;
      p.alpha = 0.95;
      this.pushParticle(p);
    }
  }

  // 🩸 Sul Harvesting Pillar: Catastrophic Blood-Plasma & Rainbow Spire Shatter Death Sequence
  addHarvestingBloodPlasmaExplosion(unit) {
    if (!unit) return;
    const originX = unit.x;
    const originY = unit.isAir ? unit.y - 18 : unit.y;
    const groundLevel = unit.y + (unit.isAir ? 24 : 6);
    const r = unit.radius || 20;

    // 1. Core pixel voxel disassembly
    this.addUnitDeathShatter(unit);
    this.addStructureDebris(originX, groundLevel, r * 1.2);

    // 2. High-velocity Iridescent Rainbow Spire Shards (Ballistic parabolas with ground bounce)
    const rainbowColors = ['#7c3aed', '#2563eb', '#059669', '#facc15', '#dc2626', '#e879f9'];
    const shardCount = this.quality === 'low' ? 12 : 28;
    for (let i = 0; i < shardCount; i++) {
      const angle = (i / shardCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const speed = 40 + Math.random() * 120;
      const shardLife = 1.8 + Math.random() * 1.4;
      this.pushParticle({
        x: originX + (Math.random() - 0.5) * 12,
        y: originY - (Math.random() * 18),
        vx: Math.cos(angle) * speed,
        vy: -50 - Math.random() * 95,
        gravity: 360,
        drag: 0.74,
        groundY: groundLevel + (Math.random() * 14 - 7),
        bounces: 2,
        settled: false,
        radius: 1.6 + Math.random() * 1.8,
        color: rainbowColors[i % rainbowColors.length],
        isDebris: true,
        life: shardLife,
        maxLife: shardLife,
        alpha: 1.0
      });
    }

    // 3. Bichromatic Blood-Plasma Shockwaves: Crimson Blood & Violet Plasma
    this.addShockwave(originX, groundLevel, r * 2.8, '#991b1b', 0.38);
    this.addShockwave(originX, groundLevel, r * 2.1, '#c084fc', 0.24);
    this.addLight(originX, groundLevel, r * 3.5, '#a855f7', 0.35);

    // 4. Acid & Plasma Splatter
    this.addAcidSplash(originX, groundLevel, 24);
    this.addHitSparks(originX, originY, 'plasma', 18);

    // 5. Lingering battlefield haze
    this.addLingeringSmokeColumn(originX, groundLevel, 3.5, 1.2);
    this.addScreenShake(3.6, 0.25);
  }

  // 🩸 Sul Claiming Pillar: Catastrophic Carmine Liquid Eruption & Viscous Blood Puddles
  addClaimingPillarBurst(unit) {
    if (!unit) return;
    const originX = unit.x;
    const originY = unit.y - 12;
    const groundLevel = unit.y + 14;
    const r = unit.radius || 26;

    // 1. Core basalt voxel destruction & heavy stone debris
    this.addUnitDeathShatter(unit);
    this.addStructureDebris(originX, groundLevel, r * 1.35);

    // 2. High-Pressure Ballistic Carmine Liquid Jets (stretched tear-droplet deformation)
    const dropletCount = this.quality === 'low' ? 16 : 36;
    const carminePalette = ['#7f1d1d', '#991b1b', '#b91c1c', '#dc2626', '#ef4444'];
    for (let i = 0; i < dropletCount; i++) {
      const angle = (i / dropletCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
      const speed = 55 + Math.random() * 165;
      const p = this.obtainCleanParticle();
      p.x = originX + (Math.random() * 12 - 6);
      p.y = originY + (Math.random() * 16 - 8);
      p.vx = Math.cos(angle) * speed;
      p.vy = -Math.abs(Math.sin(angle) * speed * 0.8) - 45 - Math.random() * 75;
      p.gravity = 340;
      p.drag = 0.82;
      p.groundY = groundLevel + (Math.random() * 16 - 8);
      p.bounces = 1;
      p.settled = false;
      p.radius = 2.0 + Math.random() * 2.2;
      p.color = carminePalette[i % carminePalette.length];
      p.highlightColor = '#fecaca';
      p.isLiquidDroplet = true;
      p.life = 1.6 + Math.random() * 1.1;
      p.maxLife = p.life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }

    // 3. Ground Carmine Blood Puddles (organic multi-lobed pooling decals with wet specular gloss)
    this.addCarminePuddle(originX, groundLevel, r * 1.4);
    const puddlePuffs = this.quality === 'low' ? 2 : 4;
    for (let j = 0; j < puddlePuffs; j++) {
      const pAngle = Math.random() * Math.PI * 2;
      const pDist = 16 + Math.random() * 40;
      this.addCarminePuddle(
        originX + Math.cos(pAngle) * pDist,
        groundLevel + Math.sin(pAngle) * pDist * 0.45,
        10 + Math.random() * 12
      );
    }

    // 4. Secondary Lingering Dripping (viscous drops falling from air to puddle over 1.2s)
    if (this.quality !== 'low') {
      for (let d = 0; d < 8; d++) {
        const p = this.obtainCleanParticle();
        p.x = originX + (Math.random() * 16 - 8);
        p.y = originY - (Math.random() * 16);
        p.vx = (Math.random() - 0.5) * 12;
        p.vy = 20 + Math.random() * 35;
        p.gravity = 320;
        p.drag = 0.9;
        p.groundY = groundLevel + (Math.random() * 8 - 4);
        p.bounces = 0;
        p.settled = false;
        p.radius = 1.6 + Math.random() * 1.2;
        p.color = '#991b1b';
        p.highlightColor = '#fecaca';
        p.isLiquidDroplet = true;
        p.delay = 0.2 + d * 0.12;
        p.life = 1.4;
        p.maxLife = 1.4;
        p.alpha = 1.0;
        this.pushParticle(p);
      }
    }

    // 5. Crimson Pressure Wave & Localized Screen Shake
    this.addShockwave(originX, groundLevel, r * 3.0, '#991b1b', 0.42);
    this.addShockwave(originX, groundLevel, r * 2.2, '#dc2626', 0.28);
    this.addLight(originX, groundLevel, r * 3.6, '#b91c1c', 0.45);
    this.addLingeringSmokeColumn(originX, groundLevel, 4.0, 1.3);
    this.addScreenShake(5.0, 0.35);
  }

  // 🚀 Hyper Devil Patrol: Out-of-control spiraling jetpack death sequence ending in fireworks spark rain ("Silvesterrakete")
  addWildRocketDeath(unit, audio = null) {
    if (!unit) return;
    const unitType = unit.cardData?.unitType || unit.name || 'HyperDevilPatrol';
    const spriteData = getSpriteGrid(unitType, unit.race || 'Human');
    const startX = unit.x;
    const startY = unit.isAir ? unit.y - 18 : unit.y;
    const team = unit.team || 'western';
    const isWestern = team === 'western';
    const q = this.quality || 'high';
    const engine = this;
    const soundEngine = audio || this.audio;

    const flightDuration = 0.95;
    const initialDir = isWestern ? 1 : -1;
    const initialAngle = initialDir * 0.14;
    const drawSize = (unit.radius || 20) * 2.2;
    const spiralFreq = 18.0;

    const rocketDeath = {
      isWildRocketDeath: true,
      life: flightDuration,
      maxLife: flightDuration,
      x: startX,
      y: startY,
      vx: initialDir * 20,
      vy: -15,
      angle: initialAngle,
      initialAngle: initialAngle,
      spinSpeed: 0,
      spiralPhase: Math.random() * Math.PI * 2,
      smokeTimer: 0,
      spriteData: spriteData,
      bitmap: typeof document !== 'undefined' ? buildSpriteBitmap(unitType, unit.race || 'Human', team) : null,
      team: team,
      isWestern: isWestern,
      dir: initialDir,
      drawSize: drawSize,
      r: unit.radius || 20,
      update: function(dt) {
        const elapsed = this.maxLife - this.life;

        // Phase 1 (0.0s - 0.20s): Stumble, thruster sputter & failure
        if (elapsed < 0.20) {
          const stumbleProg = elapsed / 0.20;
          const shudder = Math.sin(elapsed * 45.0) * (0.12 * (1 - stumbleProg));
          this.angle = this.initialAngle + shudder;
          this.vy = -15 - stumbleProg * 35;
          this.x += this.vx * dt;
          this.y += this.vy * dt;
        } else {
          // Phase 2 (0.20s - 0.95s): Full runaway acceleration & death corkscrew
          const runawayProg = Math.min(1.0, (elapsed - 0.20) / 0.35);
          this.spiralPhase += dt * spiralFreq;
          this.spinSpeed = runawayProg * 18.0;
          this.angle += this.dir * this.spinSpeed * dt;
          this.vy = -50 - runawayProg * 110;

          const corkscrewAmp = runawayProg * 65;
          this.x += (this.vx * (1 - runawayProg * 0.4) + Math.cos(this.spiralPhase) * corkscrewAmp) * dt;
          this.y += (this.vy + Math.sin(this.spiralPhase * 1.4) * (corkscrewAmp * 0.5)) * dt;
        }

        this.smokeTimer += dt;
        const smokeInterval = q === 'ultra' ? 0.02 : (q === 'high' ? 0.035 : (q === 'medium' ? 0.07 : 0.14));
        if (this.smokeTimer >= smokeInterval && engine.canSpawnCosmetic()) {
          this.smokeTimer = 0;
          engine.pushParticle({
            x: this.x + (Math.random() * 6 - 3),
            y: this.y + (Math.random() * 6 - 3),
            vx: -Math.cos(this.angle) * 15 + (Math.random() * 12 - 6),
            vy: -Math.sin(this.angle) * 15 + (Math.random() * 12 - 6),
            radius: 2.4 + Math.random() * 1.8,
            color: Math.random() > 0.4 ? '#475569' : '#1e293b',
            life: 0.35 + Math.random() * 0.25,
            maxLife: 0.6,
            alpha: 0.75
          });

          if (q !== 'low') {
            engine.pushParticle({
              x: this.x,
              y: this.y,
              vx: (Math.random() - 0.5) * 50,
              vy: (Math.random() - 0.5) * 50,
              radius: 1.5,
              color: Math.random() > 0.5 ? '#f97316' : '#facc15',
              life: 0.15,
              maxLife: 0.15,
              alpha: 0.95
            });
          }
        }
      },
      onComplete: function() {
        const apexX = this.x;
        const apexY = this.y;

        // 1. Detonation sound
        soundEngine?.playExplosion?.('heavy');

        // 2. Shockwave, screen shake & flash
        engine.addShockwave(apexX, apexY, 48, '#f97316', 0.28);
        if (q !== 'low') {
          engine.addLight?.(apexX, apexY, 70, '#f97316', 0.25);
        }
        engine.addScreenShake?.(3.0, 0.2);

        // 3. Shatter voxel debris shards
        if (this.spriteData?.grid) {
          const sd = this.spriteData;
          const pSize = (this.r * 2.0) / sd.gridSize;
          const startVoxelX = apexX - this.r;
          const startVoxelY = apexY - this.r;
          const teamBadgeColor = this.team === 'western' ? '#00f0ff' : '#ff003c';

          for (let gy = 0; gy < sd.gridSize; gy++) {
            for (let gx = 0; gx < sd.gridSize; gx++) {
              if ((gy + gx) % 2 !== 0) continue;
              let col = sd.grid[gy][gx];
              if (col) {
                if (col === 'TEAM') col = teamBadgeColor;
                const angle = Math.random() * Math.PI * 2;
                const spd = 40 + Math.random() * 90;
                engine.pushParticle({
                  x: startVoxelX + gx * pSize,
                  y: startVoxelY + gy * pSize,
                  vx: Math.cos(angle) * spd,
                  vy: Math.sin(angle) * spd + 30,
                  gravity: 320,
                  drag: 0.7,
                  radius: Math.max(1.4, pSize * 0.5),
                  color: col,
                  isDebris: true,
                  life: 1.2 + Math.random() * 0.8,
                  maxLife: 2.0,
                  alpha: 1.0
                });
              }
            }
          }
        }

        // 4. Fireworks spark rain shower ("Silvesterrakete")
        const sparkCount = q === 'ultra' ? 60 : (q === 'high' ? 30 : (q === 'medium' ? 15 : 6));
        const fireworkColors = ['#facc15', '#f97316', '#ef4444', '#38bdf8', '#ffffff', '#c084fc'];
        for (let i = 0; i < sparkCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 70 + Math.random() * 150;
          const color = fireworkColors[Math.floor(Math.random() * fireworkColors.length)];
          const sparkLife = 1.0 + Math.random() * 0.8;
          engine.pushParticle({
            x: apexX,
            y: apexY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed - 20,
            gravity: 240,
            drag: 0.82,
            radius: 1.6 + Math.random() * 1.4,
            color: color,
            life: sparkLife,
            maxLife: sparkLife,
            alpha: 1.0
          });
        }
      }
    };

    this.specialEffects.push(rocketDeath);
  }

  // 🤡 Sul Pulp: Theatrical "Pencil Trick Vanish" death sequence ("Wanna see a magic trick? ... Ta-da! It's gone.")
  // Patient, comedic 4-stage illusion:
  // Phase 1 (0.00s - 0.65s): Spotlight pool fades in, unit freezes in place, sharp yellow #2 pencil plants vertically in asphalt. Comic text "WANNA SEE A MAGIC TRICK?".
  // Phase 2 (0.65s - 1.30s): Patient suspenseful build-up; Pulp leans forward slowly, staring right down at the graphite tip; purple bio-magic vortex orbits the pencil.
  // Phase 3 (1.30s - 1.55s): THE SLAM! Violent head slam into the desk/pencil; heavy shockwave ring, yellow wood splinters, graphite fragments, purple Sul sparks; pencil driven into ground.
  // Phase 4 (1.55s - 2.65s): "TA-DA! IT'S GONE!" Poof! Pulp completely vanishes into thin air; cloud of purple smoke; 7 mini Harlequin playing cards flutter and tumble in low gravity; 3 golden jester bells bounce across ground; text flips to radiant gold "TA-DA! IT'S GONE." then fades cleanly out.
  addPulpPencilTrickDeath(unit, audio = null) {
    if (!unit) return;
    const renderRace = 'Sul';
    const team = unit.team || 'western';
    const isWestern = team === 'western';
    const forwardDir = isWestern ? 1 : -1;
    const startX = unit.x;
    const startY = unit.y;
    const drawSize = (unit.radius || 20) * 2.2;
    const q = this.quality || 'high';
    const engine = this;

    const headBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('Pulp_Head', renderRace, team) : null;
    const bodyBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('Pulp_Body', renderRace, team) : null;
    const weaponBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('Pulp_Weapon', renderRace, team) : null;
    const legsBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('Pulp_Legs_Walk1', renderRace, team) : null;
    const legsSquashBitmap = typeof document !== 'undefined' ? buildSpriteBitmap('Pulp_Legs_Walk6', renderRace, team) : null;

    const totalDuration = 2.65;

    const pencilTrick = {
      isPulpPencilTrick: true,
      life: totalDuration,
      maxLife: totalDuration,
      x: startX,
      y: startY,
      team: team,
      isWestern: isWestern,
      dir: forwardDir,
      drawSize: drawSize,
      headBitmap: headBitmap,
      bodyBitmap: bodyBitmap,
      weaponBitmap: weaponBitmap,
      legsBitmap: legsBitmap,
      legsSquashBitmap: legsSquashBitmap,
      morphProg: 0.0,
      pencilScale: 0.0,
      pencilOffsetY: 0.0,
      pencilAngle: 0.0,
      hasSlammed: false,
      hasVanished: false,
      spotlightAlpha: 0.0,
      headPitch: 0.0,
      bodySquashY: 1.0,
      bodySquashX: 1.0,
      slamOffsetY: 0.0,
      textAlpha: 0.0,
      textScale: 1.0,
      textY: startY - 26,
      cards: [],
      bells: [],
      update: function(dt) {
        const elapsed = this.maxLife - this.life;

        // Phase 1 (0.00s - 0.65s): Setup & Spotlight Freeze (No text, clown freezes under spotlight)
        if (elapsed < 0.65) {
          const p1 = elapsed / 0.65;
          this.spotlightAlpha = Math.min(1.0, p1 * 1.6);
          this.morphProg = 0.0;
          this.pencilScale = 0.0;
          this.pencilOffsetY = 0.0;
          this.textAlpha = 0.0; // Strictly NO text in Phase 1
          this.headPitch = 0.0;
          this.bodySquashY = 1.0;
          this.bodySquashX = 1.0;
          this.slamOffsetY = 0.0;
        }
        // Phase 2 (0.65s - 1.30s): Clown Morphs into Pencil!
        else if (elapsed < 1.30) {
          const p2 = (elapsed - 0.65) / 0.65;
          this.spotlightAlpha = 1.0;
          this.morphProg = p2;
          this.pencilScale = p2;
          this.textAlpha = 0.0; // Strictly NO text in Phase 2

          // Clown squashes & spins into pencil shape
          this.bodySquashX = Math.max(0.0, (1.0 - p2) * Math.cos(p2 * Math.PI * 4));
          this.bodySquashY = 1.0 + p2 * 0.25;

          // Suspense wobble right before the slam
          if (p2 > 0.85) {
            this.pencilAngle = Math.sin((elapsed - 1.20) * 50) * 0.07;
          } else {
            this.pencilAngle = 0.0;
          }

          // Arcane bio-magic sparks circling the morphing clown
          if (q !== 'low' && Math.random() < 0.40) {
            const orbitAngle = elapsed * 14.0 + Math.random() * 0.8;
            const orbitRad = 6 + Math.random() * 8;
            engine.pushParticle?.({
              x: this.x + Math.cos(orbitAngle) * orbitRad,
              y: this.y + Math.sin(orbitAngle) * 6,
              vx: -Math.sin(orbitAngle) * 20,
              vy: -10 - Math.random() * 12,
              radius: 0.8 + Math.random() * 0.8,
              color: ['#a855f7', '#fbbf24', '#10b981', '#f43f5e', '#06b6d4'][Math.floor(Math.random() * 5)],
              life: 0.45,
              maxLife: 0.45,
              alpha: 0.85
            });
          }
        }
        // Phase 3 (1.30s - 1.55s): Rammed into the Ground!
        else if (elapsed < 1.55) {
          const p3 = (elapsed - 1.30) / 0.25;
          this.morphProg = 1.0;
          this.textAlpha = 0.0; // Strictly NO text in Phase 3

          if (!this.hasSlammed) {
            this.hasSlammed = true;
            engine.addScreenShake?.(5.0, 0.28);
            engine.addShockwave?.(this.x, this.y + 12, 34, '#fbbf24', 0.28);
            engine.addShockwave?.(this.x, this.y + 12, 22, '#a855f7', 0.22);

            // Wood splinters, graphite shards, cedar chips, pink eraser bits
            const splinterCount = q === 'ultra' ? 22 : (q === 'high' ? 14 : 7);
            for (let i = 0; i < splinterCount; i++) {
              const ang = Math.random() * Math.PI * 2;
              const spd = 50 + Math.random() * 110;
              const cols = ['#eab308', '#ca8a04', '#fed7aa', '#0f172a', '#f43f5e', '#a855f7'];
              engine.pushParticle?.({
                x: this.x,
                y: this.y + 10,
                vx: Math.cos(ang) * spd,
                vy: Math.sin(ang) * spd - 35,
                gravity: 360,
                drag: 0.82,
                radius: 1.5 + Math.random() * 1.8,
                color: cols[Math.floor(Math.random() * cols.length)],
                life: 0.8 + Math.random() * 0.6,
                maxLife: 1.4,
                alpha: 1.0,
                isDebris: true
              });
            }
          }

          // Rapidly driven straight into the ground (deep plunge for 80% larger pencil!)
          const ram = Math.min(1.0, p3 / 0.35);
          this.pencilOffsetY = ram * 62;
          this.pencilScale = Math.max(0.0, 1.0 - ram);
          this.bodySquashY = 0.0;
          this.bodySquashX = 0.0;
        }
        // Phase 4 (1.55s - 2.65s): "TADAA! IT'S GONE." – Vanish & Grand Card Rain
        else {
          const p4 = (elapsed - 1.55) / 1.10;
          this.pencilScale = 0.0;
          if (!this.hasVanished) {
            this.hasVanished = true;
            this.textScale = 1.4;
            this.textAlpha = 1.0; // Text appears ONLY in Phase 4!

            // Big magical poof smoke cloud
            const puffCount = q === 'ultra' ? 20 : (q === 'high' ? 14 : 7);
            for (let i = 0; i < puffCount; i++) {
              const ang = (i / puffCount) * Math.PI * 2 + Math.random() * 0.4;
              const spd = 24 + Math.random() * 55;
              engine.pushParticle?.({
                x: this.x + (Math.random() * 12 - 6),
                y: this.y + (Math.random() * 12 - 6),
                vx: Math.cos(ang) * spd,
                vy: Math.sin(ang) * spd - 16,
                gravity: -10, // Float upward
                drag: 0.88,
                radius: 5.5 + Math.random() * 6.5,
                color: ['#7c3aed', '#a855f7', '#db2777', '#f8fafc', '#fbbf24'][Math.floor(Math.random() * 5)],
                life: 0.7 + Math.random() * 0.5,
                maxLife: 1.2,
                alpha: 0.85
              });
            }

            // Confetti playing cards (Lush 12-card rain fanning high and wide!)
            this.cards = [];
            const cardCount = 12;
            for (let i = 0; i < cardCount; i++) {
              const spread = (i / (cardCount - 1)) - 0.5; // -0.5 to +0.5
              const fanAngle = -Math.PI * 0.5 + spread * 2.2 + (Math.random() - 0.5) * 0.35;
              const fanSpd = 95 + Math.random() * 65;
              this.cards.push({
                x: this.x + (Math.random() * 10 - 5),
                y: this.y - 6,
                vx: Math.cos(fanAngle) * fanSpd * 1.55, // Broad horizontal fan-out
                vy: -130 - Math.random() * 55, // Hurled high into the sky!
                angle: Math.random() * Math.PI * 2,
                spinSpeed: (Math.random() - 0.5) * 8.0,
                scaleX: 1.0,
                scaleSpeed: 5.0 + Math.random() * 5.0,
                swayPhase: Math.random() * Math.PI * 2,
                swayAmp: 22 + Math.random() * 16,
                isRed: Math.random() < 0.5,
                suitIdx: Math.floor(Math.random() * 4),
                groundY: this.y + 12 + Math.random() * 10,
                landed: false,
                alpha: 1.0
              });
            }

            // Bouncing golden jester bells
            this.bells = [];
            for (let i = 0; i < 3; i++) {
              const bAng = -Math.PI * 0.6 + (Math.random() - 0.5) * 1.5;
              const bSpd = 35 + Math.random() * 45;
              this.bells.push({
                x: this.x + (Math.random() * 6 - 3),
                y: this.y - 8,
                vx: Math.cos(bAng) * bSpd,
                vy: Math.sin(bAng) * bSpd - 15,
                angle: 0,
                groundY: this.y + 14,
                bounces: 0,
                alpha: 1.0
              });
            }
          }

          // Update confetti cards (Slow fluttering aerodynamic descent)
          for (let card of this.cards) {
            if (!card.landed) {
              card.x += card.vx * dt;
              card.y += card.vy * dt;
              card.vx *= 0.94; // air drag on horizontal velocity

              if (card.vy < 0) {
                // Rocketing upward against initial gravity
                card.vy += 260 * dt;
              } else {
                // Slow fluttering descent through air
                card.vy = Math.min(card.vy + 75 * dt, 40); // capped at slow terminal drift
                card.x += Math.sin(card.swayPhase + elapsed * 5.5) * card.swayAmp * dt;
              }

              card.angle += card.spinSpeed * dt;
              card.scaleX = Math.cos(elapsed * card.scaleSpeed);
              if (card.y >= card.groundY) {
                card.y = card.groundY;
                card.landed = true;
                card.vx = 0;
                card.vy = 0;
                card.scaleX = card.scaleX >= 0 ? 1.0 : -1.0;
              }
            }
            if (p4 > 0.70) {
              card.alpha = Math.max(0, 1.0 - (p4 - 0.70) / 0.30);
            }
          }

          // Update bouncing bells
          for (let bell of this.bells) {
            if (bell.bounces < 3) {
              bell.x += bell.vx * dt;
              bell.y += bell.vy * dt;
              bell.vy += 320 * dt;
              bell.vx *= 0.92;
              bell.angle += bell.vx * dt * 0.2;
              if (bell.y >= bell.groundY) {
                bell.y = bell.groundY;
                bell.vy = -bell.vy * 0.55;
                bell.bounces++;
                if (Math.abs(bell.vy) < 15) {
                  bell.bounces = 3;
                  bell.vy = 0;
                  bell.vx = 0;
                }
              }
            }
            if (p4 > 0.65) {
              bell.alpha = Math.max(0, 1.0 - (p4 - 0.65) / 0.35);
            }
          }

          // Text & spotlight fade
          this.textScale = Math.max(1.0, 1.4 - (p4 * 0.4));
          this.textY = this.y - 28 - p4 * 12; // Floating up
          this.spotlightAlpha = Math.max(0, 1.0 - p4 * 1.5);
          if (p4 > 0.70) {
            this.textAlpha = Math.max(0, 1.0 - (p4 - 0.70) / 0.30);
          }
        }
      }
    };

    this.specialEffects.push(pencilTrick);
  }

  // 💋 Pink Beret: Theatrical "Turn to Camera -> Blowing Kiss -> Wink & Heart Burst" death sequence
  // 1. Soldat turns from side profile into frontal view while stepping into the spotlight
  // 2. Full frontal model pose: Hand on hip (akimbo), other hand kissing lips (Kussmund)
  // 3. Kiss throw: Hand flings kiss forward, radiant pink heart rises and flies towards camera
  // 4. Charming wink: Winking sunglasses with diamond glint star
  // 5. And only then (at 1.30s) shatters into pink hearts!
  addPinkBeretKissDeath(unit, audio = null) {
    if (!unit) return;
    const team = unit.team || 'western';
    const isWestern = team === 'western';
    const forwardDir = isWestern ? 1 : -1;
    const startX = unit.x;
    const startY = unit.y;
    const isClassic = !!(unit.forceHD || unit.variant === 'classic' || unit.skin === 'classic');
    // Exactly match live Pink Beret draw size (radius * 2.2 * 1.20 for UHD Relaunch, 1.12 for Classic)
    const drawSize = Math.round((unit.radius || 20) * 2.2 * (isClassic ? 1.12 : 1.20));
    const isGold = unit.cardData?.spriteRecolor === 'gold' || unit.edition === 'gold';
    const prefix = isClassic
      ? (isGold ? 'PinkBeretGold_Classic_' : 'PinkBeret_Classic_')
      : (isGold ? 'PinkBeretGold_UHD_' : 'PinkBeret_UHD_');
    const engine = this;

    const sideStandBitmap = typeof document !== 'undefined' ? buildSpriteBitmap(prefix + 'Legs_Stand', 'Human', team) : null;
    const sideTorsoBitmap = typeof document !== 'undefined' ? buildSpriteBitmap(prefix + 'Torso', 'Human', team) : null;
    const turnBitmap = typeof document !== 'undefined' ? buildSpriteBitmap(prefix + 'Front_Turn', 'Human', team) : null;
    const frontKissBitmap = typeof document !== 'undefined' ? buildSpriteBitmap(prefix + 'Front_Kiss', 'Human', team) : null;
    const frontWinkBitmap = typeof document !== 'undefined' ? buildSpriteBitmap(prefix + 'Front_Wink', 'Human', team) : null;
    const kissBitmap = typeof document !== 'undefined' ? buildSpriteBitmap(prefix + 'Kiss', 'Human', team) : null;

    const totalDuration = 2.0;
    const kissDeath = {
      isPinkBeretKissDeath: true,
      life: totalDuration,
      maxLife: totalDuration,
      x: startX,
      y: startY,
      team,
      isWestern,
      dir: forwardDir,
      drawSize,
      sideStandBitmap,
      sideTorsoBitmap,
      turnBitmap,
      frontKissBitmap,
      frontWinkBitmap,
      legsBitmap: sideStandBitmap,
      torsoBitmap: sideTorsoBitmap,
      kissBitmap,
      hasPopped: false,
      playedSmooch: false,
      stage: 'turn_side',
      turnProgress: 0,
      stepScale: 1.0,
      stepY: 0,
      bodyTilt: 0,
      bodyBreathe: 0,
      bodyYBob: 0,
      kissActive: false,
      kissScale: 0.8,
      kissX: startX + forwardDir * 8,
      kissY: startY - 8,
      kissAlpha: 0,
      kissBank: 0,
      kissThrob: 1.0,
      kissYaw: 1.0,
      sparkleProgress: 0,
      heartTrail: [],
      stardustTimer: 0,
      hearts: [],
      // 🌸 LIEBES-PARFÜM: Luminous Pink Perfume Mist & Sparkling Droplets
      perfumePuffs: [
        { x: startX, y: startY - 8, vx: 0, vy: -12, r: 6, maxR: 26, growSpeed: 45, alpha: 0.55, baseAlpha: 0.55, color: '#ff1493', innerColor: '#fda4af' },
        { x: startX + forwardDir * 7, y: startY - 14, vx: forwardDir * 14, vy: -18, r: 5, maxR: 22, growSpeed: 38, alpha: 0.48, baseAlpha: 0.48, color: '#ff69b4', innerColor: '#ffffff' },
        { x: startX - forwardDir * 6, y: startY - 6, vx: -forwardDir * 10, vy: -8, r: 5, maxR: 24, growSpeed: 40, alpha: 0.45, baseAlpha: 0.45, color: '#fb7185', innerColor: '#fda4af' },
        { x: startX + forwardDir * 4, y: startY + 2, vx: forwardDir * 6, vy: -4, r: 4, maxR: 18, growSpeed: 32, alpha: 0.38, baseAlpha: 0.38, color: '#ff1493', innerColor: '#ffffff' },
        { x: startX, y: startY - 18, vx: 0, vy: -20, r: 4, maxR: 19, growSpeed: 34, alpha: 0.50, baseAlpha: 0.50, color: '#ff69b4', innerColor: '#fda4af' }
      ],
      perfumeDroplets: Array.from({ length: 18 }, (_, i) => {
        const ang = (i / 18) * Math.PI * 2 + (Math.random() * 0.4 - 0.2);
        const spd = 22 + Math.random() * 40;
        const dColors = ['#ff1493', '#ff69b4', '#fb7185', '#fde047', '#ffffff', '#fda4af'];
        return {
          x: startX + (Math.random() * 8 - 4),
          y: startY - 8 + (Math.random() * 8 - 4),
          vx: Math.cos(ang) * spd,
          vy: Math.sin(ang) * spd - 22,
          r: 1.0 + Math.random() * 1.5,
          color: dColors[i % dColors.length],
          maxLife: 0.70,
          alpha: 1.0,
          twinkle: Math.random() * Math.PI * 2
        };
      }),
      update: function(dt) {
        const elapsed = this.maxLife - this.life;

        // 🌸 Update Love Perfume Mist & Sparkling Aerosol Droplets (0.00s - 0.60s)
        if (elapsed < 0.60) {
          for (let i = 0; i < this.perfumePuffs.length; i++) {
            const p = this.perfumePuffs[i];
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.vx *= 0.92;
            p.vy *= 0.92;
            p.r = Math.min(p.maxR, p.r + p.growSpeed * dt);
            // Rapid blooming fade-in in first 0.08s, sustained veil across perspective turn (0.08s-0.30s), smooth dissolve
            if (elapsed < 0.08) {
              p.alpha = (elapsed / 0.08) * p.baseAlpha;
            } else {
              p.alpha = Math.max(0, p.baseAlpha * (1.0 - (elapsed - 0.08) / 0.52));
            }
          }

          for (let i = 0; i < this.perfumeDroplets.length; i++) {
            const d = this.perfumeDroplets[i];
            d.x += d.vx * dt;
            d.y += d.vy * dt;
            d.vy -= 8 * dt; // gentle buoyant rising
            d.vx *= 0.94;
            d.twinkle += dt * 14;
            d.alpha = Math.max(0, 1.0 - elapsed / d.maxLife);
          }
        }

        // Stage 1 (0.00s - 0.30s): Drehung aus der Seitenperspektive in die Frontalperspektive
        if (elapsed < 0.30) {
          const p = elapsed / 0.30;
          this.stepScale = 1.0 + p * 0.08; // expands slightly towards camera (never shrinks!)
          this.stepY = p * 4;              // steps forward into limelight
          this.bodyTilt = Math.sin(p * Math.PI) * 0.02 * this.dir;
          this.bodyBreathe = Math.sin(p * Math.PI) * 0.015;
          this.bodyYBob = -Math.sin(p * Math.PI) * 0.8;
          if (p < 0.5) {
            this.stage = 'turn_side';
            this.turnProgress = p / 0.5;
          } else {
            this.stage = 'turn_34';
            this.turnProgress = (p - 0.5) / 0.5;
          }
        }
        // Stage 2 (0.30s - 0.65s): Vollständige Frontalperspektive: Hand auf Hüfte, Kuss an die Lippen (Kussmund)
        else if (elapsed < 0.65) {
          this.stage = 'front_kiss';
          this.stepScale = 1.08;
          this.stepY = 4;
          const tk = (elapsed - 0.30) / 0.35;
          // Organic suavity: leaning subtly into the kiss, suave chest inhale & micro-bob
          this.bodyTilt = Math.sin(tk * Math.PI) * -0.03 * this.dir;
          this.bodyBreathe = Math.sin(tk * Math.PI) * 0.035;
          this.bodyYBob = Math.sin(tk * Math.PI) * -1.2;

          if (!this.playedSmooch) {
            this.playedSmooch = true;
            if (audio?.playPinkBeretKiss) {
              audio.playPinkBeretKiss({ x: this.x });
            } else if (audio?.playKiss) {
              audio.playKiss({ x: this.x });
            } else if (audio?.playRegistrySound) {
              audio.playRegistrySound('units', 'PinkBeret', 'kiss', { x: this.x });
            }
          }
        }
        // Stage 3 (0.65s - 0.95s): Kusswurf & Aufsteigen des rosa Herzens (Hand wirft Kuss)
        else if (elapsed < 0.95) {
          this.stage = 'front_wink';
          this.stepScale = 1.08;
          this.stepY = 4;
          const tr = (elapsed - 0.65) / 0.30;
          // Dynamic throw impulse recoil: chest rebounds back from the throw, settles into pose
          this.bodyTilt = (1 - tr) * 0.04 * this.dir + Math.sin(tr * Math.PI * 2) * 0.015 * this.dir;
          this.bodyBreathe = (1 - tr) * 0.025;
          this.bodyYBob = -Math.sin(tr * Math.PI) * 1.5;

          // Rosa Herz steigt ab 0.65s auf und fliegt auf die Kamera zu (3D Flugkurve)
          this.kissActive = true;
          const pk = (elapsed - 0.65) / 0.55;
          this.kissScale = 0.8 + pk * 2.4; // wächst auf 3.2x an
          this.kissBank = Math.sin(pk * Math.PI * 3.5) * 0.22 * this.dir;
          this.kissThrob = 1.0 + Math.sin(pk * Math.PI * 6) * 0.12 * (1 - pk * 0.4);
          this.kissX = this.x + (this.dir * 12) * (1 - pk * 0.30) + Math.sin(pk * Math.PI * 2) * 3 * this.dir;
          this.kissY = this.y - 10 - Math.sin(pk * Math.PI) * 14 + pk * 22;
          this.kissAlpha = Math.min(1.0, (1.25 - elapsed) / 0.20);
        }
        // Stage 4 (0.95s - 1.30s): Das Zwinkern (Zwinker-Auge & Diamant-Glanz auf weißer Sonnenbrille)
        else if (elapsed < 1.30) {
          this.stage = 'front_wink';
          this.stepScale = 1.08;
          this.stepY = 4;
          const tw = elapsed - 0.95;
          // Living model breathing & attitude sway during the hold
          this.bodyBreathe = Math.sin(tw * 7.0) * 0.02;
          this.bodyTilt = Math.sin(tw * 4.0) * 0.015 * this.dir;
          this.bodyYBob = Math.sin(tw * 7.0) * 0.6;

          // Sparkle star twinkling on white sunglasses (0.95s - 1.25s)
          if (elapsed >= 0.95 && elapsed <= 1.25) {
            this.sparkleProgress = (elapsed - 0.95) / 0.30;
          } else {
            this.sparkleProgress = 0;
          }

          if (this.kissActive) {
            const pk = (elapsed - 0.65) / 0.55;
            this.kissScale = 0.8 + pk * 2.4;
            this.kissBank = Math.sin(pk * Math.PI * 3.5) * 0.22 * this.dir;
            this.kissThrob = 1.0 + Math.sin(pk * Math.PI * 6) * 0.12 * (1 - pk * 0.4);
            this.kissX = this.x + (this.dir * 12) * (1 - pk * 0.30) + Math.sin(pk * Math.PI * 2) * 3 * this.dir;
            this.kissY = this.y - 10 - Math.sin(pk * Math.PI) * 14 + pk * 22;
            this.kissAlpha = Math.max(0, 1.0 - (elapsed - 1.05) / 0.20);
          }
        }
        // Stage 5 (ab 1.30s): Und erst dann zerspringt er in die pinken Herzen!
        else {
          if (!this.hasPopped) {
            this.hasPopped = true;
            if (audio?.playPinkBeretHeartPop) {
              audio.playPinkBeretHeartPop({ x: this.x });
            } else if (audio?.playBalloonPop) {
              audio.playBalloonPop({ x: this.x });
            }

            // Burst of 18 floating, rotating pink hearts with 2.5D depth variation
            const heartColors = ['#ff1493', '#ff007f', '#ff2a85', '#fb7185', '#f43f5e', '#ffffff'];
            for (let i = 0; i < 18; i++) {
              const ang = (i / 18) * Math.PI * 2 + (Math.random() * 0.4 - 0.2);
              const spd = 45 + Math.random() * 85;
              const zDepth = (i % 3 === 0) ? 0.7 : (i % 3 === 1 ? 1.0 : 1.35);
              this.hearts.push({
                x: this.x + (Math.random() * 10 - 5),
                y: this.y - 8 + (Math.random() * 10 - 5),
                vx: Math.cos(ang) * spd * zDepth,
                vy: (Math.sin(ang) * spd - 38) * zDepth,
                scale: (0.70 + Math.random() * 0.75) * zDepth,
                alpha: 1.0,
                color: heartColors[i % heartColors.length],
                rot: (Math.random() - 0.5) * 0.6,
                rotSpeed: (Math.random() - 0.5) * 4.5,
                zDepth: zDepth
              });
            }

            // Sparkling golden glitter dust
            for (let i = 0; i < 16; i++) {
              engine.pushParticle?.({
                x: this.x + (Math.random() * 16 - 8),
                y: this.y - 8 + (Math.random() * 16 - 8),
                vx: (Math.random() - 0.5) * 110,
                vy: -35 - Math.random() * 70,
                radius: 1.5 + Math.random() * 1.8,
                color: Math.random() > 0.35 ? '#fde047' : '#ffffff',
                life: 0.45 + Math.random() * 0.35,
                maxLife: 0.80,
                alpha: 0.95
              });
            }
          }

          // Step hearts
          const p4 = (elapsed - 1.30) / 0.70;
          for (let i = 0; i < this.hearts.length; i++) {
            const h = this.hearts[i];
            h.x += h.vx * dt;
            h.y += h.vy * dt;
            h.vy += 45 * dt; // Gentle float fall
            h.vx *= 0.96;
            h.rot += h.rotSpeed * dt;
            h.alpha = Math.max(0, 1.0 - p4 * 1.2);
          }
        }

        // Stardust micro-heart wake trail behind flying kiss heart (0.65s - 1.25s)
        if (this.kissActive && this.kissAlpha > 0.08) {
          this.stardustTimer = (this.stardustTimer || 0) + dt;
          if (this.stardustTimer >= 0.035 && this.heartTrail.length < 24) {
            this.stardustTimer = 0;
            const trailColors = ['#ff1493', '#ff69b4', '#fda4af', '#fde047', '#ffffff'];
            this.heartTrail.push({
              x: this.kissX + (Math.random() * 6 - 3),
              y: this.kissY + (Math.random() * 6 - 3),
              vx: (Math.random() - 0.5) * 12 - this.dir * 5,
              vy: (Math.random() - 0.5) * 10 - 4,
              scale: (0.35 + Math.random() * 0.35) * Math.min(2.0, this.kissScale * 0.6),
              rot: (Math.random() - 0.5) * 0.8,
              rotSpeed: (Math.random() - 0.5) * 5,
              alpha: 0.85,
              decay: 2.2 + Math.random() * 1.5,
              isSpark: Math.random() > 0.4,
              color: trailColors[Math.floor(Math.random() * trailColors.length)]
            });
          }
        }

        // Update heart trail particles
        if (this.heartTrail && this.heartTrail.length > 0) {
          for (let i = this.heartTrail.length - 1; i >= 0; i--) {
            const tr = this.heartTrail[i];
            tr.x += tr.vx * dt;
            tr.y += tr.vy * dt;
            tr.vy -= 3 * dt;
            tr.rot += tr.rotSpeed * dt;
            tr.alpha -= tr.decay * dt;
            if (tr.alpha <= 0) {
              this.heartTrail.splice(i, 1);
            }
          }
        }
      }
    };

    this.specialEffects.push(kissDeath);
  }

  // 💥 Pink Beret: Radiant Pink Love Impact Explosion
  addPinkBeretRocketExplosion(x, y, isGold = false, audio = null) {
    const playAudio = audio || ((typeof window !== 'undefined') ? window.tcgGame?.audio : null);
    if (playAudio?.playPinkBeretRocketImpact) {
      playAudio.playPinkBeretRocketImpact({ x, y });
    } else if (playAudio?.playRegistrySound) {
      playAudio.playRegistrySound('units', 'PinkBeret', 'rocket_impact', { x, y });
    } else {
      playAudio?.playExplosion?.('heavy', { x, y });
    }

    const primaryColor = isGold ? '#ffd700' : '#ff2a85';
    const accentColor = isGold ? '#fef08a' : '#fda4af';
    const coreColor = '#ffffff';

    // 1. Central explosive flash shockwave
    this.addExplosion?.(x, y, 28, primaryColor, 16, 3.5, false);

    // 2. High-velocity radiating spark rays (20 sparks spraying 360°)
    for (let i = 0; i < 20; i++) {
      const angle = (i / 20) * Math.PI * 2 + (Math.random() * 0.4 - 0.2);
      const spd = 60 + Math.random() * 120;
      this.pushParticle({
        x: x + Math.cos(angle) * 4,
        y: y + Math.sin(angle) * 4,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd - 15,
        radius: 1.8 + Math.random() * 1.6,
        color: i % 3 === 0 ? coreColor : (i % 2 === 0 ? primaryColor : accentColor),
        life: 0.28 + Math.random() * 0.22,
        maxLife: 0.50,
        alpha: 0.95
      });
    }

    // 3. Billowing soft pink love perfume mist clouds
    for (let i = 0; i < 5; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * 14;
      this.pushParticle({
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist - 6,
        vx: (Math.random() - 0.5) * 22,
        vy: -15 - Math.random() * 20,
        radius: 6.0 + Math.random() * 4.5,
        color: isGold ? '#fde047' : '#ff69b4',
        life: 0.45 + Math.random() * 0.30,
        maxLife: 0.75,
        alpha: 0.65
      });
    }

    // 4. Ground Scorch Mark
    this.addScorchMark?.(x, y, 16);
  }

  // 🚀 Pink Beret: 45° Target-Seeking Homing Rocket with Pink & White Trail
  addPinkBeretHomingRocket(startX, startY, target, speed, onHit, trailColor = null, customDuration = null, audio = null) {
    const tx = target.x;
    const ty = target.y;
    const dist = Math.hypot(tx - startX, ty - startY);
    const duration = customDuration !== null ? customDuration : Math.max(0.40, Math.min(1.5, 0.20 + dist / (speed || 480)));
    const engine = this;
    let hitReported = false;

    // Launch backblast / muzzle flare
    for (let i = 0; i < 6; i++) {
      this.pushParticle({
        x: startX + (Math.random() * 6 - 3),
        y: startY + (Math.random() * 4 - 2),
        vx: (Math.random() - 0.5) * 35,
        vy: -15 - Math.random() * 25,
        radius: 2.5 + Math.random() * 2.0,
        color: Math.random() > 0.5 ? '#ff2a85' : '#ffffff',
        life: 0.22 + Math.random() * 0.15,
        maxLife: 0.38,
        alpha: 0.8
      });
    }

    const rocket = {
      isPinkBeretHomingRocket: true,
      startX,
      startY,
      target,
      speed: speed || 460,
      life: duration,
      maxLife: duration,
      trailTimer: 0,
      x: startX,
      y: startY,
      angle: -Math.PI / 4,
      trailColor: trailColor || '#ff2a85',
      update: function(dt) {
        const progress = 1.0 - Math.max(0, this.life / this.maxLife);
        const curTx = (this.target && typeof this.target.x === 'number') ? this.target.x : tx;
        const curTy = (this.target && typeof this.target.y === 'number') ? this.target.y : ty;

        // Phase 1: 45-degree angled climb
        // Phase 2: Homing curve diving into target
        const phase1 = 0.30;
        let nextX, nextY;

        if (progress < phase1) {
          const p1 = progress / phase1;
          const climbEase = 1.0 - Math.pow(1.0 - p1, 1.8);
          const climbDistX = (curTx >= this.startX ? 1 : -1) * 75 * climbEase;
          const climbDistY = -85 * climbEase;
          nextX = this.startX + climbDistX;
          nextY = this.startY + climbDistY;
          this.angle = Math.atan2(climbDistY, climbDistX);
        } else {
          const p2 = (progress - phase1) / (1.0 - phase1);
          const apexX = this.startX + (curTx >= this.startX ? 1 : -1) * 75;
          const apexY = this.startY - 85;

          const t = p2;
          const u = 1 - t;
          const ctrlX = apexX + (curTx - apexX) * 0.4;
          const ctrlY = apexY - 25;

          nextX = u * u * apexX + 2 * u * t * ctrlX + t * t * curTx;
          nextY = u * u * apexY + 2 * u * t * ctrlY + t * t * curTy;

          const dx = nextX - this.x;
          const dy = nextY - this.y;
          if (Math.hypot(dx, dy) > 0.001) {
            this.angle = Math.atan2(dy, dx);
          }
        }

        this.x = nextX;
        this.y = nextY;
        if (!this.trailHistory) this.trailHistory = [];
        this.trailHistory.unshift({ x: this.x, y: this.y });
        if (this.trailHistory.length > 7) this.trailHistory.pop();

        // Pink & White trailing smoke & sparkles
        this.trailTimer += dt;
        if (this.trailTimer >= 0.02) {
          this.trailTimer = 0;
          const isPink = Math.random() > 0.45;
          engine.pushParticle({
            x: this.x - Math.cos(this.angle) * 8 + (Math.random() * 4 - 2),
            y: this.y - Math.sin(this.angle) * 8 + (Math.random() * 4 - 2),
            vx: -Math.cos(this.angle) * 20 + (Math.random() * 10 - 5),
            vy: -Math.sin(this.angle) * 20 + (Math.random() * 10 - 5),
            radius: 2.2 + Math.random() * 1.8,
            color: isPink ? this.trailColor : '#ffffff',
            life: 0.25 + Math.random() * 0.15,
            maxLife: 0.4,
            alpha: 0.85
          });
        }

        if (this.life <= dt && !hitReported) {
          hitReported = true;
          engine.addPinkBeretRocketExplosion(this.x, this.y, this.trailColor === '#ffd700', audio);
          if (onHit) onHit();
        }
      }
    };

    this.specialEffects.push(rocket);
  }

  // 🗑️ Discarded Rocket Launcher Prop Tumbling to Ground
  addDiscardedLauncher(x, y, team) {
    const isWestern = team === 'western';
    const tossDir = isWestern ? -1 : 1;
    const launcher = {
      isDiscardedLauncher: true,
      x: x + tossDir * 6,
      y: y - 8,
      vx: tossDir * (45 + Math.random() * 25),
      vy: -75 - Math.random() * 30,
      angle: 0,
      rotSpeed: tossDir * (7 + Math.random() * 4),
      life: 1.1,
      maxLife: 1.1,
      groundY: y + 14,
      update: function(dt) {
        this.x += this.vx * dt;
        this.y += this.vy * dt;
        this.vy += 420 * dt; // Gravity
        this.angle += this.rotSpeed * dt;

        if (this.y >= this.groundY) {
          this.y = this.groundY;
          this.vy = -this.vy * 0.32;
          this.vx *= 0.55;
          this.rotSpeed *= 0.45;
        }
      }
    };
    this.specialEffects.push(launcher);
  }

  // Disassembles an executed infantry unit into two severed halves (Left & Right)
  // that violently fly apart with blood/spark sprays and settle on the ground!
  addUnitSplitShatter(unit) {
    if (!unit) return;
    const unitType = unit.cardData?.unitType || unit.unitType || unit.name;
    if (!unitType) return;
    const spriteData = getSpriteGrid(unitType, unit.race);
    if (!spriteData || !spriteData.grid) return;

    const r = unit.radius || 20;
    const pSize = (r * 2.2) / spriteData.gridSize;
    const teamBadgeColor = unit.team === 'western' ? '#00f0ff' : '#ff003c';
    const isWestern = unit.team === 'western';
    const originX = unit.x;
    const originY = unit.y;
    const groundLevel = unit.y + 24; // Dropping to ground level from held height
    const startX = originX - r * 1.1;
    const startY = originY - r * 1.1;

    const isLargeGrid = spriteData.gridSize >= 16;
    const halfGrid = spriteData.gridSize / 2;

    for (let gy = 0; gy < spriteData.gridSize; gy++) {
      for (let gx = 0; gx < spriteData.gridSize; gx++) {
        // High-density grids (20x20): checkerboard subsampling cuts particle count by 50%
        if (isLargeGrid && ((gy + gx) % 2 !== 0)) continue;

        const col = isWestern ? gx : (spriteData.gridSize - 1 - gx);
        let pixelColor = spriteData.grid[gy][col];
        if (pixelColor) {
          if (pixelColor === 'TEAM') pixelColor = teamBadgeColor;

          const px = startX + gx * pSize;
          const py = startY + gy * pSize;

          // Split trajectory: left half flies left-up (-VX, -VY), right half flies right-up (+VX, -VY)
          const isLeftHalf = (gx < halfGrid);
          const splitDir = isLeftHalf ? -1 : 1;
          const splitSpeed = 45 + Math.random() * 85;
          const splitVx = splitDir * splitSpeed + (Math.random() - 0.5) * 20;
          const splitVy = -45 - Math.random() * 70;
          const pixelGround = groundLevel + (Math.random() * 16 - 8);
          const life = 2.0 + Math.random() * 1.2;

          const p = this.obtainCleanParticle();
          p.x = px;
          p.y = py;
          p.vx = splitVx;
          p.vy = splitVy;
          p.gravity = 380;
          p.drag = 0.72;
          p.groundY = pixelGround;
          p.bounces = 2;
          p.settled = false;
          p.radius = Math.max(1.4, pSize * (isLargeGrid ? 0.65 : 0.5));
          p.color = pixelColor;
          p.isDebris = true;
          p.life = life;
          p.maxLife = life;
          p.alpha = 1.0;
          this.particles.push(p);
        }
      }
    }

    // Cleavage impact sparks & organic blood / fluid spray at the cut seam
    const seamSparks = this.quality === 'low' ? 6 : 16;
    for (let i = 0; i < seamSparks; i++) {
      const angle = Math.random() * Math.PI * 2;
      const spd = 30 + Math.random() * 80;
      const p = this.obtainCleanParticle();
      p.x = originX + (Math.random() - 0.5) * 6;
      p.y = originY + (Math.random() - 0.5) * 12;
      p.vx = Math.cos(angle) * spd;
      p.vy = Math.sin(angle) * spd - 25;
      p.radius = 1.2 + Math.random() * 1.5;
      p.color = ['#ff003c', '#991b1b', '#f59e0b', '#cbd5e1'][Math.floor(Math.random() * 4)];
      p.life = 0.35 + Math.random() * 0.35;
      p.maxLife = 0.7;
      p.alpha = 1.0;
      this.particles.push(p);
    }
  }

  // Disassembles a dead Sentinal Lock into its three wing/body pieces,
  // flying apart along the same ~120°-spaced directions its wings are
  // built at (js/engine/render/unitSprites.js _buildSentinalLockBody) - a
  // 3-way angular split instead of addUnitSplitShatter's left/right cut, so
  // it "zersplits" along its own natural seams. Purely cosmetic (fires once
  // from Unit.die()/takeDamage()'s death branch, never a gameplay effect).
  addSentinalLockTripleShatter(unit) {
    if (!unit) return;
    const unitType = unit.cardData?.unitType || unit.unitType || unit.name;
    if (!unitType) return;
    const spriteData = getSpriteGrid(unitType, unit.race);
    if (!spriteData || !spriteData.grid) return;

    const r = unit.radius || 20;
    const pSize = (r * 2.2) / spriteData.gridSize;
    const teamBadgeColor = unit.team === 'western' ? '#00f0ff' : '#ff003c';
    const isWestern = unit.team === 'western';
    const originX = unit.x;
    const originY = unit.y;
    const groundLevel = unit.y + 24;
    const startX = originX - r * 1.1;
    const startY = originY - r * 1.1;
    const center = spriteData.gridSize / 2;
    const isLargeGrid = spriteData.gridSize >= 16;

    // Three divergent burst directions, ~120° apart, matching the wing
    // layout (up-right / lower-left / lower-right).
    const burstAngles = [-Math.PI / 2 + 0.6, Math.PI / 2 - 0.9, Math.PI / 2 + 0.9];

    for (let gy = 0; gy < spriteData.gridSize; gy++) {
      for (let gx = 0; gx < spriteData.gridSize; gx++) {
        if (isLargeGrid && ((gy + gx) % 2 !== 0)) continue;

        const col = isWestern ? gx : (spriteData.gridSize - 1 - gx);
        let pixelColor = spriteData.grid[gy][col];
        if (pixelColor) {
          if (pixelColor === 'TEAM') pixelColor = teamBadgeColor;

          const px = startX + gx * pSize;
          const py = startY + gy * pSize;

          // Which of the three fragments this pixel belongs to, by its
          // angle around the sprite's own center.
          const pixelAngle = Math.atan2(gy - center, gx - center);
          let sector = 0;
          let bestDiff = Infinity;
          for (let s = 0; s < burstAngles.length; s++) {
            let diff = Math.abs(pixelAngle - burstAngles[s]);
            if (diff > Math.PI) diff = Math.PI * 2 - diff;
            if (diff < bestDiff) { bestDiff = diff; sector = s; }
          }
          const burstAngle = burstAngles[sector] + (Math.random() - 0.5) * 0.3;
          const splitSpeed = 45 + Math.random() * 85;
          const splitVx = Math.cos(burstAngle) * splitSpeed;
          const splitVy = Math.sin(burstAngle) * splitSpeed - 30;
          const pixelGround = groundLevel + (Math.random() * 16 - 8);
          const life = 2.0 + Math.random() * 1.2;

          const p = this.obtainCleanParticle();
          p.x = px;
          p.y = py;
          p.vx = splitVx;
          p.vy = splitVy;
          p.gravity = 380;
          p.drag = 0.72;
          p.groundY = pixelGround;
          p.bounces = 2;
          p.settled = false;
          p.radius = Math.max(1.4, pSize * (isLargeGrid ? 0.65 : 0.5));
          p.color = pixelColor;
          p.isDebris = true;
          p.life = life;
          p.maxLife = life;
          p.alpha = 1.0;
          this.particles.push(p);
        }
      }
    }

    // Liquid-splatter impact sparks at the three seams (bio-moisture palette)
    const seamSparks = this.quality === 'low' ? 6 : 16;
    for (let i = 0; i < seamSparks; i++) {
      const angle = Math.random() * Math.PI * 2;
      const spd = 30 + Math.random() * 80;
      const p = this.obtainCleanParticle();
      p.x = originX + (Math.random() - 0.5) * 6;
      p.y = originY + (Math.random() - 0.5) * 12;
      p.vx = Math.cos(angle) * spd;
      p.vy = Math.sin(angle) * spd - 25;
      p.radius = 1.2 + Math.random() * 1.5;
      p.color = ['#84cc16', '#4d7c0f', '#a3e635', '#cbd5e1'][Math.floor(Math.random() * 4)];
      p.life = 0.35 + Math.random() * 0.35;
      p.maxLife = 0.7;
      p.alpha = 1.0;
      this.particles.push(p);
    }
  }

  // Tactical Smoke Screen deployed by Hyper Tesla Walker
  addSmokeScreen(x, y, radius = 60) {
    const count = this.quality === 'low' ? 6 : (this.quality === 'medium' ? 12 : 20);
    const smokeColors = ['#94a3b8', '#64748b', '#475569', '#334155', '#1e293b'];
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * (radius * 0.6);
      const spd = 12 + Math.random() * 32;
      const life = 3.0 + Math.random() * 2.0;
      const p = this.obtainCleanParticle();
      p.x = x + Math.cos(angle) * dist;
      p.y = y + Math.sin(angle) * dist;
      p.vx = Math.cos(angle) * spd * 0.4;
      p.vy = -8 - Math.random() * 18;
      p.radius = 8 + Math.random() * 14;
      p.expandRate = 1.08;
      p.color = smokeColors[Math.floor(Math.random() * smokeColors.length)];
      p.life = life;
      p.maxLife = life;
      p.initialAlpha = 0.65;
      p.alpha = 0.65;
      p.isSmoke = true;
      this.particles.push(p);
    }
    this.addLight(x, y, radius * 1.5, '#64748b', 0.5);
  }

  // Multi-Stage 8-Bit Explosion
  addExplosion(x, y, radius = 25, color = '#ff7700', count = 18, shakeIntensity = 0, noSmoke = false) {
    if (shakeIntensity > 0) {
      this.addScreenShake(shakeIntensity, 0.2);
    }

    // Radial blast impulse on existing ambient smoke, dust and debris
    this.disperseNearbySmoke(x, y, radius);

    // Reactive point light - every blast briefly lights up the ground and
    // nearby units, scaled to how big the explosion actually is.
    this.addLight(x, y, radius * 2.4, color, 0.18 + Math.min(0.35, radius * 0.0035));

    // Real explosions (not small flame licks or muzzle puffs) leave a
    // lingering scorch mark on the ground.
    if (radius >= 30) {
      this.addScorchMark(x, y, radius * 0.9);
    }

    // 1. Center Flash Pixel Block (instant bright flash)
    const pFlash = this.obtainCleanParticle();
    pFlash.x = x;
    pFlash.y = y;
    pFlash.vx = 0;
    pFlash.vy = 0;
    pFlash.radius = radius * 0.8;
    pFlash.color = '#ffffff';
    pFlash.life = 0.06;
    pFlash.maxLife = 0.06;
    pFlash.alpha = 1.0;
    this.pushParticle(pFlash);

    // 2. High-energy burst particles
    const fireColors = ['#ffffff', '#fef08a', '#fbbf24', '#f97316', '#ef4444', color];
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (Math.random() * 0.8 + 0.3) * radius * 5.5;
      const life = 0.25 + Math.random() * 0.35;
      const p = this.obtainCleanParticle();
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.gravity = 40;
      p.drag = 2.5;
      p.radius = 2.5 + Math.random() * (radius * 0.16);
      p.color = fireColors[Math.floor(Math.random() * fireColors.length)];
      p.life = life;
      p.maxLife = life;
      p.alpha = 1.0;
      this.pushParticle(p);
    }

    // 3. Lingering rising & expanding 8-bit smoke & battlefield haze columns
    if (!noSmoke) {
      const smokeCount = Math.max(6, Math.floor(count * 0.65));
      const smokeColors = ['#64748b', '#475569', '#334155', '#1e293b', '#0f172a'];
      for (let i = 0; i < smokeCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * radius * 1.3;
        const life = 2.4 + Math.random() * 2.8; // Lingers for 2.4 - 5.2 seconds!
        const p = this.obtainCleanParticle();
        p.x = x + (Math.random() * 16 - 8);
        p.y = y + (Math.random() * 12 - 6);
        p.vx = Math.cos(angle) * speed + (Math.random() * 8 - 4);
        p.vy = -12 - Math.random() * 22; // Drift upward slowly like real smoke plume
        p.drag = 0.6;
        p.radius = 4 + Math.random() * 4;
        p.expandRate = 3.2 + Math.random() * 2.8; // Expands into thick cloud
        p.color = smokeColors[Math.floor(Math.random() * smokeColors.length)];
        p.life = life;
        p.maxLife = life;
        p.initialAlpha = 0.45 + Math.random() * 0.25;
        p.alpha = 0.5;
        p.isSmoke = true;
        this.pushParticle(p);
      }
    }

    // 4. Ground Dirt & Debris Shockwave Dust (Billowing along the floor)
    for (let d = 0; d < 6; d++) {
      const dir = d % 2 === 0 ? 1 : -1;
      const dLife = 1.8 + Math.random() * 1.8;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 14 - 7);
      p.y = y + 8;
      p.vx = dir * (18 + Math.random() * 42);
      p.vy = -5 - Math.random() * 10;
      p.drag = 1.1;
      p.radius = 5 + Math.random() * 4;
      p.expandRate = 3.0;
      p.color = '#334155';
      p.life = dLife;
      p.maxLife = dLife;
      p.initialAlpha = 0.35;
      p.alpha = 0.35;
      p.isDust = true;
      this.pushParticle(p);
    }
  }

  // Localized rising smoke column that emits expanding smoke puffs over several seconds
  addLingeringSmokeColumn(x, y, duration = 3.5, intensity = 1.0) {
    const self = this;
    const smokeColors = ['#64748b', '#475569', '#334155', '#1e293b', '#0f172a'];
    const emitter = {
      x, y,
      duration,
      timer: 0,
      interval: 0.12 / Math.max(0.5, intensity),
      update: function(dt) {
        this.duration -= dt;
        this.timer += dt;
        if (this.timer >= this.interval && this.duration > 0) {
          this.timer = 0;
          const life = 2.2 + Math.random() * 2.4;
          self.particles.push({
            x: this.x + (Math.random() * 12 - 6),
            y: this.y + (Math.random() * 8 - 4),
            vx: (Math.random() - 0.5) * 12,
            vy: -14 - Math.random() * 20,
            drag: 0.45,
            radius: 4 + Math.random() * 4,
            expandRate: 3.4,
            color: smokeColors[Math.floor(Math.random() * smokeColors.length)],
            life,
            maxLife: life,
            initialAlpha: 0.40,
            alpha: 0.40,
            isSmoke: true
          });
        }
      }
    };
    this.specialEffects.push(emitter);
  }

  // High-voltage Tesla coil electric arc: multi-branched lightning with
  // crackling dielectric discharge and electrical impact sparks.
  addTeslaArc(x1, y1, x2, y2, color = '#00f0ff') {
    const branches = 2 + Math.floor(Math.random() * 2);
    for (let b = 0; b < branches; b++) {
      const segments = 4 + Math.floor(Math.random() * 3);
      let px = x1, py = y1;
      for (let i = 1; i <= segments; i++) {
        const t = i / segments;
        const jitter = Math.sin(t * Math.PI) * (b === 0 ? 14 : 26);
        const nx = x1 + (x2 - x1) * t + (Math.random() - 0.5) * jitter;
        const ny = y1 + (y2 - y1) * t + (Math.random() - 0.5) * jitter;
        this.addBeam(px, py, nx, ny, b === 0 ? '#ffffff' : color, b === 0 ? 2.5 : 1.5, 0.16);
        px = nx;
        py = ny;
      }
    }
    this.addHitSparks(x2, y2, 'shield', 10);
    this.addLight(x1, y1, 16, color, 0.14);
  }

  // Jagged multi-segment bolt between two points, built from several short
  // addBeam() segments with random jitter - reads as chain lightning instead
  // of a single straight laser line.
  addLightningArc(x1, y1, x2, y2) {
    const colors = ['#e9d5ff', '#c084fc', '#38bdf8'];
    const color = colors[Math.floor(Math.random() * colors.length)];
    const segments = 3 + Math.floor(Math.random() * 2);
    let px = x1, py = y1;
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const jitter = (1 - t) * 14;
      const nx = x1 + (x2 - x1) * t + (Math.random() - 0.5) * jitter;
      const ny = y1 + (y2 - y1) * t + (Math.random() - 0.5) * jitter;
      this.addBeam(px, py, nx, ny, color, 1.5, 0.14);
      px = nx;
      py = ny;
    }
  }

  addBeam(x1, y1, x2, y2, color = '#00ffff', width = 2, duration = 0.1, options = null) {
    if (!isFinite(x1) || !isFinite(y1) || !isFinite(x2) || !isFinite(y2)) return;
    const dur = Math.max(0.04, duration || 0.1);
    this.beams.push({
      x1, y1, x2, y2,
      color,
      width: Math.max(1, width || 2),
      life: dur,
      maxLife: dur,
      isBastionBeam: !!options?.isBastionBeam
    });
  }

  addTracer(x1, y1, x2, y2, color = '#ffdd44', duration = 0.08) {
    if (!isFinite(x1) || !isFinite(y1) || !isFinite(x2) || !isFinite(y2)) return;
    const dur = Math.max(0.04, duration || 0.08);
    this.beams.push({
      x1, y1, x2, y2,
      color,
      width: 2.0,
      life: dur,
      maxLife: dur,
      isTracer: true
    });
  }

  addProjectile(startX, startY, targetUnit, color, size, speed, onHit) {
    const dx = targetUnit.x - startX;
    const dy = targetUnit.y - startY;
    const dist = Math.hypot(dx, dy);
    const duration = Math.max(0.05, dist / speed);

    const engine = this;
    const proj = {
      x: startX,
      y: startY,
      target: targetUnit,
      color,
      size,
      speed,
      life: duration,
      maxLife: duration,
      trailTimer: 0,
      update: function(dt) {
        const tx = this.target.x;
        const ty = this.target.y;
        const dirX = tx - this.x;
        const dirY = ty - this.y;
        const d = Math.hypot(dirX, dirY);
        if (d > 0) {
          this.x += (dirX / d) * this.speed * dt;
          this.y += (dirY / d) * this.speed * dt;
        }
        
        // Dynamic Retro Trail
        this.trailTimer += dt;
        if (this.trailTimer >= 0.03) {
          this.trailTimer = 0;
          engine.particles.push({
            x: this.x + (Math.random() * 4 - 2),
            y: this.y + (Math.random() * 4 - 2),
            vx: -(dirX / (d || 1)) * 30 + (Math.random() * 10 - 5),
            vy: -(dirY / (d || 1)) * 30 + (Math.random() * 10 - 5),
            radius: this.size * 0.6,
            color: this.color,
            life: 0.15,
            maxLife: 0.15,
            alpha: 0.9
          });
        }
      },
      onComplete: () => {
        onHit && onHit();
      }
    };
    this.specialEffects.push(proj);
  }

  // Sul Rejected Loaf: Reflected damage dealt as chitin shards returning to attacker
  addChitinShard(startX, startY, target, color = '#c084fc', speed = 420, onHit = null) {
    const targetObj = target || { x: startX + 50, y: startY };
    const getTargetY = (t) => (t && (t.isAir || t.elementType === 'Air')) ? (t.y - 18) : (t ? t.y : startY);
    const tx = typeof targetObj.x === 'number' ? targetObj.x : (startX + 50);
    const ty = typeof targetObj.y === 'number' ? getTargetY(targetObj) : startY;
    const dx = tx - startX;
    const dy = ty - startY;
    const dist = Math.hypot(dx, dy);
    const duration = Math.max(0.08, dist / speed);

    const engine = this;
    if (engine.audio?.playDeflected) {
      engine.audio.playDeflected({ x: startX });
    } else {
      engine.audio?.playRicochet?.({ x: startX });
    }

    // Initial burst of reflective bio-chitin motes at launch point
    if (this.quality !== 'low') {
      for (let i = 0; i < 4; i++) {
        const ang = Math.atan2(dy, dx) + (Math.random() * 0.8 - 0.4);
        const spd = 20 + Math.random() * 40;
        const p = this.obtainCleanParticle();
        p.x = startX;
        p.y = startY;
        p.vx = Math.cos(ang) * spd;
        p.vy = Math.sin(ang) * spd;
        p.radius = 1.5 + Math.random() * 1.5;
        p.color = Math.random() < 0.5 ? '#f0abfc' : '#c084fc';
        p.life = 0.16;
        p.maxLife = 0.16;
        p.alpha = 0.9;
        p.isRound = true;
        this.particles.push(p);
      }
    }

    const shard = {
      isChitinShard: true,
      x: startX,
      y: startY,
      startX,
      startY,
      target: targetObj,
      targetX: tx,
      targetY: ty,
      color,
      speed,
      life: duration,
      maxLife: duration,
      rotation: Math.atan2(dy, dx),
      rotSpeed: (Math.random() < 0.5 ? 1 : -1) * (14 + Math.random() * 8),
      trailTimer: 0,
      update: function(dt) {
        if (this.target && typeof this.target.x === 'number') {
          this.targetX = this.target.x;
          this.targetY = getTargetY(this.target);
        }
        const curDx = this.targetX - this.x;
        const curDy = this.targetY - this.y;
        const curDist = Math.hypot(curDx, curDy);
        if (curDist > 0) {
          const moveStep = Math.min(curDist, this.speed * dt);
          this.x += (curDx / curDist) * moveStep;
          this.y += (curDy / curDist) * moveStep;
          this.rotation += this.rotSpeed * dt;
        }

        // Bio-luminescent trailing chitin motes
        this.trailTimer += dt;
        if (this.trailTimer >= 0.035) {
          this.trailTimer = 0;
          if (engine.quality !== 'low') {
            engine.particles.push({
              x: this.x + (Math.random() * 4 - 2),
              y: this.y + (Math.random() * 4 - 2),
              vx: -(curDx / (curDist || 1)) * 25 + (Math.random() * 8 - 4),
              vy: -(curDy / (curDist || 1)) * 25 + (Math.random() * 8 - 4),
              radius: 1.6 + Math.random() * 1.2,
              color: Math.random() < 0.6 ? '#e879f9' : '#a855f7',
              life: 0.16,
              maxLife: 0.16,
              alpha: 0.85,
              isRound: true
            });
          }
        }
      },
      onComplete: () => {
        onHit && onHit();
      }
    };
    this.specialEffects.push(shard);
    return shard;
  }

  addDualCurvedMissiles(startX, startY, targetUnit, color, size, speed, onHit, trailColor = null) {
    const tx = targetUnit.x;
    const ty = targetUnit.y;
    const dist = Math.hypot(tx - startX, ty - startY);
    const duration = Math.max(0.12, dist / (speed || 480));
    const engine = this;
    let hitReported = false;

    for (let m = 0; m < 2; m++) {
      const curveSign = (m === 0) ? -1 : 1;
      const initialY = startY + curveSign * 6;

      const missile = {
        isCurvedMissile: true,
        startX: startX,
        startY: initialY,
        target: targetUnit,
        color: color || '#f97316',
        trailColor: trailColor || null,
        size: size || 5,
        speed: speed || 480,
        curveSign: curveSign,
        life: duration,
        maxLife: duration,
        trailTimer: 0,
        x: startX,
        y: initialY,
        angle: 0,
        update: function(dt) {
          const progress = 1.0 - Math.max(0, this.life / this.maxLife);
          const curTx = this.target.x;
          const curTy = this.target.y;

          const baseX = this.startX + (curTx - this.startX) * progress;
          const baseY = this.startY + (curTy - this.startY) * progress;

          // Sinusoidal arc offset: expands outward at midpoint, converges on target at end
          const arcOffset = Math.sin(progress * Math.PI) * 28 * this.curveSign;
          const nextX = baseX;
          const nextY = baseY + arcOffset;

          const dx = nextX - this.x;
          const dy = nextY - this.y;
          if (Math.hypot(dx, dy) > 0.001) {
            this.angle = Math.atan2(dy, dx);
          }

          this.x = nextX;
          this.y = nextY;
          if (!this.trailHistory) this.trailHistory = [];
          this.trailHistory.unshift({ x: this.x, y: this.y });
          if (this.trailHistory.length > 7) this.trailHistory.pop();

          // Trail FX (Smoke & Booster flame)
          this.trailTimer += dt;
          if (this.trailTimer >= 0.02) {
            this.trailTimer = 0;
            const dirX = curTx >= this.startX ? 1 : -1;
            // Smoke puff (tinted if trailColor is equipped)
            engine.particles.push({
              x: this.x - dirX * 6 + (Math.random() * 4 - 2),
              y: this.y + (Math.random() * 4 - 2),
              vx: -dirX * 18 + (Math.random() * 8 - 4),
              vy: (Math.random() * 8 - 4),
              radius: this.size * 0.7,
              color: this.trailColor || '#64748b',
              life: 0.22,
              maxLife: 0.22,
              alpha: this.trailColor ? 0.65 : 0.55
            });
            // Thruster fire spark
            engine.particles.push({
              x: this.x - dirX * 4,
              y: this.y,
              vx: -dirX * 24 + (Math.random() * 6 - 3),
              vy: (Math.random() * 6 - 3),
              radius: this.size * 0.45,
              color: this.trailColor || '#f59e0b',
              life: 0.12,
              maxLife: 0.12,
              alpha: 0.95
            });
          }
        },
        onComplete: () => {
          if (!hitReported) {
            hitReported = true;
            onHit && onHit();
          }
        }
      };

      this.specialEffects.push(missile);
    }
  }

  addOrchestraRocket(startX, startY, targetUnit, color, speed, onHit, trailColor = null) {
    const tx = (targetUnit && typeof targetUnit.x === 'number') ? targetUnit.x : startX + 100;
    const ty = (targetUnit && typeof targetUnit.y === 'number') ? targetUnit.y : startY;
    const dist = Math.hypot(tx - startX, ty - startY);
    const duration = Math.max(0.20, dist / (speed || 480));
    const arcHeight = Math.min(130, Math.max(45, dist * 0.26));
    const engine = this;
    let hitReported = false;

    // Launch backblast smoke plume at launcher tube (Initial launch eruption)
    for (let i = 0; i < 6; i++) {
      const dirX = tx >= startX ? -1 : 1;
      this.pushParticle({
        x: startX + dirX * 6 + (Math.random() * 8 - 4),
        y: startY + (Math.random() * 8 - 4),
        vx: dirX * (20 + Math.random() * 25) + (Math.random() * 10 - 5),
        vy: (Math.random() * 16 - 12),
        radius: 4.5 + Math.random() * 4,
        color: trailColor || '#ffffff',
        life: 0.35 + Math.random() * 0.2,
        maxLife: 0.55,
        alpha: 0.85
      });
    }

    const rocket = {
      isOrchestraRocket: true,
      startX,
      startY,
      target: targetUnit,
      targetX: tx,
      targetY: ty,
      color: color || '#f1f5f9',
      trailColor: trailColor || null,
      speed: speed || 480,
      life: duration,
      maxLife: duration,
      arcHeight,
      trailTimer: 0,
      x: startX,
      y: startY,
      angle: 0,
      update: function(dt) {
        const progress = 1.0 - Math.max(0, this.life / this.maxLife);
        const curTx = (this.target && typeof this.target.x === 'number') ? this.target.x : this.targetX;
        const curTy = (this.target && typeof this.target.y === 'number') ? this.target.y : this.targetY;

        const baseX = this.startX + (curTx - this.startX) * progress;
        const arcOffset = -Math.sin(progress * Math.PI) * this.arcHeight;
        const baseY = this.startY + (curTy - this.startY) * progress + arcOffset;

        const dx = baseX - this.x;
        const dy = baseY - this.y;
        if (Math.hypot(dx, dy) > 0.001) {
          this.angle = Math.atan2(dy, dx);
        }

        this.x = baseX;
        this.y = baseY;
        if (!this.trailHistory) this.trailHistory = [];
        this.trailHistory.unshift({ x: this.x, y: this.y });
        if (this.trailHistory.length > 7) this.trailHistory.pop();

        // Dichter Schweif (besonders in der Startphase)
        this.trailTimer += dt;
        const isStartPhase = progress < 0.40;
        const emitInterval = isStartPhase ? 0.015 : 0.03;

        if (this.trailTimer >= emitInterval) {
          this.trailTimer = 0;

          if (isStartPhase) {
            // Startphase: Gewaltige dichte Rauchwolken
            engine.pushParticle({
              x: this.x - Math.cos(this.angle) * 8 + (Math.random() * 6 - 3),
              y: this.y - Math.sin(this.angle) * 8 + (Math.random() * 6 - 3),
              vx: -Math.cos(this.angle) * 15 + (Math.random() * 10 - 5),
              vy: -Math.sin(this.angle) * 15 + (Math.random() * 10 - 7),
              radius: 4.5 + Math.random() * 3.5,
              color: this.trailColor || '#ffffff',
              life: 0.38,
              maxLife: 0.38,
              alpha: 0.90
            });
            engine.pushParticle({
              x: this.x - Math.cos(this.angle) * 12 + (Math.random() * 4 - 2),
              y: this.y - Math.sin(this.angle) * 12 + (Math.random() * 4 - 2),
              vx: -Math.cos(this.angle) * 20 + (Math.random() * 6 - 3),
              vy: (Math.random() * 6 - 3),
              radius: 3.5 + Math.random() * 2.5,
              color: this.trailColor || '#f1f5f9',
              life: 0.28,
              maxLife: 0.28,
              alpha: 0.75
            });
          } else {
            // Marsch- & Sturzflugphase: Kondensstreifen
            engine.pushParticle({
              x: this.x - Math.cos(this.angle) * 8 + (Math.random() * 4 - 2),
              y: this.y - Math.sin(this.angle) * 8 + (Math.random() * 4 - 2),
              vx: -Math.cos(this.angle) * 12 + (Math.random() * 6 - 3),
              vy: (Math.random() * 6 - 3),
              radius: 2.8 + Math.random() * 1.5,
              color: this.trailColor || '#e2e8f0',
              life: 0.22,
              maxLife: 0.22,
              alpha: 0.65
            });
          }

          // Booster flame spark
          engine.pushParticle({
            x: this.x - Math.cos(this.angle) * 6,
            y: this.y - Math.sin(this.angle) * 6,
            vx: -Math.cos(this.angle) * 25 + (Math.random() * 6 - 3),
            vy: (Math.random() * 6 - 3),
            radius: 2.0,
            color: this.trailColor || '#f97316',
            life: 0.10,
            maxLife: 0.10,
            alpha: 0.95
          });
        }
      },
      onComplete: () => {
        if (!hitReported) {
          hitReported = true;
          onHit && onHit();
        }
      }
    };

    this.specialEffects.push(rocket);
  }

  addVultureBioMissile(startX, startY, targetUnit, color = '#ff0033', speed = 520, onHit = null, isIntercept = false, attackerUnitId = null, podIndex = null) {
    const tx = (targetUnit && typeof targetUnit.x === 'number') ? targetUnit.x : startX + 120;
    const ty = (targetUnit && typeof targetUnit.y === 'number') ? targetUnit.y : startY;
    const dist = Math.hypot(tx - startX, ty - startY);
    // High vertical ascent: climbs 90-110px straight into the air before target lock
    const ejectionHeight = 92 + Math.random() * 18;
    const duration = Math.max(0.90, Math.min(1.5, 0.50 + dist / speed));
    const phase1Fraction = 0.38;
    const engine = this;
    let hitReported = false;

    // Launch backblast / bio-spore puff straight UP from dorsal pod
    for (let i = 0; i < 7; i++) {
      this.pushParticle({
        x: startX + (Math.random() * 8 - 4),
        y: startY + (Math.random() * 4 - 2),
        vx: (Math.random() * 10 - 5),
        vy: -25 - Math.random() * 30,
        radius: 3.5 + Math.random() * 2.5,
        color: '#ff2a4b',
        life: 0.28 + Math.random() * 0.15,
        maxLife: 0.45,
        alpha: 0.85
      });
    }

    const missile = {
      isVultureBioMissile: true,
      startX,
      startY,
      target: targetUnit,
      targetX: tx,
      targetY: ty,
      ejectionHeight,
      color: color || '#ff0033',
      isIntercept,
      attackerUnitId,
      podIndex,
      speed,
      life: duration,
      maxLife: duration,
      trailTimer: 0,
      phase: 1,
      x: startX,
      y: startY,
      angle: -Math.PI / 2,
      update: function(dt) {
        const progress = 1.0 - Math.max(0, this.life / this.maxLife);
        const curTx = (this.target && typeof this.target.x === 'number') ? this.target.x : this.targetX;
        const curTy = (this.target && typeof this.target.y === 'number') ? this.target.y : this.targetY;

        let nextX, nextY;
        if (progress < phase1Fraction) {
          // Phase 1: Pure 90-degree vertical rocket ascent straight UP into the sky
          this.phase = 1;
          const p1 = progress / phase1Fraction;
          // Smooth deceleration as it reaches the apex
          const climbEase = 1.0 - Math.pow(1.0 - p1, 2.0);
          nextX = this.startX;
          nextY = this.startY - (this.ejectionHeight * climbEase);
          this.angle = -Math.PI / 2;
        } else {
          // Phase 2: Apex target lock-on & accelerated homing dive
          this.phase = 2;
          const p2 = (progress - phase1Fraction) / (1.0 - phase1Fraction);
          const apexX = this.startX;
          const apexY = this.startY - this.ejectionHeight;

          // Apex crest bezier curve
          const t = p2;
          const u = 1 - t;
          const ctrlX = apexX + (curTx - apexX) * 0.35;
          const ctrlY = apexY - 30; // slight arc over apex crest before diving

          nextX = u * u * apexX + 2 * u * t * ctrlX + t * t * curTx;
          nextY = u * u * apexY + 2 * u * t * ctrlY + t * t * curTy;

          const dx = nextX - this.x;
          const dy = nextY - this.y;
          if (Math.hypot(dx, dy) > 0.001) {
            this.angle = Math.atan2(dy, dx);
          }
        }

        this.x = nextX;
        this.y = nextY;

        this.trailTimer += dt;
        if (this.trailTimer >= 0.02) {
          this.trailTimer = 0;
          if (this.phase === 1) {
            // Vertical bio-propulsion exhaust blasting straight down
            engine.pushParticle({
              x: this.x + (Math.random() * 4 - 2),
              y: this.y + 4,
              vx: (Math.random() * 6 - 3),
              vy: 22 + Math.random() * 25,
              radius: 2.8 + Math.random() * 1.6,
              color: Math.random() > 0.3 ? '#ff0033' : '#ff758f',
              life: 0.26,
              maxLife: 0.26,
              alpha: 0.85
            });
          } else {
            // Angled fiery rocket trail opposite to missile vector
            engine.pushParticle({
              x: this.x - Math.cos(this.angle) * 4,
              y: this.y - Math.sin(this.angle) * 4,
              vx: -Math.cos(this.angle) * 16 + (Math.random() * 4 - 2),
              vy: -Math.sin(this.angle) * 16 + (Math.random() * 4 - 2),
              radius: 2.4 + Math.random() * 1.5,
              color: Math.random() > 0.4 ? '#ff1744' : '#ff8899',
              life: 0.20,
              maxLife: 0.20,
              alpha: 0.80
            });
          }
        }
      },
      onComplete: () => {
        if (!hitReported) {
          hitReported = true;
          if (missile.isIntercept) {
            engine.addExplosion?.(missile.x, missile.y, 22, '#ff0055', 14);
            engine.addSparks?.(missile.x, missile.y, 16, '#ffffff');
            engine.addDamageText?.(missile.x, missile.y - 12, 'INTERCEPTED', 'crit');
          }
          onHit && onHit();
        }
      }
    };

    this.specialEffects.push(missile);
  }

  addExplosiveCrow(startX, startY, targetUnit, onDetonate) {
    const tx = targetUnit.x;
    const ty = targetUnit.y;
    const dist = Math.hypot(tx - startX, ty - startY);
    
    // Multi-phase natural bird flight:
    // 1. Approach flight (0 to approachDuration)
    // 2. Predatory circling overhead (approachDuration to approachDuration + circleDuration)
    // 3. Dive-bomb strike (to totalFlightTime)
    const approachDuration = Math.max(0.45, Math.min(1.1, dist / 380));
    const circleDuration = 0.52;
    const diveDuration = 0.20;
    const totalFlightTime = approachDuration + circleDuration + diveDuration;
    
    const engine = this;
    let hitReported = false;

    const crow = {
      isExplosiveCrow: true,
      startX,
      startY,
      target: targetUnit,
      targetX: tx,
      targetY: ty,
      x: startX,
      y: startY,
      shadowY: ty,
      approachDuration,
      circleDuration,
      diveDuration,
      flightTime: totalFlightTime,
      life: totalFlightTime,
      maxLife: totalFlightTime,
      age: 0,
      wingPhase: 0,
      trailTimer: 0,
      isDiving: false,
      isGliding: false,
      circleExitX: tx,
      circleExitY: ty - 32,
      angle: tx >= startX ? -0.2 : Math.PI + 0.2,
      update: function(dt) {
        this.age += dt;
        this.wingPhase += dt;

        const curTx = (this.target && !this.target.isDead) ? this.target.x : this.targetX;
        const curTy = (this.target && !this.target.isDead) ? this.target.y : this.targetY;
        this.targetX = curTx;
        this.targetY = curTy;
        this.shadowY = curTy + 10;

        let nextX = this.x;
        let nextY = this.y;

        if (this.age < this.approachDuration) {
          // PHASE 1: Approach & Thermal Climb toward target airspace
          const prog = this.age / this.approachDuration;
          const targetAirY = curTy - 32;

          // Smooth curved approach into high circling altitude
          const baseX = this.startX + (curTx - this.startX) * prog;
          const baseY = this.startY + (targetAirY - this.startY) * prog;
          
          // Bird thermal lift (undulates slightly in air)
          const thermalSwoop = Math.sin(prog * Math.PI) * Math.min(22, dist * 0.08);
          nextX = baseX;
          nextY = baseY - thermalSwoop;

          this.isDiving = false;
          // Intermittent flapping vs glide: flaps for 0.25s, glides for 0.15s
          this.isGliding = (this.wingPhase % 0.45) > 0.30;

        } else if (this.age < this.approachDuration + this.circleDuration) {
          // PHASE 2: Predatory Circling Overhead (Shadowing the Prey)
          const circleAge = this.age - this.approachDuration;
          const circleProg = circleAge / this.circleDuration;
          
          // 1.2 turn ellipse spiral centered above target
          const theta = circleProg * (Math.PI * 2 * 1.25);
          const rx = Math.min(32, Math.max(18, dist * 0.1));
          const ry = 14;
          const circleCenterY = curTy - 32;

          nextX = curTx + Math.cos(theta) * rx;
          nextY = circleCenterY + Math.sin(theta) * ry;

          this.circleExitX = nextX;
          this.circleExitY = nextY;
          this.isDiving = false;
          this.isGliding = true; // Soaring glide during circling

        } else {
          // PHASE 3: Fatal Steep Dive-Bomb Strike
          const diveAge = this.age - (this.approachDuration + this.circleDuration);
          const diveProg = Math.min(1.0, diveAge / this.diveDuration);
          
          // Accelerating quadratic dive directly into victim's chest
          const easeDive = diveProg * diveProg;
          nextX = this.circleExitX + (curTx - this.circleExitX) * easeDive;
          nextY = this.circleExitY + (curTy - this.circleExitY) * easeDive;

          this.isDiving = true;
          this.isGliding = false;
        }

        // Calculate smooth trajectory angle
        const dx = nextX - this.x;
        const dy = nextY - this.y;
        if (Math.hypot(dx, dy) > 0.001) {
          this.angle = Math.atan2(dy, dx);
        }

        this.x = nextX;
        this.y = nextY;

        // Shadow & Crimson Ember Trails
        this.trailTimer += dt;
        if (this.trailTimer >= 0.025) {
          this.trailTimer = 0;
          const dirX = curTx >= this.startX ? 1 : -1;
          // Dark shadow smoke wisp
          engine.particles.push({
            x: this.x - dirX * 3 + (Math.random() * 4 - 2),
            y: this.y + (Math.random() * 4 - 2),
            vx: -dirX * 12 + (Math.random() * 6 - 3),
            vy: (Math.random() * 6 - 3),
            radius: this.isDiving ? 4.5 : 3.0,
            color: '#090d16',
            life: 0.16,
            maxLife: 0.16,
            alpha: 0.7
          });
          // Crimson glowing cyber ember
          engine.particles.push({
            x: this.x - dirX * 2,
            y: this.y + (Math.random() * 2 - 1),
            vx: -dirX * 18 + (Math.random() * 8 - 4),
            vy: (Math.random() * 6 - 3),
            radius: this.isDiving ? 2.8 : 1.8,
            color: '#ef4444',
            life: 0.14,
            maxLife: 0.14,
            alpha: 0.95
          });
        }
      },
      onComplete: () => {
        if (!hitReported) {
          hitReported = true;
          onDetonate && onDetonate();
        }
      }
    };

    this.specialEffects.push(crow);
  }

  // 🔴 Sul Parasite Leap: High-velocity visceral leap arc onto victim with bio-impact
  addParasiteLeap(startX, startY, target, onImpact = null) {
    const tx = (target && typeof target.x === 'number') ? target.x : (startX + 120);
    const ty = (target && typeof target.y === 'number') ? target.y : startY;
    const duration = 0.40; // 0.40s visible ballistic pounce, synchronized with 24 sim ticks
    const engine = this;
    let hitReported = false;

    // Launch burst: fleshy spray from vulture's back (Pooled zero-alloc)
    for (let i = 0; i < 8; i++) {
      const p = this.obtainCleanParticle();
      p.x = startX + (Math.random() * 8 - 4);
      p.y = startY + (Math.random() * 8 - 4);
      p.vx = (Math.random() - 0.5) * 50;
      p.vy = -20 - Math.random() * 40;
      p.radius = 2.2 + Math.random() * 2.0;
      p.color = Math.random() > 0.4 ? '#dc2626' : '#7f1d1d';
      p.life = 0.35 + Math.random() * 0.20;
      p.maxLife = 0.55;
      p.alpha = 0.9;
      this.pushParticle(p);
    }

    const leap = {
      isParasiteLeap: true,
      startX,
      startY,
      target,
      targetX: tx,
      targetY: ty,
      life: duration,
      maxLife: duration,
      x: startX,
      y: startY,
      groundY: startY,
      elevation: 0,
      angle: 0,
      update: function(dt) {
        const progress = Math.max(0, Math.min(1.0, 1.0 - (this.life / this.maxLife)));
        const curTx = (this.target && typeof this.target.x === 'number') ? this.target.x : this.targetX;
        const curTy = (this.target && typeof this.target.y === 'number') ? this.target.y : this.targetY;

        // Ballistic arc with mid-air peak
        const arcPeak = 48;
        const arcY = -Math.sin(progress * Math.PI) * arcPeak;
        const nextX = this.startX + (curTx - this.startX) * progress;
        const baseGroundY = this.startY + (curTy - this.startY) * progress;
        const nextY = baseGroundY + arcY;

        this.angle = Math.atan2(nextY - this.y, nextX - this.x);
        this.x = nextX;
        this.y = nextY;
        this.groundY = baseGroundY;
        this.elevation = -arcY;

        // Bio-trail behind meatball: dripping visceral droplets along the arc (Pooled zero-alloc)
        if (engine.canSpawnCosmetic && engine.canSpawnCosmetic()) {
          const p = engine.obtainCleanParticle();
          p.x = this.x + (Math.random() - 0.5) * 5;
          p.y = this.y + (Math.random() - 0.5) * 5;
          p.vx = -Math.cos(this.angle) * 20 + (Math.random() - 0.5) * 12;
          p.vy = -Math.sin(this.angle) * 20 + (Math.random() - 0.5) * 12;
          p.radius = 2.0 + Math.random() * 1.8;
          p.color = Math.random() > 0.5 ? '#ef4444' : '#991b1b';
          p.life = 0.25 + Math.random() * 0.15;
          p.maxLife = 0.4;
          p.alpha = 0.95;
          engine.pushParticle(p);
        }
      },
      onComplete: function() {
        if (!hitReported) {
          hitReported = true;
          const impactX = (this.target && typeof this.target.x === 'number') ? this.target.x : this.x;
          const impactY = (this.target && typeof this.target.y === 'number') ? this.target.y : this.y;

          // Bio impact: crimson shockwave & splatter
          engine.addShockwave(impactX, impactY, 42, '#dc2626', 0.28);
          engine.addShockwave(impactX, impactY, 20, '#ffffff', 0.16);

          const splatterCount = engine.quality === 'low' ? 8 : 18;
          for (let i = 0; i < splatterCount; i++) {
            const ang = Math.random() * Math.PI * 2;
            const spd = 30 + Math.random() * 70;
            const p = engine.obtainCleanParticle();
            p.x = impactX;
            p.y = impactY;
            p.vx = Math.cos(ang) * spd;
            p.vy = Math.sin(ang) * spd - 15;
            p.gravity = 55;
            p.radius = 2.2 + Math.random() * 2.2;
            p.color = PARASITE_SPLATTER_COLORS[Math.floor(Math.random() * PARASITE_SPLATTER_COLORS.length)];
            p.life = 0.40 + Math.random() * 0.25;
            p.maxLife = 0.65;
            p.alpha = 0.95;
            engine.pushParticle(p);
          }

          onImpact && onImpact();
        }
      }
    };

    this.specialEffects.push(leap);
    return leap;
  }

  addSulEnergyOrb(startX, startY, targetX, targetY, onImpact = null) {
    const shell = this.addBallisticBaseShell(startX, startY, targetX, targetY, onImpact, '#d946ef');
    if (shell) shell.isSulEnergyOrb = true;
    return shell;
  }

  addBallisticBaseShell(startX, startY, targetX, targetY, onImpact = null, color = null) {
    const dist = Math.hypot(targetX - startX, targetY - startY);
    const totalFlightTime = Math.max(0.20, Math.min(0.38, dist / 950));
    const maxArcHeight = Math.min(12, Math.max(4, dist * 0.025));
    const engine = this;
    const isSul = color === '#d946ef';

    const shell = {
      isBallisticShell: true,
      isSulEnergyOrb: isSul,
      startX,
      startY,
      targetX,
      targetY,
      color: color || null,
      x: startX,
      y: startY,
      arcHeight: maxArcHeight,
      flightTime: totalFlightTime,
      age: 0,
      life: totalFlightTime,
      maxLife: totalFlightTime,
      trailTimer: 0,
      angle: targetX >= startX ? 0 : Math.PI,
      update: function(dt) {
        this.age += dt;
        const prog = Math.min(1.0, this.age / this.flightTime);
        
        // Ground position (X, Y)
        this.x = this.startX + (this.targetX - this.startX) * prog;
        this.y = this.startY + (this.targetY - this.startY) * prog;
        
        // Very flat, kinetic horizontal flight trajectory
        this.elevation = 4 * this.arcHeight * prog * (1 - prog);
        this.drawY = this.y - this.elevation;

        // Strictly horizontal flight orientation (left -> right = 0 rad, right -> left = PI rad)
        const isFacingRight = this.targetX >= this.startX;
        this.angle = isFacingRight ? 0 : Math.PI;

        // Subtle, compact Mach tracer sparks trailing behind the shell / orb
        this.trailTimer += dt;
        if (this.trailTimer >= 0.02) {
          this.trailTimer = 0;
          const backX = isFacingRight ? -4 : 4;
          const sparkColors = (this.isSulEnergyOrb || this.color === '#d946ef')
            ? ['#ffffff', '#fdf4ff', '#e879f9', '#fef08a', '#d946ef']
            : (this.color ? ['#ffffff', this.color, this.color] : ['#ffffff', '#fde047', '#f97316']);
          const p = engine.obtainCleanParticle();
          p.x = this.x + backX + (Math.random() * 2 - 1);
          p.y = this.drawY + (Math.random() * 2 - 1);
          p.vx = (isFacingRight ? -1 : 1) * (20 + Math.random() * 15);
          p.vy = (Math.random() * 4 - 2);
          p.radius = (this.isSulEnergyOrb || this.color === '#d946ef') ? (1.5 + Math.random() * 1.0) : (1.0 + Math.random() * 0.6);
          p.color = sparkColors[Math.floor(Math.random() * sparkColors.length)];
          p.life = 0.08;
          p.maxLife = 0.08;
          p.alpha = 0.9;
          engine.particles.push(p);
        }
      },
      onComplete: () => {
        if (shell.isSulEnergyOrb || shell.color === '#d946ef') {
          engine.addShockwave(targetX, targetY, 36, '#d946ef', 0.24);
          engine.addExplosion(targetX, targetY, 26, '#ec4899', 16);
          engine.addScorchMark(targetX, targetY, 14);

          for (let i = 0; i < 12; i++) {
            const angle = Math.random() * Math.PI * 2;
            const spd = 45 + Math.random() * 85;
            const p = engine.obtainCleanParticle();
            p.x = targetX;
            p.y = targetY;
            p.vx = Math.cos(angle) * spd;
            p.vy = Math.sin(angle) * spd * 0.7 - 10;
            p.radius = 1.5 + Math.random() * 2;
            p.color = ['#ffffff', '#fef08a', '#e879f9', '#d946ef', '#a855f7'][Math.floor(Math.random() * 5)];
            p.life = 0.28 + Math.random() * 0.2;
            p.maxLife = 0.48;
            p.alpha = 1.0;
            engine.particles.push(p);
          }
        } else {
          engine.addShockwave(targetX, targetY, 32, '#f97316', 0.2);
          engine.addExplosion(targetX, targetY, 24, '#facc15', 14);
          engine.addScorchMark(targetX, targetY, 14);

          for (let i = 0; i < 10; i++) {
            const angle = Math.random() * Math.PI * 2;
            const spd = 40 + Math.random() * 80;
            const p = engine.obtainCleanParticle();
            p.x = targetX;
            p.y = targetY;
            p.vx = Math.cos(angle) * spd;
            p.vy = -Math.abs(Math.sin(angle) * spd) - 15;
            p.radius = 1.5 + Math.random() * 2;
            p.color = ['#ffffff', '#facc15', '#f97316', '#ef4444'][Math.floor(Math.random() * 4)],
            p.life = 0.25 + Math.random() * 0.2;
            p.maxLife = 0.45;
            p.alpha = 1.0;
            engine.particles.push(p);
          }
        }

        onImpact && onImpact();
      }
    };
    this.specialEffects.push(shell);
    return shell;
  }

  // Fungal-spore-style ballistic lob (originally Sul Apostate Loaf's mortar,
  // now shared with any unit that lobs a glob - e.g. Sentinal Lock's toxic
  // bite). Fires a glob towards target coordinates and splatters on impact
  // with a bio-shockwave. `options` lets a second unit re-theme/reshape the
  // lob without a separate copy of this function:
  //   - palette: overrides the trail/drip/impact color set below (default =
  //     Apostate's original fungal green/violet/cyan mix) - the dominant
  //     glob-body color (`color`, already wired to each unit's own
  //     equippable customBulletColor) commonly needs per-unit tuning on its
  //     own; the secondary spark colors stay themed per palette instead of
  //     following that custom color.
  //   - arcAngleDeg: launch angle off the horizontal at the muzzle, in
  //     degrees (Apostate's steep mortar-like default is left as an
  //     unangled/dist-proportional arcHeight below when this is omitted -
  //     "die vom Apostate belassen as is").
  //   - sizeScale: uniform scale on the rendered glob's own size (see
  //     addSporeLob's isSporeLob branch in the particle-render switch),
  //     default 1 (Apostate's original clump size).
  addSporeLob(startX, startY, targetX, targetY, onImpact = null, color = null, options = null) {
    const dist = Math.hypot(targetX - startX, targetY - startY);
    // Soaring high-arc parabolic lob flight duration (0.48s to 0.76s)
    const totalFlightTime = Math.max(0.48, Math.min(0.76, 0.38 + dist * 0.0016));
    // Steep parabolic arc (elevation rises 50px to 95px above ground) unless
    // an explicit launch angle was requested - tan(angle) = 4*arcHeight/dist
    // for this update()'s symmetric "4*h*p*(1-p)" parabola, solved for
    // arcHeight so a shallow angle (e.g. Sentinal Lock's ~5°) reads as
    // nearly horizontal instead of Apostate's lofted mortar arc.
    const maxArcHeight = (options?.arcAngleDeg != null)
      ? Math.max(2, dist * Math.tan(options.arcAngleDeg * Math.PI / 180) / 4)
      : Math.min(95, Math.max(50, dist * 0.38));
    const engine = this;
    const sporeColor = color || '#84cc16';
    const sizeScale = options?.sizeScale || 1;
    const noSmoke = options?.noSmoke === true;
    const pal = {
      trail: ['#84cc16', '#bef264', '#a855f7', '#65a30d', '#4d7c0f', '#00f0ff'],
      drip: ['#65a30d', '#84cc16'],
      splatter: ['#bef264', '#84cc16', '#a855f7', '#00f0ff', '#4d7c0f'],
      shockwave: '#84cc16',
      explosion: '#65a30d',
      scorch: 'rgba(40, 60, 10, 0.45)',
      ...options?.palette
    };

    const spore = {
      isSporeLob: true,
      startX,
      startY,
      targetX,
      targetY,
      color: sporeColor,
      palette: pal,
      sizeScale,
      noSmoke,
      x: startX,
      y: startY,
      arcHeight: maxArcHeight,
      flightTime: totalFlightTime,
      age: 0,
      life: totalFlightTime,
      maxLife: totalFlightTime,
      trailTimer: 0,
      rotAngle: 0,
      elevation: 0,
      drawY: startY,
      update: function(dt) {
        this.age += dt;
        const prog = Math.min(1.0, this.age / this.flightTime);

        this.x = this.startX + (this.targetX - this.startX) * prog;
        this.y = this.startY + (this.targetY - this.startY) * prog;

        // High parabolic arc elevation (peaks at midpoint: prog = 0.5)
        this.elevation = 4 * this.arcHeight * prog * (1 - prog);
        this.drawY = this.y - this.elevation;

        // Tumbling organic rotation during flight
        this.rotAngle += dt * 7.5;

        // Trail of bioluminescent fungal spore motes, powdery smoke & dropping globs
        this.trailTimer += dt;
        if (this.trailTimer >= 0.018) {
          this.trailTimer = 0;
          // 1. Powdery spore smoke mote lingering in the high-arc wake (suppressed when noSmoke is true)
          if (!this.noSmoke) {
            engine.particles.push({
              x: this.x + (Math.random() * 6 - 3),
              y: this.drawY + (Math.random() * 6 - 3),
              vx: (Math.random() - 0.5) * 10,
              vy: 2 + Math.random() * 6,
              radius: 1.0 + Math.random() * 1.4,
              color: pal.trail[Math.floor(Math.random() * pal.trail.length)],
              life: 0.28 + Math.random() * 0.16,
              maxLife: 0.44,
              alpha: 0.8
            });
          }

          // 2. Dripping viscous spore globule falling from the clumpy mass in flight
          if (Math.random() < 0.45) {
            engine.particles.push({
              x: this.x + (Math.random() * 4 - 2),
              y: this.drawY + 2,
              vx: (Math.random() - 0.5) * 6,
              vy: 10 + Math.random() * 12, // falling downward
              radius: 1.5 + Math.random() * 1.5,
              color: pal.drip[Math.floor(Math.random() * pal.drip.length)],
              life: 0.24 + Math.random() * 0.12,
              maxLife: 0.36,
              alpha: 0.9
            });
          }
        }
      },
      onComplete: () => {
        // Impact spore burst & bio shockwave
        engine.addShockwave(targetX, targetY, 28, pal.shockwave, 0.22);
        engine.addExplosion(targetX, targetY, 20, pal.explosion, 12, 0, noSmoke);
        engine.addAcidSplash(targetX, targetY, 8);
        engine.addScorchMark(targetX, targetY, 12, pal.scorch);

        // Splattering spore motes in toxic green & Sul bio colors
        for (let i = 0; i < 10; i++) {
          const ang = Math.random() * Math.PI * 2;
          const spd = 30 + Math.random() * 65;
          engine.particles.push({
            x: targetX,
            y: targetY,
            vx: Math.cos(ang) * spd,
            vy: Math.sin(ang) * spd * 0.65 - 8,
            radius: 1.5 + Math.random() * 2.0,
            color: pal.splatter[Math.floor(Math.random() * pal.splatter.length)],
            life: 0.28 + Math.random() * 0.2,
            maxLife: 0.48,
            alpha: 0.95
          });
        }

        onImpact && onImpact();
      }
    };

    this.specialEffects.push(spore);
    return spore;
  }

  // Processing Pillar: 3-Second Plasma Bubble Gathering
  addPlasmaBubbleGather(x, y, progress, faceDir = 1) {
    // Runs every tick for the full 3s charge (unit.js's Processing Pillar
    // plasma-channel phase), unthrottled - unlike sibling cosmetic emitters
    // (e.g. the smoke emitter above) it had no budget check before pushing,
    // so several simultaneous Pillars channeling at once could spike the
    // particle array past MAX_PARTICLES within a single frame before the
    // end-of-tick trim catches up.
    if (!this.canSpawnCosmetic()) return;
    const isLow = this.quality === 'low';
    const count = isLow ? 1 : 2;
    // Spread contracts as progress approaches 1.0 (blasen morphen zusammen)
    const clusterSpread = Math.max(4, 18 * (1.0 - progress * 0.75));
    const colors = ['#ffffff', '#67e8f9', '#c084fc', '#38bdf8', '#e0f2fe'];
    for (let i = 0; i < count; i++) {
      const ang = Math.random() * Math.PI * 2;
      const r = Math.random() * clusterSpread;
      const bx = x + Math.cos(ang) * r;
      const by = y + Math.sin(ang) * r;
      const vx = -Math.cos(ang) * (8 + progress * 20);
      const vy = -Math.sin(ang) * (8 + progress * 20) - 4;
      const rad = 1.2 + Math.random() * (1.8 + progress * 1.5);
      const p = this.obtainCleanParticle();
      p.x = bx;
      p.y = by;
      p.vx = vx;
      p.vy = vy;
      p.radius = rad;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.life = 0.16 + Math.random() * 0.12;
      p.maxLife = 0.28;
      p.alpha = 0.85;
      p.isBubble = true;
      p.isRound = true;
      this.pushParticle(p);
    }

    // Flanking/side-drifting smaller bubbles ("hier und da kleinere bubbles zu seite fliegen")
    if (Math.random() < (isLow ? 0.35 : 0.65)) {
      const sideDir = (Math.random() < 0.5 ? -1 : 1);
      // Lateral trajectory away from center with subtle buoyant float
      const sideAngle = (sideDir === 1 ? 0 : Math.PI) + (Math.random() - 0.5) * 0.85;
      const sideSpeed = 15 + Math.random() * 25;
      const sideRad = 0.8 + Math.random() * 1.2;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 4 - 2);
      p.y = y + (Math.random() * 4 - 2);
      p.vx = Math.cos(sideAngle) * sideSpeed;
      p.vy = Math.sin(sideAngle) * sideSpeed * 0.35 - (5 + Math.random() * 8);
      p.radius = sideRad;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.life = 0.20 + Math.random() * 0.16;
      p.maxLife = 0.36;
      p.alpha = 0.85;
      p.isBubble = true;
      p.isRound = true;
      this.pushParticle(p);
    }
  }

  // Processing Pillar: Plasma Bubble Fizzle (when target dies < 3s)
  addPlasmaFizzle(x, y) {
    const colors = ['#c084fc', '#38bdf8', '#ffffff', '#93c5fd'];
    const count = this.quality === 'low' ? 4 : 8;
    for (let i = 0; i < count; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 15 + Math.random() * 25;
      const p = this.obtainCleanParticle();
      p.x = x + (Math.random() * 8 - 4);
      p.y = y + (Math.random() * 8 - 4);
      p.vx = Math.cos(ang) * spd;
      p.vy = Math.sin(ang) * spd - 6;
      p.radius = 1.5 + Math.random() * 1.5;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.life = 0.20 + Math.random() * 0.15;
      p.maxLife = 0.35;
      p.alpha = 0.7;
      p.isBubble = true;
      p.isRound = true;
      this.pushParticle(p);
    }
  }

  // Processing Pillar: small plasma bubble-burst at the target's body center
  // on impact (distinct from the ground/impact-point flash - reads as the
  // plasma popping against the enemy's body, not just at their feet)
  addImpactBubbleBurst(target, fallbackX, fallbackY) {
    let bx = fallbackX;
    let by = fallbackY;
    if (target && !target.isDead) {
      bx = target.x;
      by = target.isAir ? (target.y - 18) : (target.y - Math.max(8, (target.radius || 18) * 0.45));
    }

    // Bright strobing core - the "blitzartig" pop right on the enemy's body,
    // big and brief enough to read clearly instead of blending into the
    // surrounding explosion/flash particles.
    this.addLight?.(bx, by, 70, '#ffffff', 0.12);
    const pFlash = this.obtainCleanParticle();
    pFlash.x = bx;
    pFlash.y = by;
    pFlash.vx = 0;
    pFlash.vy = 0;
    pFlash.radius = 20;
    pFlash.color = '#ffffff';
    pFlash.life = 0.09;
    pFlash.maxLife = 0.09;
    pFlash.alpha = 1.0;
    pFlash.isBubble = true;
    pFlash.isRound = true;
    this.pushParticle(pFlash);

    const colors = ['#ffffff', '#67e8f9', '#c084fc', '#e0f2fe'];
    const count = this.quality === 'low' ? 5 : 10;
    for (let i = 0; i < count; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 30 + Math.random() * 55;
      const p = this.obtainCleanParticle();
      p.x = bx + (Math.random() * 6 - 3);
      p.y = by + (Math.random() * 6 - 3);
      p.vx = Math.cos(ang) * spd;
      p.vy = Math.sin(ang) * spd - 10;
      p.radius = 3.5 + Math.random() * 4;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      p.life = 0.18 + Math.random() * 0.14;
      p.maxLife = 0.32;
      p.alpha = 0.9;
      p.isBubble = true;
      p.isRound = true;
      this.pushParticle(p);
    }
  }

  // Processing Pillar: Slow Homing Plasma Sphere with Splash Damage (Ground Attack)
  // Das Projektil ist die große Kugel ("Große Kugel wird losgeschickt") begleitet von kleineren Bubbles
  addHomingPlasmaSphere(startX, startY, target, splashRadius, damage, team, sourceUnit, flightTimeSec = 1.0) {
    const engine = this;

    // Companion smaller bubbles accompanying the big sphere ("behalte die aktuell gefeuerten kleinen bubbles zusätzlich")
    const colors = ['#ffffff', '#67e8f9', '#c084fc', '#38bdf8', '#bae6fd'];
    const escortCount = (engine.quality === 'low' ? 2 : 4);
    for (let c = 0; c < escortCount; c++) {
      const cAng = (c / escortCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const cDist = 8 + Math.random() * 6;
      const cRad = 2.2 + Math.random() * 1.8;
      engine.particles.push({
        x: startX + Math.cos(cAng) * cDist,
        y: startY + Math.sin(cAng) * cDist,
        vx: (Math.random() * 16 - 8),
        vy: (Math.random() * 16 - 8) - 2,
        radius: cRad,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 0.35 + Math.random() * 0.25,
        maxLife: 0.60,
        alpha: 0.9,
        isBubble: true,
        isRound: true
      });
    }

    const sphere = {
      isPlasmaSphere: true,
      x: startX,
      y: startY,
      target: target,
      targetX: target ? target.x : startX,
      targetY: target ? target.y : startY,
      splashRadius: splashRadius || 50,
      damage: damage,
      team: team,
      sourceUnit: sourceUnit,
      speed: 130, // Bedrohlich langsame Anmutung
      life: flightTimeSec,
      maxLife: flightTimeSec,
      trailTimer: 0,
      radius: 13.5, // Die große Kugel (identisch mit der Kugel aus den Händen)
      hasImpacted: false,
      update: function(dt) {
        if (this.hasImpacted) return;
        this.life -= dt;

        if (this.target && !this.target.isDead) {
          this.targetX = this.target.x;
          this.targetY = this.target.isAir ? (this.target.y - 12) : this.target.y;
        }

        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        const dist = Math.hypot(dx, dy);

        if (dist > 3) {
          const moveDist = Math.min(dist, this.speed * dt);
          this.x += (dx / dist) * moveDist;
          this.y += (dy / dist) * moveDist;
        }

        // Trailing plasma motes & smaller escort bubbles
        this.trailTimer += dt;
        if (this.trailTimer >= 0.035) {
          this.trailTimer = 0;
          if (engine.quality !== 'low') {
            engine.particles.push({
              x: this.x + (Math.random() * 8 - 4),
              y: this.y + (Math.random() * 8 - 4),
              vx: (Math.random() * 10 - 5),
              vy: (Math.random() * 10 - 5) - 3,
              radius: 2.2 + Math.random() * 2.2,
              color: Math.random() < 0.55 ? '#67e8f9' : '#c084fc',
              life: 0.24,
              maxLife: 0.24,
              alpha: 0.85,
              isBubble: true,
              isRound: true
            });
          }
        }

        if (dist <= 12 || this.life <= 0) {
          this.hasImpacted = true;
          this.life = 0;
          this.onImpact();
        }
      },
      onImpact: function() {
        // Blinding white-hot core flash on impact (brighter/whiter than the
        // colored shockwave/scorch so the actual damage hit reads clearly)
        engine.addLight?.(this.x, this.y, 160, '#ffffff', 0.2);
        engine.particles.push({
          x: this.x, y: this.y,
          vx: 0, vy: 0,
          radius: 40,
          color: '#ffffff',
          life: 0.08,
          maxLife: 0.08,
          alpha: 1.0,
          isRound: true,
          isBubble: true
        });
        engine.addShockwave?.(this.x, this.y, 48, '#c084fc', 0.25);
        engine.addExplosion?.(this.x, this.y, 25, '#38bdf8', 12);
        engine.addScorchMark?.(this.x, this.y, 14);
        engine.addImpactBubbleBurst?.(this.target, this.x, this.y);
        const audio = engine.audio || engine.game?.audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
        audio?.stopProcessingPillar?.(true);
        audio?.playExplosion?.('medium', { x: this.x, volume: 0.5 }); // 50% leiser auf Nutzer-Feedback
      }
    };
    this.specialEffects.push(sphere);
    return sphere;
  }

  // Processing Pillar: Elastic Piercing Plasma Spear (Air Attack)
  addElasticPlasmaSpear(startX, startY, target, damage, team, sourceUnit, customTravelTime = null) {
    const engine = this;
    const isTargetAir = !!(target?.isAir || target?.elementType === 'Air');
    const destX = target && typeof target.x === 'number' ? target.x : (startX + 120);
    const destY = target && typeof target.y === 'number' ? (isTargetAir ? (target.y - 18) : target.y) : startY;
    const dirX = destX - startX;
    const dirY = destY - startY;
    const totalDist = Math.hypot(dirX, dirY) || 1;
    const normX = dirX / totalDist;
    const normY = dirY / totalDist;

    // Continues 35px past the target to pierce through!
    const pierceEndX = destX + normX * 35;
    const pierceEndY = destY + normY * 35;
    const fullDist = totalDist + 35;
    const spearSpeed = 750; // Elastic forward snap!
    const travelTime = customTravelTime !== null ? customTravelTime : (fullDist / spearSpeed);

    const spear = {
      isElasticPlasmaSpear: true,
      startX: startX,
      startY: startY,
      x: startX,
      y: startY,
      targetX: destX,
      targetY: destY,
      pierceEndX: pierceEndX,
      pierceEndY: pierceEndY,
      normX: normX,
      normY: normY,
      target: target,
      team: team,
      sourceUnit: sourceUnit,
      damage: damage,
      speed: spearSpeed,
      travelTime: travelTime,
      age: 0,
      life: travelTime + 0.35, // Lingers for 0.35s as a glowing trail in the air
      maxLife: travelTime + 0.35,
      hasImpacted: false,
      update: function(dt) {
        this.age += dt;
        this.life -= dt;

        if (this.age <= this.travelTime) {
          const prog = Math.min(1.0, this.age / this.travelTime);
          this.x = this.startX + (this.pierceEndX - this.startX) * prog;
          this.y = this.startY + (this.pierceEndY - this.startY) * prog;

          if (!this.hasImpacted && prog >= (totalDist / fullDist)) {
            this.hasImpacted = true;
            this.onImpact();
          }
        } else {
          this.x = this.pierceEndX;
          this.y = this.pierceEndY;
        }
      },
      onImpact: function() {
        // Find accurate hit location on the target (accounting for elevation & movement)
        let hitX = this.targetX;
        let hitY = this.targetY;
        if (this.target && typeof this.target.x === 'number') {
          hitX = this.target.x;
          hitY = (this.target.isAir || this.target.elementType === 'Air') ? (this.target.y - 18) : this.target.y;
        }

        // 1. Blinding white & cyan core flash
        engine.addLight?.(hitX, hitY, 160, '#ffffff', 0.22);
        engine.particles.push({
          x: hitX, y: hitY,
          vx: 0, vy: 0,
          radius: 38,
          color: '#ffffff',
          life: 0.12,
          maxLife: 0.12,
          alpha: 1.0,
          isRound: true,
          isBubble: true
        });

        // 2. Anamorphic horizontal flare
        engine.addAnamorphicFlare?.(hitX, hitY, 130, 4.0, 0.18, '#67e8f9');

        // 3. Expanding plasma shockwave ring
        engine.addShockwave?.(hitX, hitY, 44, '#c084fc', 0.25);

        // 4. Piercing energetic plasma explosion
        engine.addExplosion?.(hitX, hitY, 28, '#38bdf8', 16);

        // 5. Plasma distortion ring
        engine.addDistortionRing?.(hitX, hitY, 36, 0.28, '#38bdf8');

        // 6. Directional piercing needle splinters shooting through the target
        if (engine.quality !== 'low') {
          for (let i = 0; i < 10; i++) {
            const spreadAng = Math.atan2(this.normY, this.normX) + (Math.random() * 0.9 - 0.45);
            const spd = 70 + Math.random() * 80;
            engine.particles.push({
              x: hitX + this.normX * 8,
              y: hitY + this.normY * 8,
              vx: Math.cos(spreadAng) * spd,
              vy: Math.sin(spreadAng) * spd,
              radius: 2.2 + Math.random() * 1.8,
              color: Math.random() < 0.6 ? '#38bdf8' : '#e0f2fe',
              life: 0.25,
              maxLife: 0.25,
              alpha: 0.95
            });
          }
        }

        // 7. Pop glowing Sul plasma bubbles
        engine.addImpactBubbleBurst?.(this.target, hitX, hitY);

        // 8. White arcade hit flash on victim
        if (this.target && !this.target.isDead) {
          this.target.hitFlashTimer = 0.14;
        }

        const audio = engine.audio || engine.game?.audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
        audio?.stopProcessingPillar?.(true);
        audio?.playExplosion?.('medium', { x: hitX, volume: 0.5 }); // 50% leiser auf Nutzer-Feedback
      }
    };
    this.specialEffects.push(spear);
    return spear;
  }

  // Invasion Capsule Sling: a slow, high-lobbed steel pod (not a shell -
  // no explosion on landing, just a dusty touchdown) carrying 3 loaded
  // Marines from the structure to the frontline - see
  // Battlefield.launchInvasionCapsule() in js/engine/battlefield.js and
  // Unit's capsuleUnit ticking in js/engine/unit.js.
  addInvasionCapsule(startX, startY, targetX, targetY, onLand) {
    const dist = Math.hypot(targetX - startX, targetY - startY);
    const totalFlightTime = Math.max(0.9, Math.min(1.6, dist / 550));
    const maxArcHeight = Math.min(70, Math.max(30, dist * 0.09));
    const engine = this;

    const capsule = {
      isInvasionCapsule: true,
      startX,
      startY,
      targetX,
      targetY,
      x: startX,
      y: startY,
      arcHeight: maxArcHeight,
      flightTime: totalFlightTime,
      age: 0,
      life: totalFlightTime,
      maxLife: totalFlightTime,
      trailTimer: 0,
      spinAngle: 0,
      update: function(dt) {
        this.age += dt;
        const prog = Math.min(1.0, this.age / this.flightTime);

        this.x = this.startX + (this.targetX - this.startX) * prog;
        this.y = this.startY + (this.targetY - this.startY) * prog;

        // Parabolic lob (0 at launch/landing, peak at the midpoint) - much
        // higher and slower than the flat artillery shell above, reading as
        // "catapulted" rather than "fired".
        this.elevation = 4 * this.arcHeight * prog * (1 - prog);
        this.drawY = this.y - this.elevation;
        this.spinAngle = prog * Math.PI * 1.5;

        // Thin steel-grey smoke trail
        this.trailTimer += dt;
        if (this.trailTimer >= 0.03) {
          this.trailTimer = 0;
          engine.particles.push({
            x: this.x + (Math.random() * 3 - 1.5),
            y: this.drawY + (Math.random() * 3 - 1.5),
            vx: (Math.random() - 0.5) * 8,
            vy: 6 + Math.random() * 8,
            radius: 1.6 + Math.random() * 1.4,
            expandRate: 1.2,
            color: '#94a3b8',
            life: 0.35,
            maxLife: 0.35,
            initialAlpha: 0.35,
            alpha: 0.35,
            isDust: true
          });
        }
      },
      onComplete: () => {
        // Heavy impact detonation + hatch pop: deals tank-grade splash damage
        engine.addShockwave(targetX, targetY, 34, '#f97316', 0.25);
        engine.addExplosion(targetX, targetY, 26, '#facc15', 14);
        engine.addScorchMark(targetX, targetY, 14);

        // Explosive shrapnel + dust burst
        for (let i = 0; i < 12; i++) {
          const angle = Math.random() * Math.PI * 2;
          const spd = 35 + Math.random() * 65;
          engine.particles.push({
            x: targetX,
            y: targetY,
            vx: Math.cos(angle) * spd,
            vy: -Math.abs(Math.sin(angle) * spd) - 12,
            radius: 1.4 + Math.random() * 1.8,
            color: ['#ffffff', '#facc15', '#f97316', '#94a3b8'][Math.floor(Math.random() * 4)],
            life: 0.25 + Math.random() * 0.25,
            maxLife: 0.5,
            alpha: 1.0
          });
        }
        onLand && onLand();
      }
    };
    this.specialEffects.push(capsule);
  }

  // 🛸 Drone Command: fast, unstoppable suicide quadcopter flying toward ground target
  addSuicideDrone(startX, startY, targetX, targetY, team, flightTime, onLand, simDrone = null, targetRef = null, speedPx = 260) {
    const engine = this;
    const dur = Math.max(0.3, flightTime);
    const drone = {
      isSuicideDrone: true,
      team,
      startX,
      startY,
      targetX,
      targetY,
      targetRef,
      speedPx,
      x: startX,
      y: startY,
      flightTime: dur,
      age: 0,
      life: dur,
      maxLife: dur,
      trailTimer: 0,
      rotorAngle: 0,
      simDrone,
      headingX: team === 'western' ? 1 : -1,
      headingY: 0,
      update: function(dt) {
        this.age += dt;
        this.rotorAngle += dt * 45;

        if (this.simDrone) {
          const prevX = this.x;
          const prevY = this.y;
          this.x = this.simDrone.x;
          this.y = this.simDrone.y;
          this.targetX = this.simDrone.targetX;
          this.targetY = this.simDrone.targetY;

          const moveDx = this.x - prevX;
          const moveDy = this.y - prevY;
          const moveDist = Math.hypot(moveDx, moveDy);
          if (moveDist > 0.01) {
            this.headingX = moveDx / moveDist;
            this.headingY = moveDy / moveDist;
          }

          // Low, fast trajectory with subtle bobbing and initial liftoff arc
          const bob = Math.sin(this.age * 16) * 1.5;
          const liftoffArc = this.age < 0.4 ? -Math.sin((this.age / 0.4) * Math.PI) * 10 : 0;
          this.drawY = this.y - 4 + bob + liftoffArc;

          if (this.simDrone.isDetonated) {
            this.life = 0;
          }
        } else {
          // Dynamic target tracking for online playback or visual-only drones
          if (this.targetRef && !this.targetRef.isDead) {
            this.targetX = this.targetRef.x;
            this.targetY = this.targetRef.isAir ? (this.targetRef.y - 12) : this.targetRef.y;
          }
          const dx = this.targetX - this.x;
          const dy = this.targetY - this.y;
          const dist = Math.hypot(dx, dy);
          const step = (this.speedPx || 260) * dt;

          if (dist <= Math.max(step, 6)) {
            this.x = this.targetX;
            this.y = this.targetY;
            this.life = 0;
          } else {
            this.x += (dx / dist) * step;
            this.y += (dy / dist) * step;
            this.headingX = dx / dist;
            this.headingY = dy / dist;
            const bob = Math.sin(this.age * 16) * 1.5;
            const liftoffArc = this.age < 0.4 ? -Math.sin((this.age / 0.4) * Math.PI) * 10 : 0;
            this.drawY = this.y - 4 + bob + liftoffArc;
          }
        }

        // Trail puffs & high-energy thruster particles
        this.trailTimer += dt;
        if (this.trailTimer >= 0.025) {
          this.trailTimer = 0;
          const isWest = team === 'western';
          const pColor = isWest ? 'rgba(0, 240, 255, 0.7)' : 'rgba(255, 0, 85, 0.7)';
          const bDir = this.headingX !== undefined ? (this.headingX >= 0 ? 1 : -1) : (isWest ? 1 : -1);
          engine.particles.push({
            x: this.x - bDir * 6 + (Math.random() * 2 - 1),
            y: this.drawY + 1.5 + (Math.random() * 2 - 1),
            vx: -bDir * (18 + Math.random() * 12),
            vy: (Math.random() - 0.5) * 8,
            radius: 1.2 + Math.random() * 0.9,
            expandRate: 0.9,
            color: pColor,
            life: 0.22,
            maxLife: 0.22,
            initialAlpha: 0.7,
            alpha: 0.7,
            isDust: true
          });
          if (this.age < 0.45) {
            // Bright white launch spark during takeoff burst
            engine.particles.push({
              x: this.x - bDir * 4,
              y: this.drawY + 1.5,
              vx: -bDir * (32 + Math.random() * 16),
              vy: (Math.random() - 0.5) * 12,
              radius: 1.0,
              expandRate: 0.2,
              color: '#ffffff',
              life: 0.14,
              maxLife: 0.14,
              initialAlpha: 1.0,
              alpha: 1.0
            });
          }
        }
      },
      onComplete: () => {
        // Mini-splash detonation at drone's current impact location
        const impactX = (drone.simDrone && drone.simDrone.x !== undefined) ? drone.simDrone.x : drone.x;
        const impactY = (drone.simDrone && drone.simDrone.y !== undefined) ? drone.simDrone.y : drone.y;
        engine.addShockwave(impactX, impactY, 24, '#f97316', 0.22);
        engine.addExplosion(impactX, impactY, 20, '#facc15', 10);
        engine.addScorchMark(impactX, impactY, 10);
        onLand && onLand();
      }
    };
    this.specialEffects.push(drone);
    return drone;
  }

  addFire(startX, startY, targetX, targetY, customColor = null) {
    const dx = targetX - startX;
    const dy = targetY - startY;
    const dist = Math.hypot(dx, dy);
    const steps = Math.max(1, Math.floor(dist / 14));
    
    let fireColors;
    let explosionColor;

    if (customColor && typeof customColor === 'string' && customColor.startsWith('#')) {
      fireColors = this._generateFlamePalette(customColor);
      explosionColor = customColor;
    } else {
      fireColors = ['#fff7cc', '#fef08a', '#fbbf24', '#f97316', '#ff4400', '#ef4444'];
      explosionColor = '#ff4400';
    }

    const smokeColors = ['#64748b', '#475569', '#334155', '#1e293b', '#0f172a'];

    // 1. Reactive illumination at the target and along the flame path
    this.addLight(targetX, targetY, 65, explosionColor, 0.35);

    for (let i = 0; i <= steps; i++) {
      const stepRatio = i / steps;
      const px = startX + (dx * stepRatio) + (Math.random() * 16 - 8);
      const py = startY + (dy * stepRatio) + (Math.random() * 16 - 8);

      // Erst das Feuer: Dense, licking flame tongues shoot out immediately
      const flameCount = 7;
      for (let j = 0; j < flameCount; j++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (Math.random() * 0.6 + 0.2) * 45;
        const life = 0.35 + Math.random() * 0.30;
        const p = this.obtainCleanParticle();
        p.x = px;
        p.y = py;
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed - 18;
        p.gravity = -15;
        p.drag = 3;
        p.radius = 3 + Math.random() * 5;
        p.color = fireColors[Math.floor(Math.random() * fireColors.length)];
        p.life = life;
        p.maxLife = life;
        p.alpha = 1.0;
        this.particles.push(p);
      }

      // Dann der Rauch: Baut sich zeitversetzt auf, wächst dann zur vollen dichten Schwade!
      // Delay ist gestaffelt nach Flammenfortschritt (Feuer brennt zuerst, Rauch quillt danach hervor)
      const smokeDelay = 0.12 + stepRatio * 0.22;
      const smokePerStep = (i === steps) ? 6 : (i % 2 === 0 ? 3 : 2);
      for (let s = 0; s < smokePerStep; s++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 18;
        const life = 2.4 + Math.random() * 2.2;
        const p = this.obtainCleanParticle();
        p.x = px + (Math.random() * 12 - 6);
        p.y = py + (Math.random() * 10 - 5);
        p.vx = Math.cos(angle) * speed + (Math.random() * 6 - 3);
        p.vy = -14 - Math.random() * 20;
        p.drag = 0.6;
        p.radius = 4 + Math.random() * 3;
        p.expandRate = 3.5 + Math.random() * 2.5;
        p.color = smokeColors[Math.floor(Math.random() * smokeColors.length)];
        p.life = life;
        p.maxLife = life;
        p.initialAlpha = 0.52 + Math.random() * 0.18;
        p.alpha = 0;
        p.delay = smokeDelay;
        p.isSmoke = true;
        this.particles.push(p);
      }
    }

    // Impact scorch mark at the target
    this.addScorchMark(targetX, targetY, 22);
  }

  _generateFlamePalette(hex) {
    if (!this._flamePaletteCache) this._flamePaletteCache = new Map();
    const cached = this._flamePaletteCache.get(hex);
    if (cached) return cached;

    const clean = hex.replace('#', '');
    const num = parseInt(clean, 16);
    if (isNaN(num)) return ['#fff7cc', '#fef08a', '#fbbf24', '#f97316', '#ff4400', '#ef4444'];
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;

    const toHex = (cr, cg, cb) => {
      const ir = Math.max(0, Math.min(255, Math.round(cr)));
      const ig = Math.max(0, Math.min(255, Math.round(cg)));
      const ib = Math.max(0, Math.min(255, Math.round(cb)));
      return '#' + HEX_LUT[ir] + HEX_LUT[ig] + HEX_LUT[ib];
    };

    const palette = [
      toHex(r * 0.2 + 255 * 0.8, g * 0.2 + 255 * 0.8, b * 0.2 + 255 * 0.8), // White-hot incandescent core
      toHex(r * 0.55 + 255 * 0.45, g * 0.55 + 255 * 0.45, b * 0.55 + 255 * 0.45), // Bright hot flame
      hex,                                                                          // Primary flame hue
      toHex(r * 0.82, g * 0.82, b * 0.82),                                         // Deep flame body
      toHex(r * 0.62, g * 0.62, b * 0.62),                                         // Outer flame tongue
      toHex(r * 0.42, g * 0.42, b * 0.42)                                          // Dark trailing ember
    ];
    this._flamePaletteCache.set(hex, palette);
    return palette;
  }

  triggerNuclearStrike(targetX, targetY, onDetonate) {
    const warhead = {
      isNukeWarhead: true,
      x: targetX,
      y: targetY - 550,
      targetX,
      targetY,
      speed: 480,
      life: NUKE_IMPACT_DELAY,
      maxLife: NUKE_IMPACT_DELAY,
      update: function(dt) {
        this.y += this.speed * dt;
      },
      onComplete: () => {
        // 1. Blinding Arcade White-Out Flash & Searing Radial Flare (Longer duration)
        this.nukeFlash = { duration: 1.35, maxDuration: 1.35, x: targetX, y: targetY };

        // 2. Heavy Apocalyptic Tectonic Screen Shake
        this.addScreenShake(32, 1.6);

        // 3. Multi-Tier Concentric Shockwaves & Cinematic Anamorphic Lens Flares
        this.addShockwave(targetX, targetY, 220, '#ffffff', 0.5);
        this.addShockwave(targetX, targetY, 380, '#fde047', 0.7);
        this.addShockwave(targetX, targetY, 560, '#f97316', 0.9);
        this.addShockwave(targetX, targetY, 780, '#ef4444', 1.1);
        this.addAnamorphicFlare(targetX, targetY, 520, 8.0, 0.35, '#ffedd5');
        this.addAnamorphicFlare(targetX, targetY - 100, 420, 6.0, 0.40, '#f97316');
        this.addRisingEmbers(targetX, targetY, 24, 80);
        this.addHeatShimmer(targetX, targetY - 20, 90, 40, 2.5);

        // 4. Massive 8-Bit Nuclear Mushroom Cloud Fireball & Plasma Column
        this.addExplosion(targetX, targetY, 190, '#ffffff', 90, 30);
        this.addExplosion(targetX, targetY, 260, '#facc15', 80, 26);
        this.addExplosion(targetX, targetY - 45, 220, '#f97316', 70, 24);
        this.addExplosion(targetX, targetY - 100, 280, '#ef4444', 90, 28);
        this.addExplosion(targetX, targetY - 160, 340, '#b91c1c', 100, 32);
        this.addExplosion(targetX, targetY - 220, 380, '#7f1d1d', 110, 35);
        this.addExplosion(targetX, targetY - 270, 420, '#334155', 120, 40);

        // 5. Rising Radioactive Embers (120 drift particles)
        for (let i = 0; i < 120; i++) {
          const angle = Math.random() * Math.PI * 2;
          const spd = 50 + Math.random() * 260;
          const p = this.obtainCleanParticle();
          p.x = targetX + (Math.random() * 80 - 40);
          p.y = targetY + (Math.random() * 40 - 20);
          p.vx = Math.cos(angle) * spd;
          p.vy = -Math.abs(Math.sin(angle) * spd) - 80;
          p.radius = 2 + Math.random() * 5;
          p.color = ['#ffffff', '#fef08a', '#facc15', '#f97316', '#ea580c', '#b91c1c', '#7c2d12'][i % 7];
          p.life = 1.5 + Math.random() * 2.0;
          p.maxLife = 3.5;
          p.alpha = 1.0;
          this.particles.push(p);
        }

        // 6. Long-Lasting Rising Smoke, Fire & Thermal Fallout Emitter (9.5 seconds duration)
        const self = this;
        const nukeEmitter = {
          x: targetX,
          y: targetY,
          duration: 9.5,
          timer: 0,
          update: function(dt) {
            this.duration -= dt;
            this.timer += dt;
            if (this.timer >= 0.045 && this.duration > 0) {
              this.timer = 0;
              const intensity = Math.min(1.0, this.duration / 4.0);

              // Rising billowing smoke clouds (Ash & Slate with smooth gradual fade)
              for (let s = 0; s < 2; s++) {
                const p = self.obtainCleanParticle();
                p.x = targetX + (Math.random() * 60 - 30);
                p.y = targetY + (Math.random() * 20 - 10);
                p.vx = (Math.random() * 25 - 10) + 8; // Wind drift
                p.vy = -35 - Math.random() * 75;
                p.radius = 6 + Math.random() * 12;
                p.color = ['#334155', '#475569', '#64748b', '#94a3b8'][Math.floor(Math.random() * 4)];
                p.life = 2.2 + Math.random() * 1.8;
                p.maxLife = 4.0;
                p.alpha = 0.42 * intensity;
                self.particles.push(p);
              }

              // Rising fire flame tongues & incandescent thermal sparks
              for (let f = 0; f < 2; f++) {
                const p = self.obtainCleanParticle();
                p.x = targetX + (Math.random() * 50 - 25);
                p.y = targetY + (Math.random() * 15 - 7);
                p.vx = (Math.random() * 20 - 10) + 6;
                p.vy = -60 - Math.random() * 100;
                p.radius = 2.5 + Math.random() * 4;
                p.color = ['#ffffff', '#fef08a', '#facc15', '#f97316', '#ea580c', '#b91c1c'][Math.floor(Math.random() * 6)];
                p.life = 0.9 + Math.random() * 1.0;
                p.maxLife = 1.9;
                p.alpha = 0.85 * intensity;
                self.particles.push(p);
              }
            }
          }
        };
        this.specialEffects.push(nukeEmitter);

        // Global annihilation callback
        onDetonate && onDetonate();
      }
    };
    this.specialEffects.push(warhead);
  }

  triggerBioPlasmaStrike(targetX, targetY, onIncision, onWipe, battlefield = null) {
    const BIO_WAVE_DURATION = 0.70;
    const BIO_WAVE_WIPE_R = 1400;

    // Phase 0: Hush starts immediately (0.00 - 0.90s)
    this.bioPlasmaHush = { duration: 0.90, maxDuration: 0.90 };

    const self = this;
    const scratchUnits = [];

    const bioStrike = {
      isBioStrike: true,
      targetX,
      targetY,
      battlefield,
      elapsed: 0,
      phase: 0, // 0: Hush, 1: Null Point, 2: Incision, 3: Wave, 4: Afterglow
      isBioPlasmaColumn: false,
      columnProgress: 0,
      columnY: 0,
      isBioPlasmaWave: false,
      currentWaveR: 0,
      incisionTriggered: false,
      wipeTriggered: false,
      bubbleTimer: 0,
      ionSparkTimer: 0,
      life: 4.2,
      maxLife: 4.2,

      update: function(dt) {
        this.elapsed += dt;

        // Phase 0: Ground Bio-Target Convergence & Atmosphere Ionization (0.00 - 0.90s)
        if (this.phase === 0 && self.canSpawnCosmetic()) {
          this.bubbleTimer += dt;
          if (this.bubbleTimer >= 0.035) {
            this.bubbleTimer = 0;
            const convAngle = Math.random() * Math.PI * 2;
            const convDist = 30 + Math.random() * 55;
            self.pushParticle({
              x: this.targetX + Math.cos(convAngle) * convDist,
              y: this.targetY + Math.sin(convAngle) * convDist * 0.48,
              vx: -Math.cos(convAngle) * (convDist * 1.6),
              vy: -Math.sin(convAngle) * (convDist * 0.8) - 15,
              radius: 1.5 + Math.random() * 2.0,
              color: Math.random() > 0.4 ? '#a3e635' : '#c084fc',
              life: 0.30,
              maxLife: 0.30,
              alpha: 0.85,
              isRound: true
            });
            if (Math.random() < 0.28) {
              self.addLightningArc(this.targetX + (Math.random() * 24 - 12), this.targetY, this.targetX + (Math.random() * 40 - 20), this.targetY - 45 - Math.random() * 35);
            }
          }
        }

        // Phase 0 -> Phase 1: Null Point (0.90s)
        if (this.elapsed >= 0.90 && this.phase === 0) {
          this.phase = 1;
          this.isBioPlasmaColumn = true;
        }

        // Phase 1: Column descent from ceiling down to targetY (0.90s - 1.25s, 0.35s)
        if (this.phase === 1) {
          const colProg = Math.min(1.0, (this.elapsed - 0.90) / 0.35);
          this.columnProgress = colProg;
          this.columnY = this.targetY * colProg;

          // Ionisations-Abreißfunken an der absteigenden gerundeten Strahlspitze
          this.ionSparkTimer += dt;
          if (this.ionSparkTimer >= 0.016 && self.canSpawnCosmetic() && this.columnY > 15) {
            this.ionSparkTimer = 0;
            const r = 17;
            const capH = Math.min(r, this.columnY);
            const cy = this.columnY - capH;
            const angle = Math.random() * Math.PI; // 0 bis PI: nach unten gewölbter Bogen
            const sx = this.targetX + Math.cos(angle) * (r + Math.random() * 4);
            const sy = cy + Math.sin(angle) * capH;
            const side = Math.cos(angle) >= 0 ? 1 : -1;
            self.pushParticle({
              x: sx,
              y: sy,
              vx: side * (12 + Math.random() * 24),
              vy: -60 - Math.random() * 80, // Zieht nach oben / streift atmosphärisch ab
              radius: 1.2 + Math.random() * 1.8,
              color: Math.random() > 0.35 ? '#f5d0fe' : (Math.random() > 0.5 ? '#d946ef' : '#ffffff'),
              life: 0.16 + Math.random() * 0.18,
              maxLife: 0.35,
              alpha: 0.95,
              isRound: true
            });
          }
        }

        // Fortlaufende Ionisationsfunken entlang der Säule während Incision (1.25s - 1.95s)
        if (this.phase === 2 && this.elapsed < 1.95 && self.canSpawnCosmetic()) {
          this.ionSparkTimer += dt;
          if (this.ionSparkTimer >= 0.035) {
            this.ionSparkTimer = 0;
            const side = Math.random() > 0.5 ? 1 : -1;
            self.pushParticle({
              x: this.targetX + side * (17 + Math.random() * 6),
              y: Math.random() * Math.max(10, this.targetY - 20),
              vx: side * (10 + Math.random() * 18),
              vy: -35 - Math.random() * 55,
              radius: 1.0 + Math.random() * 1.5,
              color: Math.random() > 0.4 ? '#f5d0fe' : '#c084fc',
              life: 0.18 + Math.random() * 0.18,
              maxLife: 0.36,
              alpha: 0.85,
              isRound: true
            });
          }
        }

        // Phase 1 -> Phase 2 & 3: Incision & Resonance Wave (1.25s)
        if (this.elapsed >= 1.25 && (this.phase === 1 || this.phase === 0)) {
          this.phase = 2;
          this.isBioPlasmaColumn = true;
          this.columnProgress = 1.0;
          this.columnY = this.targetY;
          this.isBioPlasmaWave = true;

          if (!this.incisionTriggered) {
            this.incisionTriggered = true;
            self.zoomPull = { elapsed: 0, duration: 0.5 };
            self.nukeFlash = { duration: 1.1, maxDuration: 1.1, x: this.targetX, y: this.targetY };
            self.addScreenShake(30, 1.4);
            self.addAnamorphicFlare(this.targetX, this.targetY, 520, 8.0, 0.38, '#f5d0fe');
            self.addAnamorphicFlare(this.targetX, this.targetY - 80, 400, 6.0, 0.30, '#a3e635');
            self.addShockwave(this.targetX, this.targetY, 220, '#f5d0fe', 0.4);
            self.addShockwave(this.targetX, this.targetY, 360, '#a3e635', 0.55);
            self.addShockwave(this.targetX, this.targetY, 520, '#d946ef', 0.7);
            self.addLight(this.targetX, this.targetY, 400, '#d946ef', 0.95);
            self.addLight(this.targetX, this.targetY, 280, '#84cc16', 0.85);
            onIncision && onIncision();
          }
        }

        // Sustained seismic screen rumble during heavy impact and wave propagation (1.25s - 2.2s)
        if (this.phase >= 2 && this.elapsed <= 2.2) {
          self.addScreenShake(7, 0.2);
        }

        // Boiling neon-green raw plasma pool bubble particles & ejecta (1.25s - 2.6s)
        if (this.phase >= 2 && this.elapsed < 2.6) {
          this.bubbleTimer += dt;
          if (this.bubbleTimer >= 0.035 && self.canSpawnCosmetic()) {
            this.bubbleTimer = 0;
            const bAngle = Math.random() * Math.PI * 2;
            const bDist = Math.random() * 65;
            self.pushParticle({
              x: this.targetX + Math.cos(bAngle) * bDist,
              y: this.targetY + Math.sin(bAngle) * bDist * 0.48,
              vx: (Math.random() * 20 - 10),
              vy: -25 - Math.random() * 45,
              radius: 2 + Math.random() * 4.0,
              color: Math.random() > 0.4 ? '#84cc16' : '#a3e635',
              life: 0.45 + Math.random() * 0.45,
              maxLife: 0.9,
              alpha: 0.9
            });
          }
        }

        // Phase 3: Paced, heavy resonance wave expansion (1.25s - 1.95s)
        if (this.isBioPlasmaWave) {
          if (this.elapsed <= BIO_WIPE_AT) {
            const waveT = Math.min(1.0, (this.elapsed - 1.25) / BIO_WAVE_DURATION);
            this.currentWaveR = BIO_WAVE_WIPE_R * waveT;

            // Radial hit test pro tick
            const bf = this.battlefield;
            if (bf && Array.isArray(bf.units) && bf.units.length > 0) {
              scratchUnits.length = 0;
              for (let i = 0; i < bf.units.length; i++) {
                scratchUnits.push(bf.units[i]);
              }
              const curR = this.currentWaveR;
              for (let i = 0; i < scratchUnits.length; i++) {
                const u = scratchUnits[i];
                if (!u || u.isDead) continue;
                const dx = u.x - this.targetX;
                const dy = u.y - this.targetY;
                if (Math.hypot(dx, dy / 0.48) <= curR) {
                  self.bioPlasmaWaveActive = true;
                  u.takeDamage(99999, self, 'Energy', false, false, null, false, { isSuperweapon: true, superType: 'BIO_PLASMA' });
                  self.bioPlasmaWaveActive = false;
                  if (self.canSpawnCosmetic()) {
                    self.addVaporPuff(u.x, u.y, 18, 0.45);
                    self.addHitSparks(u.x, u.y, 'plasma', 6);
                  }
                }
              }
            }
          } else {
            // Post 1.95s: visual wave continues rolling outwards with weight
            this.currentWaveR = BIO_WAVE_WIPE_R + (this.elapsed - BIO_WIPE_AT) * 650;
            if (this.elapsed >= 3.2) {
              this.isBioPlasmaWave = false;
            }
          }
        }

        // Phase 4: Afterglow & guaranteed Nachfang-Wipe at 1.95s
        if (this.elapsed >= BIO_WIPE_AT - 0.0001 && !this.wipeTriggered) {
          this.wipeTriggered = true;
          this.phase = 4;
          this.isBioPlasmaColumn = false;
          self.bioPlasmaAfterglow = { duration: 5.0, maxDuration: 5.0 };

          self.bioPlasmaWaveActive = true;
          onWipe && onWipe();
          self.bioPlasmaWaveActive = false;

          // Lingering ambient spore particles
          if (self.canSpawnCosmetic()) {
            for (let i = 0; i < 40; i++) {
              const spAngle = Math.random() * Math.PI * 2;
              const spDist = Math.random() * 80;
              self.pushParticle({
                x: this.targetX + Math.cos(spAngle) * spDist,
                y: this.targetY + Math.sin(spAngle) * spDist * 0.48,
                vx: (Math.random() * 24 - 12),
                vy: -20 - Math.random() * 35,
                radius: 1.5 + Math.random() * 2.5,
                color: ['#a855f7', '#d946ef', '#c084fc', '#84cc16'][i % 4],
                life: 1.5 + Math.random() * 1.5,
                maxLife: 3.0,
                alpha: 0.8
              });
            }
          }
        }

        // Clean termination at 3.5s
        if (this.elapsed >= 3.5) {
          this.life = 0;
        }
      }
    };

    this.specialEffects.push(bioStrike);
  }

  triggerOrbitalBombardment(targetX, targetY, onComplete) {
    let shots = 0;
    const interval = setInterval(() => {
      if (shots >= 6) {
        clearInterval(interval);
        onComplete && onComplete();
        return;
      }
      const offsetX = targetX + (Math.random() * 120 - 60);
      const offsetY = targetY + (Math.random() * 60 - 30);
      this.addBeam(offsetX, offsetY - 500, offsetX, offsetY, '#ff0033', 6, 0.2);
      this.addExplosion(offsetX, offsetY, 45, '#ff4400', 30, 4);
      shots++;
    }, 140);
  }

  addA9PigeonBombDrop(startX, startY, destX, destY, team = 'western', duration = 0.55, audio = null, triggerImpactFX = false) {
    const engine = this;
    const dir = team === 'western' ? 1 : -1;
    const bombDuration = Math.max(0.20, Number(duration) || 0.55);

    // Mechanical Pylon Decoupling Ejector Gas Puffs (downward separation push)
    if (this.canSpawnCosmetic?.()) {
      for (let i = 0; i < 6; i++) {
        const p = this.obtainCleanParticle();
        p.x = startX + (Math.random() - 0.5) * 6;
        p.y = startY + 2 + (Math.random() - 0.5) * 3;
        p.vx = (Math.random() - 0.5) * 16;
        p.vy = 8 + Math.random() * 14; // Ejector gas vents downward, pushing bomb free!
        p.radius = 1.6 + Math.random() * 1.4;
        p.color = i % 2 === 0 ? '#f8fafc' : (i % 3 === 0 ? '#facc15' : '#94a3b8');
        p.life = 0.20 + Math.random() * 0.12;
        p.maxLife = 0.32;
        p.alpha = 0.85;
        this.pushParticle(p);
      }
    }

    const bombEffect = {
      isA9PigeonBombDrop: true,
      x: startX,
      y: startY,
      startX,
      startY,
      destX,
      destY,
      team,
      dir,
      angle: 0,
      life: bombDuration,
      maxLife: bombDuration,
      trailTimer: 0,
      history: [],
      update(dt) {
        this.life -= dt;
        const prog = Math.max(0, Math.min(1, 1.0 - (this.life / this.maxLife)));

        // Realistic ballistics:
        // Horizontal: Linear uniform drift to ground impact coordinate (no forward catapulting / "rausplumpsen")
        this.x = this.startX + (this.destX - this.startX) * prog;

        // Vertical: Initial pylon ejector velocity + continuous gravity acceleration
        // (0.28 * prog + 0.72 * prog^2) begins moving downward immediately and accelerates into ground
        const gravProg = 0.28 * prog + 0.72 * (prog * prog);
        this.y = this.startY + (this.destY - this.startY) * gravProg;

        // Aerodynamic fin-stabilized pitch dive:
        // Starts horizontal at pylon release (0°), fins catch upward airflow and pitch
        // nose down into steep 86° vertical plunge
        const divePitch = Math.min(1.0, Math.pow(prog, 0.80) * 1.08);
        this.angle = this.dir * (Math.PI * 0.48) * divePitch;

        // Record flight path for smooth condensation ribbon
        if (!this.history) this.history = [];
        this.history.push({ x: this.x, y: this.y });
        if (this.history.length > 14) this.history.shift();

        this.trailTimer += dt;
        if (this.trailTimer >= 0.022 && engine.canSpawnCosmetic?.()) {
          this.trailTimer = 0;
          // Turbulent smoke puff from fin vortices
          const p = engine.obtainCleanParticle();
          p.x = this.x - this.dir * 8;
          p.y = this.y - 2;
          p.vx = -this.dir * 14 + (Math.random() - 0.5) * 8;
          p.vy = -14 - Math.random() * 16;
          p.radius = 2.0 + Math.random() * 1.6;
          p.color = Math.random() > 0.35 ? '#cbd5e1' : '#fde047';
          p.life = 0.28 + Math.random() * 0.16;
          p.maxLife = 0.44;
          p.alpha = 0.85;
          engine.pushParticle(p);
        }
      },
      onComplete() {
        if (triggerImpactFX) {
          engine.addExplosion?.(this.destX, this.destY, 65, '#f97316', 34);
          engine.addShockwave?.(this.destX, this.destY, 78, '#fbbf24', 0.4);
          engine.addScorchMark?.(this.destX, this.destY, 20);
          engine.addArtillerySpall?.(this.destX, this.destY);
          engine.addScreenShake?.(5.5, 0.25);
          if (audio?.playA9PigeonBombExplosion) {
            audio.playA9PigeonBombExplosion({ x: this.destX });
          } else {
            audio?.playExplosion?.('heavy', { x: this.destX });
          }
        }
      }
    };

    this.specialEffects.push(bombEffect);
    return bombEffect;
  }

  addA9PigeonDeathFlock(unit, audio = null) {
    if (!unit) return null;
    const engine = this;
    const x = unit.x;
    const y = (unit.isAir ? unit.y - 18 : unit.y);
    const team = unit.team || 'western';
    const race = unit.race || unit.cardData?.race || 'Human';
    const dir = unit.facingDir || (team === 'western' ? 1 : -1);

    if (audio?.playA9PigeonDeath) {
      audio.playA9PigeonDeath({ x: unit.x });
    } else {
      audio?.playExplosion?.('medium', { x: unit.x });
    }

    // 1. Aircraft chassis explosion, shockwave & voxel shatter
    this.addExplosion(x, y, 44, '#f97316', 26);
    this.addShockwave(x, y, 52, '#38bdf8', 0.32);
    this.addUnitDeathShatter(unit);

    // Ejection canopy glass shards (cyan/ice-blue glass spray from blowing cockpit canopy)
    for (let g = 0; g < 10; g++) {
      const p = this.obtainCleanParticle();
      const gAngle = -Math.PI * 0.5 + (Math.random() - 0.5) * 1.4;
      const gSpeed = 45 + Math.random() * 95;
      p.x = x + dir * 6 + (Math.random() - 0.5) * 8;
      p.y = y - 10 + (Math.random() - 0.5) * 6;
      p.vx = Math.cos(gAngle) * gSpeed;
      p.vy = Math.sin(gAngle) * gSpeed;
      p.gravity = 160;
      p.drag = 0.93;
      p.radius = 1.6 + Math.random() * 2.0;
      p.color = Math.random() > 0.4 ? '#38bdf8' : '#e0f2fe';
      p.life = 0.75 + Math.random() * 0.45;
      p.maxLife = 1.2;
      p.alpha = 0.95;
      this.pushParticle(p);
    }

    // 2. Swirling burst of 24 drifting iridescent pigeon feathers
    const featherPalette = ['#ffffff', '#f8fafc', '#cbd5e1', '#94a3b8', '#10b981', '#a855f7'];
    const featherCount = this.quality === 'low' ? 12 : 24;
    for (let i = 0; i < featherCount; i++) {
      const p = this.obtainCleanParticle();
      const ang = (i / featherCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const spd = 28 + Math.random() * 65;
      p.x = x + (Math.random() - 0.5) * 18;
      p.y = y + (Math.random() - 0.5) * 12;
      p.vx = Math.cos(ang) * spd * 0.7;
      p.vy = Math.sin(ang) * spd * 0.6 - 28;
      p.gravity = 16;
      p.drag = 0.95;
      p.swayAmp = 16 + Math.random() * 12;
      p.swayFreq = 4.2 + Math.random() * 2.2;
      p.swayPhase = Math.random() * Math.PI * 2;
      p.radius = 2.0 + Math.random() * 1.6;
      p.color = featherPalette[i % featherPalette.length];
      p.life = 1.5 + Math.random() * 0.8;
      p.maxLife = 2.4;
      p.initialAlpha = 0.95;
      p.alpha = 0.95;
      this.pushParticle(p);
    }

    // 3. Flock of 6 animated 8-bit Pigeons (Heroic Pilot Pigeon with goggles & fluttering scarf + Wingman + Crew flock)
    const wingmanStartX = Number.isFinite(unit._wingmanX) ? unit._wingmanX : (x - dir * 14);
    const wingmanStartY = Number.isFinite(unit._wingmanY) ? unit._wingmanY : (y - 18);
    const birds = [
      // #0: HEROIC PILOT PIGEON (Ejecting straight up out of the canopy with gold goggles & red scarf!)
      {
        isPilot: true,
        x: x + dir * 6,
        y: y - 8,
        vx: dir * 55,
        vy: -140,
        dir: dir,
        flapPhase: 0.0,
        flapSpeed: 23.0,
        wavePhase: 0.2,
        scale: 1.35
      },
      // #1: THE LOYAL WINGMAN PIGEON (Escorts the Pilot immediately from exact formation spot)
      {
        isWingman: true,
        x: wingmanStartX,
        y: wingmanStartY,
        vx: dir * 35,
        vy: -125,
        dir: dir,
        flapPhase: 1.2,
        flapSpeed: 21.0,
        wavePhase: 1.1,
        scale: 1.18
      },
      // #2: Co-Pilot Pigeon climbing steep left
      {
        x: x - dir * 4,
        y: y - 6,
        vx: -dir * 38,
        vy: -108,
        dir: -dir,
        flapPhase: 2.1,
        flapSpeed: 19.5,
        wavePhase: 2.3,
        scale: 1.05
      },
      // #3: Gunner Pigeon climbing forward-right
      {
        x: x + dir * 10,
        y: y - 4,
        vx: dir * 75,
        vy: -102,
        dir: dir,
        flapPhase: 0.6,
        flapSpeed: 20.0,
        wavePhase: 3.5,
        scale: 1.02
      },
      // #4: Bombardier Pigeon spiraling high center
      {
        x: x,
        y: y - 6,
        vx: dir * 20,
        vy: -130,
        dir: dir,
        flapPhase: 2.8,
        flapSpeed: 22.0,
        wavePhase: 4.8,
        scale: 1.08
      },
      // #5: Flight Engineer Pigeon peeling off high rear
      {
        x: x - dir * 8,
        y: y - 5,
        vx: -dir * 50,
        vy: -115,
        dir: -dir,
        flapPhase: 1.7,
        flapSpeed: 19.0,
        wavePhase: 5.6,
        scale: 0.98
      }
    ];

    const flockDuration = 2.4;
    const flockEffect = {
      isA9PigeonDeathFlock: true,
      x,
      y,
      team,
      race,
      birds,
      life: flockDuration,
      maxLife: flockDuration,
      featherTimer: 0,
      update(dt) {
        const elapsed = this.maxLife - this.life;
        for (let i = 0; i < this.birds.length; i++) {
          const b = this.birds[i];
          b.flapPhase += dt * b.flapSpeed;
          const waveVX = Math.cos(elapsed * 4.2 + b.wavePhase) * 16;
          b.x += (b.vx + waveVX) * dt;
          b.y += b.vy * dt;
          b.vy -= 16 * dt; // Upward thermal lift as they ascend toward the clouds
        }

        // Shedding tiny down feathers along the escape climb
        this.featherTimer += dt;
        if (this.featherTimer >= 0.08 && engine.canSpawnCosmetic?.()) {
          this.featherTimer = 0;
          const b = this.birds[Math.floor(Math.random() * this.birds.length)];
          const p = engine.obtainCleanParticle();
          p.x = b.x;
          p.y = b.y + 4;
          p.vx = (Math.random() - 0.5) * 16;
          p.vy = 8 + Math.random() * 12;
          p.swayAmp = 12;
          p.swayFreq = 5.0;
          p.swayPhase = Math.random() * Math.PI * 2;
          p.radius = 1.6;
          p.color = Math.random() > 0.35 ? '#ffffff' : '#cbd5e1';
          p.life = 0.65;
          p.maxLife = 0.65;
          p.alpha = 0.85;
          engine.pushParticle(p);
        }
      }
    };

    this.specialEffects.push(flockEffect);
    return flockEffect;
  }

  // Ground scorch marks - drawn separately from render() so the caller can
  // paint them onto the battlefield floor before units/particles are drawn.
  renderDecals(ctx) {
    if (this.decals.length === 0) return;
    ctx.save();
    for (const d of this.decals) {
      ctx.globalAlpha = d.alpha;
      ctx.fillStyle = d.color;
      if (d.type === 'shadow_footprint') {
        // Faserige Schattenschleppe auf dem Boden (Anti Shade - organisch & zerfasert, keine Rechtecke)
        const r = d.radius;
        ctx.beginPath();
        ctx.moveTo(d.x - r * 1.5, d.y);
        ctx.lineTo(d.x - r * 0.8, d.y - r * 0.35);
        ctx.lineTo(d.x, d.y - r * 0.15);
        ctx.lineTo(d.x + r * 0.7, d.y - r * 0.30);
        ctx.lineTo(d.x + r * 1.4, d.y);
        ctx.lineTo(d.x + r * 0.6, d.y + r * 0.28);
        ctx.lineTo(d.x - r * 0.2, d.y + r * 0.12);
        ctx.lineTo(d.x - r * 0.9, d.y + r * 0.32);
        ctx.closePath();
        ctx.fill();

        // Feine faserige Saumfäden
        ctx.strokeStyle = d.color;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(d.x - r * 0.4, d.y);
        ctx.lineTo(d.x - r * 1.8, d.y - r * 0.20);
        ctx.moveTo(d.x + r * 0.3, d.y);
        ctx.lineTo(d.x + r * 1.6, d.y + r * 0.22);
        ctx.stroke();
      } else if (d.type === 'carmine_puddle') {
        // Deep carmine liquid puddle: multi-lobed organic pooling with wet specular gloss
        const r = d.radius * (d.spread || 1.0);
        const ang = d.angle || 0;

        // 1. Dark coagulated rim
        ctx.fillStyle = d.rimColor || '#450a0a';
        ctx.beginPath();
        ctx.ellipse(d.x, d.y, r * 1.35, r * 0.52, ang, 0, Math.PI * 2);
        ctx.ellipse(d.x + r * 0.35, d.y + r * 0.12, r * 0.85, r * 0.42, ang + 0.3, 0, Math.PI * 2);
        ctx.ellipse(d.x - r * 0.42, d.y - r * 0.08, r * 0.75, r * 0.38, ang - 0.25, 0, Math.PI * 2);
        ctx.fill();

        // 2. Rich arterial carmine body
        ctx.fillStyle = d.color || '#991b1b';
        ctx.beginPath();
        ctx.ellipse(d.x, d.y, r * 1.15, r * 0.42, ang, 0, Math.PI * 2);
        ctx.ellipse(d.x + r * 0.28, d.y + r * 0.08, r * 0.70, r * 0.32, ang + 0.3, 0, Math.PI * 2);
        ctx.ellipse(d.x - r * 0.32, d.y - r * 0.06, r * 0.60, r * 0.28, ang - 0.25, 0, Math.PI * 2);
        ctx.fill();

        // 3. Wet gloss specular curved shine
        ctx.strokeStyle = 'rgba(255, 230, 230, 0.45)';
        ctx.lineWidth = Math.max(1, r * 0.12);
        ctx.beginPath();
        ctx.ellipse(d.x - r * 0.15, d.y - r * 0.14, r * 0.65, r * 0.18, ang - 0.2, Math.PI * 0.95, Math.PI * 1.85);
        ctx.stroke();
      } else {
        // Retro 8-bit: an irregular blotch built from a few overlapping squares
        // rather than a perfect circle.
        const r = d.radius;
        ctx.fillRect(d.x - r, d.y - r * 0.55, r * 2, r * 1.1);
        ctx.fillRect(d.x - r * 0.55, d.y - r * 0.8, r * 1.1, r * 1.6);
      }
    }
    ctx.restore();
  }

  draw(ctx) {
    this.renderDecals(ctx);
    this.render(ctx);
  }

  render(ctx, quality = 'high', customBounds = null) {
    const isLow = quality === 'low';
    const bounds = customBounds || (typeof window !== 'undefined' ? window.tcgGame?.viewport?.getVisibleBounds?.() : null);

    // 1. Render Beams & Bullet Tracers - Solid continuous lines (Marine Gauss, Lasers, Plasma)
    ctx.save();
    if (typeof ctx.setLineDash === 'function') {
      ctx.setLineDash([]);
    }
    ctx.lineCap = 'round';
    for (const b of this.beams) {
      if (b.isBastionBeam) continue; // Rendered within Bastion Lock's depth layer between body and front shield
      if (bounds) {
        const minX = Math.min(b.x1, b.x2);
        const maxX = Math.max(b.x1, b.x2);
        if (maxX < bounds.left - 40 || minX > bounds.right + 40) continue;
      }
      const alpha = Math.max(0, b.life / b.maxLife);
      if (alpha <= 0) continue;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = b.color;
      ctx.lineWidth = Math.max(1.5, b.width);
      ctx.beginPath();
      ctx.moveTo(b.x1, b.y1);
      ctx.lineTo(b.x2, b.y2);
      ctx.stroke();
    }
    ctx.restore();

    // 2. Render Special Effects
    ctx.save();
    for (const s of this.specialEffects) {
      if (bounds) {
        const effR = s.currentWaveR || s.maxRadius || s.radius || (s.width ? s.width / 2 : 0) || 100;
        const posX = (s.isBioPlasmaColumn && s.targetX !== undefined) ? s.targetX : (s.x !== undefined ? s.x : s.targetX);
        if (posX !== undefined) {
          if (posX + effR < bounds.left - 60 || posX - effR > bounds.right + 60) {
            continue; // Skip offscreen special effects (gradients, shockwaves, drawImage passes)
          }
        }
      }
      if (s.maxRadius !== undefined) {
        // Expanding Multi-Layer Refractive 2.5D Shockwave Ring with dynamic line-width compression
        const alpha = Math.max(0, s.life / s.maxLife);
        const sr = s.radius;
        const progress = 1 - alpha;
        // Compression thickness: starts thick and incandescent (4.8px), thins out at perimeter (1.2px)
        const compressionWidth = Math.max(1.2, (1 - progress * 0.72) * 4.6);
        
        ctx.save();
        // 1. Outer Refraction Compression Ring
        ctx.strokeStyle = s.color || '#00f0ff';
        ctx.globalAlpha = alpha * 0.85;
        ctx.lineWidth = compressionWidth;
        ctx.beginPath();
        ctx.ellipse(s.x, s.y, sr, sr * 0.48, 0, 0, Math.PI * 2);
        ctx.stroke();

        // 2. Inner White-Hot Compression Shock Rim
        ctx.strokeStyle = '#ffffff';
        ctx.globalAlpha = alpha * 0.95;
        ctx.lineWidth = Math.max(1.0, compressionWidth * 0.45);
        ctx.beginPath();
        ctx.ellipse(s.x, s.y, sr * 0.95, sr * 0.45, 0, 0, Math.PI * 2);
        ctx.stroke();

        // 3. Outer Chromatic Aberration Dispersion Halo
        ctx.strokeStyle = 'rgba(255, 0, 90, 0.45)';
        ctx.globalAlpha = alpha * 0.4;
        ctx.lineWidth = Math.max(1.0, compressionWidth * 0.58);
        ctx.beginPath();
        ctx.ellipse(s.x, s.y, sr * 1.05, sr * 0.51, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      } else if (s.isDropInBeam) {
        // 🌟 Celestial Orbital Skybeam from canvas ceiling down to ground contact
        const alpha = Math.max(0, s.life / s.maxLife);
        const groundY = s.groundY;
        const beamW = s.width * (0.35 + 0.65 * Math.sin(alpha * Math.PI));

        ctx.save();
        // 1. Wide ambient golden glow
        ctx.fillStyle = s.outerColor || 'rgba(250, 204, 21, 0.45)';
        ctx.globalAlpha = alpha * 0.65;
        ctx.fillRect(s.x - beamW / 2, 0, beamW, groundY);

        // 2. Focused white-hot core column
        const coreW = Math.max(3, beamW * 0.35);
        ctx.fillStyle = s.coreColor || '#ffffff';
        ctx.globalAlpha = alpha * 0.95;
        ctx.fillRect(s.x - coreW / 2, 0, coreW, groundY);

        // 3. Ground contact impact lens flare pool
        ctx.fillStyle = s.outerColor || 'rgba(250, 204, 21, 0.65)';
        ctx.globalAlpha = alpha * 0.85;
        ctx.beginPath();
        ctx.ellipse(s.x, groundY, beamW * 1.5, beamW * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      } else if (s.isBioPlasmaColumn || s.isBioPlasmaWave) {
        // Sul Bio-Plasma orbital incision beam, billowing impact cauldron & volumetric resonance waves
        ctx.save();
        const tx = s.targetX;
        const ty = s.targetY;

        // 0. Pre-Incision Bio-Target Convergence & Ground Creep Sigil (Phase 0)
        if (s.phase === 0) {
          const prepProg = Math.min(1.0, s.elapsed / 0.90);
          ctx.save();
          const sigilR = 55 - prepProg * 22;
          ctx.strokeStyle = 'rgba(163, 230, 53, ' + (0.45 + prepProg * 0.45) + ')';
          ctx.lineWidth = 2.0;
          ctx.beginPath();
          ctx.ellipse(tx, ty, sigilR, sigilR * 0.48, s.elapsed * 4, 0, Math.PI * 2);
          ctx.stroke();

          ctx.strokeStyle = 'rgba(217, 70, 239, ' + (0.55 + prepProg * 0.4) + ')';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.ellipse(tx, ty, sigilR * 0.65, sigilR * 0.65 * 0.48, -s.elapsed * 6, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }

        // 1. Incision Beam (Cylindrical Amethyst column with incandescent white core & rounded capsule tip)
        if (s.isBioPlasmaColumn && s.columnY > 0) {
          const colW = 34;
          const beamH = s.columnY;
          const r = colW / 2;
          const capH = Math.min(r, beamH);
          const cy = beamH - capH;

          if (this.quality !== 'low') {
            // Broad outer atmospheric corona with rounded capsule tip
            const coronaW = colW + 24;
            const rCorona = coronaW / 2;
            const capHCorona = Math.min(rCorona, beamH);
            const cyCorona = beamH - capHCorona;

            ctx.fillStyle = 'rgba(59, 7, 100, 0.45)';
            ctx.beginPath();
            ctx.moveTo(tx + rCorona, 0);
            if (cyCorona > 0) ctx.lineTo(tx + rCorona, cyCorona);
            ctx.ellipse(tx, cyCorona, rCorona, capHCorona, 0, 0, Math.PI, false);
            ctx.lineTo(tx - rCorona, 0);
            ctx.closePath();
            ctx.fill();

            // Radiant Amethyst body with incandescent core - always the same
            // colW-wide profile, only its x-position (tx) moves per frame, so
            // bake the gradient once in local [0, colW] space and translate
            // instead of calling createLinearGradient() every frame
            // (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md §4).
            ctx.save();
            ctx.translate(tx, 0);
            ctx.fillStyle = getBioPlasmaColumnBeamGrad(ctx, colW);
            ctx.beginPath();
            ctx.moveTo(r, 0);
            if (cy > 0) ctx.lineTo(r, cy);
            ctx.ellipse(0, cy, r, capH, 0, 0, Math.PI, false);
            ctx.lineTo(-r, 0);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          } else {
            ctx.fillStyle = '#d946ef';
            ctx.beginPath();
            ctx.moveTo(tx + r, 0);
            if (cy > 0) ctx.lineTo(tx + r, cy);
            ctx.ellipse(tx, cy, r, capH, 0, 0, Math.PI, false);
            ctx.lineTo(tx - r, 0);
            ctx.closePath();
            ctx.fill();
          }

          // Double Helix Bio-Plasma Filaments winding around the beam
          if (this.quality !== 'low' && beamH > 20) {
            ctx.save();
            const helixSpeed = s.elapsed * 18;
            const helixAmp = r + 5;
            ctx.lineWidth = 2.0;
            ctx.lineCap = 'round';
            // Strand 1 (Fluorescent Lime-Green)
            ctx.strokeStyle = '#a3e635';
            ctx.globalAlpha = 0.85;
            ctx.beginPath();
            for (let hy = 0; hy <= beamH; hy += 12) {
              const hx = tx + Math.sin(hy * 0.055 + helixSpeed) * helixAmp;
              if (hy === 0) ctx.moveTo(hx, hy);
              else ctx.lineTo(hx, hy);
            }
            ctx.stroke();

            // Strand 2 (Fluorescent Amethyst Violet)
            ctx.strokeStyle = '#f5d0fe';
            ctx.globalAlpha = 0.85;
            ctx.beginPath();
            for (let hy = 0; hy <= beamH; hy += 12) {
              const hx = tx - Math.sin(hy * 0.055 + helixSpeed) * helixAmp;
              if (hy === 0) ctx.moveTo(hx, hy);
              else ctx.lineTo(hx, hy);
            }
            ctx.stroke();
            ctx.restore();
          }

          // Sharp cylindrical boundary lines curving smoothly around the rounded bottom cap
          ctx.strokeStyle = '#f5d0fe';
          ctx.lineWidth = 2.0;
          ctx.beginPath();
          ctx.moveTo(tx + r, 0);
          if (cy > 0) ctx.lineTo(tx + r, cy);
          ctx.ellipse(tx, cy, r, capH, 0, 0, Math.PI, false);
          ctx.lineTo(tx - r, 0);
          ctx.stroke();
        }

        // 2. Ground Impact Area — Boiling Cauldron & Billowing Plumes ("Aufbauschen")
        if (s.phase >= 2) {
          const impactAge = Math.max(0, s.elapsed - 1.25);
          // Erupts and swells rapidly in first 0.35s, pulses, then gradually recedes into the glass scar after 2.3s
          const swell = Math.min(1.0, impactAge / 0.35);
          const recede = s.elapsed > 2.3 ? Math.max(0, 1.0 - (s.elapsed - 2.3) / 1.1) : 1.0;
          const plumeFactor = swell * recede;

          if (plumeFactor > 0.01) {
            ctx.save();

            // 2A. Searing Ground Fracture Fissures (Molten rock tearing open)
            if (this.quality !== 'low') {
              ctx.strokeStyle = 'rgba(163, 230, 53, ' + (0.75 * plumeFactor) + ')';
              ctx.lineWidth = 2.5 * plumeFactor;
              ctx.beginPath();
              const fissureAngles = [0.15, 0.42, 0.78, 1.12, 1.48, 1.85, 2.22, 2.58];
              for (let i = 0; i < fissureAngles.length; i++) {
                const fa = fissureAngles[i] * Math.PI;
                const fLen = (55 + ((i * 17) % 30)) * swell;
                ctx.moveTo(tx, ty);
                ctx.lineTo(tx + Math.cos(fa) * fLen, ty + Math.sin(fa) * fLen * 0.48);
              }
              ctx.stroke();
            }

            // 2B. Boiling Ground Basin / Molten Cauldron
            const wobble = Math.sin(s.elapsed * 22) * 5;
            const pw = (85 + wobble) * swell;
            const ph = pw * 0.48;

            if (this.quality !== 'low') {
              const poolCanvas = getPlasmaPoolCanvas();
              if (poolCanvas) {
                ctx.drawImage(poolCanvas, tx - pw, ty - ph, pw * 2, ph * 2);
              } else {
                const poolGrad = ctx.createRadialGradient(tx, ty, 4, tx, ty, pw);
                poolGrad.addColorStop(0, '#ffffff');
                poolGrad.addColorStop(0.25, '#a3e635');
                poolGrad.addColorStop(0.55, '#84cc16');
                poolGrad.addColorStop(0.85, '#4a044e');
                poolGrad.addColorStop(1, 'rgba(59, 7, 100, 0)');
                ctx.fillStyle = poolGrad;
                ctx.beginPath();
                ctx.ellipse(tx, ty, pw, ph, 0, 0, Math.PI * 2);
                ctx.fill();
              }
            } else {
              ctx.fillStyle = '#84cc16';
              ctx.beginPath();
              ctx.ellipse(tx, ty, pw, ph, 0, 0, Math.PI * 2);
              ctx.fill();
            }

            // Searing Cauldron Lip
            ctx.strokeStyle = 'rgba(245, 208, 254, ' + (0.85 * plumeFactor) + ')';
            ctx.lineWidth = 2.0;
            ctx.beginPath();
            ctx.ellipse(tx, ty, pw * 0.95, ph * 0.95, 0, 0, Math.PI * 2);
            ctx.stroke();

            // 2C. Swelling Energy Dome & Billowing Plumes ("Aufbauschen")
            // Surges 90-130px upward along the column base with rolling lobes
            const domeH = (95 + Math.sin(s.elapsed * 14) * 14) * plumeFactor;
            const domeW = (120 + Math.cos(s.elapsed * 11) * 12) * plumeFactor;

            // Multiple overlapping billowing lobes (Mushrooming plasma cloud)
            for (let i = 0; i < BILLOW_PUFF_CONFIGS.length; i++) {
              const b = BILLOW_PUFF_CONFIGS[i];
              const bx = tx + domeW * b.oxFrac;
              const by = ty + domeH * b.oyFrac;
              const br = domeH * b.rFrac;
              if (br <= 0) continue;

              const puffCanvas = getPlasmaPuffCanvas(b.type);
              if (puffCanvas) {
                ctx.drawImage(puffCanvas, bx - br, by - br, br * 2, br * 2);
              } else {
                const puffGrad = ctx.createRadialGradient(bx, by, 2, bx, by, br);
                if (b.c === '#ffffff') {
                  puffGrad.addColorStop(0, '#ffffff');
                  puffGrad.addColorStop(0.4, '#fdf4ff');
                  puffGrad.addColorStop(0.8, 'rgba(217, 70, 239, 0.7)');
                  puffGrad.addColorStop(1, 'rgba(59, 7, 100, 0)');
                } else if (b.c === '#84cc16') {
                  puffGrad.addColorStop(0, '#bef264');
                  puffGrad.addColorStop(0.5, '#84cc16');
                  puffGrad.addColorStop(0.8, '#365314');
                  puffGrad.addColorStop(1, 'rgba(54, 83, 20, 0)');
                } else {
                  puffGrad.addColorStop(0, '#f5d0fe');
                  puffGrad.addColorStop(0.4, '#d946ef');
                  puffGrad.addColorStop(0.75, '#581c87');
                  puffGrad.addColorStop(1, 'rgba(59, 7, 100, 0)');
                }
                ctx.fillStyle = puffGrad;
                ctx.beginPath();
                ctx.arc(bx, by, br, 0, Math.PI * 2);
                ctx.fill();
              }
            }

            // 2D. Arcing Plasma Geysers / Tendrils leaping outward
            if (s.elapsed < 2.3 && this.quality !== 'low') {
              ctx.strokeStyle = '#a3e635';
              ctx.lineWidth = 2.0;
              ctx.beginPath();
              // Left arc
              ctx.moveTo(tx, ty - 15);
              ctx.quadraticCurveTo(tx - 65 * swell, ty - domeH * 0.8, tx - 95 * swell, ty + 5);
              // Right arc
              ctx.moveTo(tx, ty - 15);
              ctx.quadraticCurveTo(tx + 65 * swell, ty - domeH * 0.8, tx + 95 * swell, ty + 5);
              ctx.stroke();
            }

            ctx.restore();
          }
        }

        // 3. Expanding Concentric Resonance Waves ("Langsamer und Wuchtiger")
        if (s.isBioPlasmaWave && s.currentWaveR > 0) {
          const wr = s.currentWaveR;
          const waveAlpha = Math.max(0, 1.0 - (wr / 2200));

          if (waveAlpha > 0.01) {
            ctx.save();

            // 3A. Atmospheric Ionization Wake (Glowing superheated area behind the shockwave)
            if (this.quality !== 'low') {
              ctx.save();
              ctx.translate(tx, ty);
              ctx.scale(wr, wr * 0.48);
              ctx.globalAlpha *= waveAlpha;
              ctx.fillStyle = getBioPlasmaWakeGrad(ctx);
              ctx.beginPath();
              ctx.arc(0, 0, 1, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }

            // 3B. Front 1: Massive Primary Volumetric Shockwave
            const bandW = Math.min(42, 18 + wr * 0.025);
            // Outer compression glow
            ctx.strokeStyle = 'rgba(168, 85, 247, ' + (0.35 * waveAlpha) + ')';
            ctx.lineWidth = bandW + 14;
            ctx.beginPath();
            ctx.ellipse(tx, ty, wr, wr * 0.48, 0, 0, Math.PI * 2);
            ctx.stroke();

            // Primary dense amethyst blast body
            ctx.strokeStyle = 'rgba(217, 70, 239, ' + (0.75 * waveAlpha) + ')';
            ctx.lineWidth = bandW;
            ctx.beginPath();
            ctx.ellipse(tx, ty, wr, wr * 0.48, 0, 0, Math.PI * 2);
            ctx.stroke();

            // Neon-lime bio-resonance inner core of the shockfront
            ctx.strokeStyle = 'rgba(163, 230, 53, ' + (0.85 * waveAlpha) + ')';
            ctx.lineWidth = bandW * 0.45;
            ctx.beginPath();
            ctx.ellipse(tx, ty, wr * 0.98, wr * 0.98 * 0.48, 0, 0, Math.PI * 2);
            ctx.stroke();

            // High-intensity razor-sharp leading crest
            ctx.strokeStyle = '#ffffff';
            ctx.globalAlpha = waveAlpha * 0.95;
            ctx.lineWidth = 3.5;
            ctx.beginPath();
            ctx.ellipse(tx, ty, wr, wr * 0.48, 0, 0, Math.PI * 2);
            ctx.stroke();

            // Ground displacement ticks along the leading edge
            if (this.quality !== 'low' && wr < 1600) {
              ctx.strokeStyle = 'rgba(245, 208, 254, ' + (0.8 * waveAlpha) + ')';
              ctx.lineWidth = 2.0;
              ctx.beginPath();
              const tickCount = 14;
              for (let i = 0; i < tickCount; i++) {
                const angle = (i / tickCount) * Math.PI * 2;
                const cosA = Math.cos(angle);
                const sinA = Math.sin(angle);
                const rIn = wr - 12;
                const rOut = wr + 14;
                ctx.moveTo(tx + cosA * rIn, ty + sinA * rIn * 0.48);
                ctx.lineTo(tx + cosA * rOut, ty + sinA * rOut * 0.48);
              }
              ctx.stroke();
            }

            // 3C. Front 2: Secondary Harmonic Shockwave (Follows ~130px behind)
            const r2 = Math.max(0, wr - 130);
            if (r2 > 0) {
              const alpha2 = waveAlpha * 0.75;
              ctx.strokeStyle = 'rgba(163, 230, 53, ' + (0.6 * alpha2) + ')';
              ctx.lineWidth = 20;
              ctx.beginPath();
              ctx.ellipse(tx, ty, r2, r2 * 0.48, 0, 0, Math.PI * 2);
              ctx.stroke();

              ctx.strokeStyle = '#c084fc';
              ctx.lineWidth = 2.5;
              ctx.beginPath();
              ctx.ellipse(tx, ty, r2, r2 * 0.48, 0, 0, Math.PI * 2);
              ctx.stroke();
            }

            // 3D. Front 3: Tertiary Seismic Reverberation (Follows ~260px behind)
            const r3 = Math.max(0, wr - 260);
            if (r3 > 0) {
              const alpha3 = waveAlpha * 0.5;
              ctx.strokeStyle = 'rgba(168, 85, 247, ' + (0.45 * alpha3) + ')';
              ctx.lineWidth = 12;
              ctx.beginPath();
              ctx.ellipse(tx, ty, r3, r3 * 0.48, 0, 0, Math.PI * 2);
              ctx.stroke();

              ctx.strokeStyle = '#fdf4ff';
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              ctx.ellipse(tx, ty, r3, r3 * 0.48, 0, 0, Math.PI * 2);
              ctx.stroke();
            }

            ctx.restore();
          }
        }

        ctx.restore();
      } else if (s.isCurvedMissile) {
        // 🚀 Curved Micro-Missile (with nose cone, fins, and glowing booster)
        this._renderTaperedRibbonTrail(ctx, s.trailHistory, s.trailColor || s.color || '#f97316', 3.2);
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);

        // Ground shadow beneath missile
        ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
        ctx.fillRect(-3, 8, 6, 2);

        // Missile body casing
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(-4, -1.5, 7, 3);
        ctx.fillStyle = '#ffffff'; // Top highlight
        ctx.fillRect(-4, -1.5, 7, 1);
        
        // Stabilizer tail fins
        ctx.fillStyle = '#334155';
        ctx.fillRect(-4, -2.5, 2, 5);

        // Aerodynamic Warhead tip (+X)
        ctx.fillStyle = s.color || '#ef4444';
        ctx.fillRect(3, -1, 2, 2);
        ctx.fillStyle = s.color ? '#ffffff' : '#facc15';
        ctx.fillRect(4.5, -0.5, 1, 1);

        // Booster flame exhaust (-X)
        ctx.fillStyle = s.trailColor || s.color || '#f97316';
        ctx.fillRect(-5.5, -0.8, 1.5, 1.6);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-5.0, -0.4, 1, 0.8);

        ctx.restore();
      } else if (s.isOrchestraRocket) {
        // 🚀 Heavy Ballistic Artillery Rocket (Orchestra)
        this._renderTaperedRibbonTrail(ctx, s.trailHistory, s.trailColor || '#f97316', 4.6);
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);

        // Ground shadow beneath missile
        ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
        ctx.fillRect(-5, 12, 10, 2.5);

        // Heavy Rocket Body (Longer fuselage, 12px x 4px)
        ctx.fillStyle = '#64748b';
        ctx.fillRect(-6, -2, 11, 4);
        ctx.fillStyle = '#f1f5f9'; // Top metallic reflection
        ctx.fillRect(-6, -2, 11, 1.2);
        ctx.fillStyle = '#334155'; // Bottom shadow line
        ctx.fillRect(-6, 1, 11, 1);

        // 4 Dark Stabilizer Fins at tail (-X)
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(-6, -3.5, 3, 7);

        // Warhead Cone (+X)
        ctx.fillStyle = s.color || '#ef4444';
        ctx.fillRect(5, -1.5, 3, 3);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(8, -0.5, 1.5, 1);

        // Rocket Booster Thruster Nozzle & Flame (-X)
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(-7.5, -1.5, 1.5, 3);
        ctx.fillStyle = s.trailColor || s.color || '#ff9100';
        ctx.fillRect(-10.5, -1, 3, 2);
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(-9.0, -0.5, 1.5, 1);

        ctx.restore();
      } else if (s.isVultureBioMissile) {
        // 🩸 Sul Enforcer Vulture Bio-Rocket Ball (90° Vertical Rise + Homing Dive)
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);

        // Bioluminescent corona halo
        ctx.fillStyle = 'rgba(255, 0, 50, 0.42)';
        ctx.beginPath();
        ctx.arc(0, 0, 7.0, 0, Math.PI * 2);
        ctx.fill();

        // 3D Spherical Bio-Ball (radial specular gradient)
        const rocketColor = s.color || '#ff0033';
        const ballCanvas = getVultureBioRocketCanvas(rocketColor);
        if (ballCanvas) {
          ctx.drawImage(ballCanvas, -8, -8, 16, 16);
        } else {
          const radGrad = ctx.createRadialGradient(-1.0, -1.0, 0.5, 0, 0, 4.4);
          radGrad.addColorStop(0.0, '#ffffff');
          radGrad.addColorStop(0.25, '#ff4d6d');
          radGrad.addColorStop(0.70, rocketColor);
          radGrad.addColorStop(1.0, '#4c0519');

          ctx.fillStyle = radGrad;
          ctx.beginPath();
          ctx.arc(0, 0, 4.4, 0, Math.PI * 2);
          ctx.fill();
        }

        // Dynamic bio-thruster nozzle & flame (-X)
        const flamePulse = 0.8 + 0.3 * Math.sin((s.life || 0) * 40);
        ctx.fillStyle = '#ff4d6d';
        ctx.fillRect(-7.0 * flamePulse, -1.5, 3.2 * flamePulse, 3.0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-5.2 * flamePulse, -0.8, 2.0 * flamePulse, 1.6);

        ctx.restore();
      } else if (s.isExplosiveCrow) {
        // 🦅 8-Bit Cyber-Shadow Explosive Crow (Natural Bird Animation & Multi-State Wings)
        
        // 1. Dynamic ground shadow at target ground level
        const altitude = Math.max(0, (s.shadowY || s.y + 20) - s.y);
        const shadowScale = Math.max(0.4, 1.0 - (altitude / 80));
        const shadowAlpha = Math.max(0.08, 0.32 * shadowScale);
        ctx.save();
        ctx.fillStyle = '#000000';
        ctx.globalAlpha = shadowAlpha;
        ctx.beginPath();
        ctx.ellipse(s.x, s.shadowY || (s.y + 16), 8 * shadowScale, 3 * shadowScale, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // 2. Crow Sprite Body & Animated Wings
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);

        if (s.isDiving) {
          // 🔻 DIVE BOMB STATE: Streamlined Aerodynamic Arrow Silhouette (Wings Tucked Back)
          // Tucked wing casing (-X)
          ctx.fillStyle = '#020617';
          ctx.fillRect(-6, -2, 5, 4);
          ctx.fillStyle = '#ef4444'; // Glowing wingtip streaks
          ctx.fillRect(-7, -2.5, 3, 1.2);
          ctx.fillRect(-7, 1.3, 3, 1.2);

          // Central Body & Tail
          ctx.fillStyle = '#090d16';
          ctx.fillRect(-3, -2.5, 7, 5);

          // Head & Fierce Red Optic Lenses
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(4, -2, 3, 4);
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(5, -1.2, 2, 2);
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(6, -0.8, 1, 1);

          // Razor Beak (+X)
          ctx.fillStyle = '#f59e0b';
          ctx.fillRect(7, -0.6, 2.5, 1.2);

          // Explosive Core Friction Glow
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(0, -1, 3, 2);
          ctx.fillStyle = '#ffea00';
          ctx.fillRect(1, -0.5, 1.5, 1);

        } else if (s.isGliding) {
          // 🦅 SOARING GLIDE STATE: Wide Outstretched Flat Wings (Thermal Riding & Circling)
          // Central Body & Tail Feathers
          ctx.fillStyle = '#020617';
          ctx.fillRect(-6, -1.5, 4, 3); // Fan tail
          ctx.fillStyle = '#090d16';
          ctx.fillRect(-3, -2.5, 6, 5); // Torso

          // Head & Optic Visor
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(3, -2, 3, 4);
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(4, -1.2, 1.5, 1.5);
          // Sharp Beak
          ctx.fillStyle = '#f59e0b';
          ctx.fillRect(6, -0.6, 2, 1.2);

          // Flat Outstretched Upper Wing (Soaring)
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(-3, -8, 5, 6);
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(-2, -10, 4, 3);
          ctx.fillStyle = '#ef4444'; // Crimson Primary Feather Tips
          ctx.fillRect(-1, -11, 3, 2);

          // Flat Outstretched Lower Wing (Soaring)
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(-3, 2, 5, 6);
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(-2, 7, 4, 3);
          ctx.fillStyle = '#dc2626'; // Crimson Primary Feather Tips
          ctx.fillRect(-1, 9, 3, 2);

          // Glowing Nano-Core
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(0, -1, 2, 2);

        } else {
          // 🕊️ FLAPPING FLIGHT STATE: Dynamic Wing Flap-Cycle (Pushing Air & Climbing)
          const wingFlap = Math.sin((s.wingPhase || 0) * 20);

          // Central Body & Tail
          ctx.fillStyle = '#020617';
          ctx.fillRect(-5, -2, 4, 4); // Tail
          ctx.fillStyle = '#090d16';
          ctx.fillRect(-2, -2.5, 6, 5); // Torso

          // Head & Optic Visor
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(3, -2, 3, 4);
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(4, -1.2, 1.5, 1.5);
          // Sharp Beak
          ctx.fillStyle = '#f59e0b';
          ctx.fillRect(6, -0.6, 2, 1.2);

          // Dynamic Wing Articulation
          const wingYOffset = wingFlap * 5.0;
          // Upper Wing
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(-2, -5.5 - wingYOffset, 4.5, 3.5);
          ctx.fillStyle = '#ef4444';
          ctx.fillRect(-1, -7.5 - wingYOffset, 3.5, 2.5);

          // Lower Wing
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(-2, 2.0 + wingYOffset, 4.5, 3.5);
          ctx.fillStyle = '#dc2626';
          ctx.fillRect(-1, 4.5 + wingYOffset, 3.5, 2.5);

          // Explosive Core Micro-Glow
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(0, -1, 2, 2);
        }

        ctx.restore();
      } else if (s.isChitinShard) {
        // Jagged spinning chitin shard projectile (Sul Rejected Loaf reflection)
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rotation || 0);

        ctx.fillStyle = s.color || '#c084fc';
        ctx.beginPath();
        ctx.moveTo(8, 0);
        ctx.lineTo(-5, -4);
        ctx.lineTo(-2, 0);
        ctx.lineTo(-5, 4);
        ctx.closePath();
        ctx.fill();

        // Glowing outer edge
        ctx.strokeStyle = '#f5d0fe';
        ctx.lineWidth = 1.2;
        ctx.stroke();

        ctx.restore();
      } else if (s.isParasiteLeap) {
        // 🔴 Leaping Meatball Parasite with drop shadow and trailing squirming filaments
        // 1. Soft ground drop shadow
        if (s.groundY) {
          ctx.save();
          const shadowScale = Math.max(0.3, 1.0 - (s.elevation || 0) / 70);
          ctx.fillStyle = `rgba(0, 0, 0, ${0.28 * shadowScale})`;
          ctx.beginPath();
          ctx.ellipse(s.x, s.groundY, 9 * shadowScale, 4.5 * shadowScale, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        // 2. Leaping Parasite Body
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);

        // Core Meatball Body (Pulsating visceral flesh mass)
        ctx.fillStyle = '#7f1d1d';
        ctx.beginPath();
        ctx.ellipse(0, 0, 8, 6, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.ellipse(1, 0, 6.5, 4.5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Inner glowing veins / muscle ridges
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.ellipse(2, -1, 3.5, 2.5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Glossy visceral highlight / ocular glint
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(2, -2, 2, 2);

        // Animated squirming whip filaments trailing behind (-X)
        const squirmTime = s.life * 32;
        const w1 = Math.sin(squirmTime) * 4;
        const w2 = Math.cos(squirmTime * 1.3) * 4;
        const w3 = Math.sin(squirmTime * 0.8 + 1.5) * 3;

        ctx.strokeStyle = '#991b1b';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(-6, -2);
        ctx.quadraticCurveTo(-11, -5 + w1, -16, -2 + w1 * 1.5);
        ctx.moveTo(-6, 0);
        ctx.quadraticCurveTo(-12, 0 + w2, -18, 1 + w2 * 1.5);
        ctx.moveTo(-6, 2);
        ctx.quadraticCurveTo(-10, 5 + w3, -15, 4 + w3 * 1.5);
        ctx.stroke();

        ctx.restore();
      } else if (s.target !== undefined) {
        // 8-Bit Projectile rendering (Missiles, Plasma, Shells, Acid)
        const sz = s.size || 5;
        ctx.fillStyle = s.color;
        ctx.fillRect(s.x - sz, s.y - sz, sz * 2, sz * 2);
        // Bright energetic core
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(s.x - sz * 0.4, s.y - sz * 0.4, sz * 0.8, sz * 0.8);
      } else if (s.isBallisticShell) {
        if (s.isSulEnergyOrb || s.color === '#d946ef') {
          // 🔮 Sul Bio-Plasma Energy Sphere (Energiekugel)
          // 1. Subtle ground drop shadow
          const shadowAlpha = 0.28 * (1.0 - (s.elevation / (s.arcHeight * 2.0)));
          ctx.fillStyle = '#0a0314';
          ctx.globalAlpha = Math.max(0.06, shadowAlpha);
          ctx.beginPath();
          ctx.ellipse(s.x, s.y, 6, 2.5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;

          // 2. Flying Glowing Plasma Sphere at (s.x, s.drawY)
          const orbR = 5.5 + Math.sin(s.age * 25) * 0.8;
          // Outer plasma aura gradient
          const auraR = orbR * 2.2;
          const orbTex = getSulEnergyOrbCanvas();
          if (orbTex) {
            ctx.drawImage(orbTex, s.x - auraR, s.drawY - auraR, auraR * 2, auraR * 2);
          }

          // Dense plasma mantle
          ctx.fillStyle = '#c026d3';
          ctx.beginPath();
          ctx.arc(s.x, s.drawY, orbR, 0, Math.PI * 2);
          ctx.fill();

          // Intense energetic white/gold core
          ctx.fillStyle = '#fef08a';
          ctx.beginPath();
          ctx.arc(s.x, s.drawY, orbR * 0.55, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(s.x, s.drawY, orbR * 0.28, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // 🚀 120mm Sleek Compact Artillery Projectile (liegt horizontal in der Luft)
          // 1. Subtle ground drop shadow
          const shadowAlpha = 0.25 * (1.0 - (s.elevation / (s.arcHeight * 2.0)));
          ctx.fillStyle = '#000000';
          ctx.globalAlpha = Math.max(0.05, shadowAlpha);
          ctx.beginPath();
          ctx.ellipse(s.x, s.y, 4, 1.5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;

          // 2. Compact Horizontal Artillery Shell Body at (s.x, s.drawY)
          const shellAngle = s.angle !== undefined ? s.angle : (s.targetX >= s.startX ? 0 : Math.PI);
          
          ctx.save();
          ctx.translate(s.x, s.drawY);
          ctx.rotate(shellAngle);

          // Cylindrical steel casing (compact 8-bit bullet, length: 8px, height: 3px)
          ctx.fillStyle = '#475569';
          ctx.fillRect(-3.5, -1.2, 5.5, 2.4);
          ctx.fillStyle = '#94a3b8'; // Top metallic highlight
          ctx.fillRect(-3, -1.2, 4.5, 0.8);
          ctx.fillStyle = '#1e293b'; // Bottom shadow bevel
          ctx.fillRect(-3, 0.4, 4.5, 0.8);

          // Brass driving band
          ctx.fillStyle = s.color || '#f59e0b';
          ctx.fillRect(-1.5, -1.2, 1.2, 2.4);

          // Sharp aerodynamic bullet tip (+X forward)
          ctx.fillStyle = s.color || '#ef4444'; // Warhead accent
          ctx.fillRect(2, -0.8, 1.5, 1.6);
          ctx.fillStyle = s.color ? '#ffffff' : '#fef08a'; // Tungsten tip point
          ctx.fillRect(3.5, -0.4, 1.2, 0.8);

          // Mach tracer base flame at rear (-X)
          ctx.fillStyle = s.color || '#f97316';
          ctx.fillRect(-4.5, -0.8, 1.2, 1.6);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(-4.0, -0.4, 0.8, 0.8);

          ctx.restore();
        }
      } else if (s.isSporeLob) {
        // 🍄 Sul Spore Mortar Lob — Grotesque, Clumpy, Organic Spore Cluster ("klumpig & sporig")
        // Uniform size knob (e.g. Sentinal Lock's addSporeLob(..., {sizeScale: 0.5})
        // for a half-size bite instead of Apostate's full mortar clump) -
        // applied to the shadow/mote-cloud world-space sizes here, and via
        // ctx.scale() around the clump-body layers below.
        const lobScale = s.sizeScale || 1;
        // 1. Multi-nodal asymmetrical drop shadow on the ground (no smooth circle)
        const heightFactor = Math.min(1.0, s.elevation / Math.max(1, s.arcHeight));
        const shadowAlpha = 0.32 * (1.0 - heightFactor * 0.65);
        const baseW = Math.max(2.2, 5.5 - heightFactor * 2.2) * lobScale;

        ctx.fillStyle = '#051b03';
        ctx.globalAlpha = Math.max(0.05, shadowAlpha);
        ctx.beginPath();
        ctx.ellipse(s.x, s.y, baseW, baseW * 0.38, 0, 0, Math.PI * 2);
        ctx.fill();
        if (!isLow) {
          ctx.beginPath();
          ctx.ellipse(s.x + 2.5, s.y + 0.8, baseW * 0.7, baseW * 0.28, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.ellipse(s.x - 2.2, s.y - 0.5, baseW * 0.65, baseW * 0.25, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // 2. Surrounding Powdery Spore Aerosol / Mote Cloud (Drifting closely around the clump)
        if (!isLow && !s.noSmoke) {
          for (let m = 0; m < 7; m++) {
            const mAngle = (s.age * 6.0 + m * 0.92) % (Math.PI * 2);
            const mDist = (6.0 + Math.sin(s.age * 11 + m * 1.8) * 2.5) * lobScale;
            const mx = s.x + Math.cos(mAngle) * mDist;
            const my = s.drawY + Math.sin(mAngle) * (mDist * 0.75);
            const motePalette = s.palette?.trail || ['#bef264', '#00f0ff', '#a855f7'];
            ctx.fillStyle = motePalette[m % motePalette.length];
            ctx.globalAlpha = 0.6 + Math.sin(s.age * 15 + m) * 0.3;
            ctx.fillRect(mx - 0.75 * lobScale, my - 0.75 * lobScale, 1.5 * lobScale, 1.5 * lobScale);
          }
          ctx.globalAlpha = 1.0;
        }

        // 3. Fungal Clump Body — 5 Asymmetrical, Quivering Bio-Nodules + Spore Filaments
        ctx.save();
        ctx.translate(s.x, s.drawY);
        ctx.rotate(s.rotAngle || 0);
        ctx.scale(lobScale, lobScale);

        // 5 interconnected organic lobes/nodules with irregular offsets and breathing frequencies
        const n0_r = 3.8 + Math.sin(s.age * 15) * 0.45;         // Center node
        const n1_r = 3.0 + Math.sin(s.age * 19 + 1.2) * 0.5;   // Forward-top lobe
        const n2_r = 3.4 + Math.sin(s.age * 17 + 2.6) * 0.45;  // Rear-bottom lobe
        const n3_r = 2.4 + Math.sin(s.age * 22 + 3.8) * 0.4;   // Top-rear node
        const n4_r = 2.7 + Math.sin(s.age * 18 + 4.9) * 0.45;  // Forward-bottom lobe

        // Trailing fungal filaments (viscous tendrils waving behind the cluster)
        if (!isLow) {
          ctx.strokeStyle = '#4d7c0f';
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(-2.5, 0.5);
          ctx.quadraticCurveTo(-6.0, Math.sin(s.age * 20) * 3.0, -9.0, Math.sin(s.age * 25) * 2.0);
          ctx.stroke();

          ctx.strokeStyle = '#65a30d';
          ctx.lineWidth = 1.0;
          ctx.beginPath();
          ctx.moveTo(-1.8, -2.0);
          ctx.quadraticCurveTo(-5.0, -4.0 + Math.sin(s.age * 22) * 2.5, -8.0, -3.5);
          ctx.stroke();

          // Tip droplets on tendrils
          ctx.fillStyle = '#84cc16';
          ctx.fillRect(-9.5, Math.sin(s.age * 25) * 2.0 - 0.8, 1.6, 1.6);
          ctx.fillRect(-8.5, -4.2, 1.4, 1.4);
        }

        // Layer A: Dark rotting fungal crust (Forms irregular outer silhouette)
        ctx.fillStyle = '#1c2d09';
        ctx.beginPath();
        ctx.arc(0, 0, n0_r + 0.8, 0, Math.PI * 2);
        ctx.arc(2.8, -2.0, n1_r + 0.8, 0, Math.PI * 2);
        ctx.arc(-2.6, 1.8, n2_r + 0.8, 0, Math.PI * 2);
        ctx.arc(-1.8, -2.2, n3_r + 0.7, 0, Math.PI * 2);
        ctx.arc(2.4, 2.2, n4_r + 0.7, 0, Math.PI * 2);
        ctx.fill();

        // Layer B: Dense mossy bio-pulp
        ctx.fillStyle = '#4d7c0f';
        ctx.beginPath();
        ctx.arc(0, 0, n0_r, 0, Math.PI * 2);
        ctx.arc(2.8, -2.0, n1_r, 0, Math.PI * 2);
        ctx.arc(-2.6, 1.8, n2_r, 0, Math.PI * 2);
        ctx.arc(-1.8, -2.2, n3_r, 0, Math.PI * 2);
        ctx.arc(2.4, 2.2, n4_r, 0, Math.PI * 2);
        ctx.fill();

        // Layer C: Toxic active spore mantle (Lime green highlights on individual lobes)
        ctx.fillStyle = s.color || '#84cc16';
        ctx.beginPath();
        ctx.arc(0.2, -0.2, n0_r * 0.7, 0, Math.PI * 2);
        ctx.arc(3.0, -2.2, n1_r * 0.72, 0, Math.PI * 2);
        ctx.arc(-2.4, 1.6, n2_r * 0.68, 0, Math.PI * 2);
        ctx.arc(-1.6, -2.3, n3_r * 0.65, 0, Math.PI * 2);
        ctx.arc(2.5, 2.0, n4_r * 0.7, 0, Math.PI * 2);
        ctx.fill();

        // Layer D: Sunken Spore Pores (Dark pitting on the surface of the lobes)
        ctx.fillStyle = '#142006';
        ctx.fillRect(-1.0, 0.4, 1.2, 1.2);
        ctx.fillRect(1.8, -1.0, 1.2, 1.2);
        ctx.fillRect(-2.8, 0.8, 1.0, 1.0);
        ctx.fillRect(1.5, 2.2, 1.0, 1.0);

        // Layer E: Bioluminescent Sul Spore Pustules & Glistening Cysts
        // Cyan cyst on leading lobe
        ctx.fillStyle = '#00f0ff';
        ctx.fillRect(3.6, -2.6, 1.6, 1.6);
        // Purple hymenium vesicle on rear lobe
        ctx.fillStyle = '#c084fc';
        ctx.fillRect(-3.6, 2.2, 1.5, 1.5);
        // Chartreuse spore pustule on bottom node
        ctx.fillStyle = '#bef264';
        ctx.fillRect(2.8, 2.8, 1.5, 1.5);
        // Small amber spore bead on top-rear
        ctx.fillStyle = '#fde047';
        ctx.fillRect(-2.2, -2.8, 1.3, 1.3);

        // Layer F: Luminous Core Glow (Hot glowing nuclear spore center)
        ctx.fillStyle = '#bef264';
        ctx.beginPath();
        ctx.arc(0.2, -0.2, 1.6, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(0.2, -0.2, 0.8, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      } else if (s.isPlasmaSphere) {
        // 🔮 Processing Pillar Slow Homing Plasma Sphere (Ground Attack, die große Plasmakugel)
        const alpha = Math.max(0.2, Math.min(1.0, s.life / (s.maxLife * 0.3 || 0.3)));
        ctx.save();
        ctx.translate(s.x, s.y);

        // Layer 1: Soft Outer Corona Glow (Violet/Teal, quality-gated)
        if (this.quality !== 'low') {
          ctx.fillStyle = '#c084fc';
          ctx.globalAlpha = 0.28 * alpha;
          ctx.beginPath();
          ctx.arc(0, 0, 22.0, 0, Math.PI * 2);
          ctx.fill();
        }

        // Layer 2: Swirling Bio-Plasma Bubble Mantle (Die große Kugel: 13.5)
        ctx.fillStyle = '#38bdf8';
        ctx.globalAlpha = 0.85 * alpha;
        ctx.beginPath();
        ctx.arc(0, 0, 13.5, 0, Math.PI * 2);
        ctx.fill();

        // Layer 3: Inner Radiant Core
        ctx.fillStyle = '#bae6fd';
        ctx.globalAlpha = 0.90 * alpha;
        ctx.beginPath();
        ctx.arc(0, 0, 7.8, 0, Math.PI * 2);
        ctx.fill();

        // Layer 4: Brilliant White Center
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.98 * alpha;
        ctx.beginPath();
        ctx.arc(0, 0, 5.0, 0, Math.PI * 2);
        ctx.fill();

        // Layer 5: Specular Bubble Glint (characteristic organic bubble highlight)
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.85 * alpha;
        ctx.beginPath();
        ctx.arc(-4.0, -4.0, 2.6, 0, Math.PI * 2);
        ctx.fill();

        // Layer 6: Soft iridescent bubble rim
        ctx.strokeStyle = '#e0f2fe';
        ctx.lineWidth = 1.3;
        ctx.globalAlpha = 0.70 * alpha;
        ctx.beginPath();
        ctx.arc(0, 0, 13.5, 0, Math.PI * 2);
        ctx.stroke();

        ctx.restore();
      } else if (s.isElasticPlasmaSpear) {
        // ⚡ Processing Pillar Elastic Piercing Plasma Spear (Air Attack)
        // Die Blase „reißt“ nicht, sondern schnellt wie ein hochelastisches Projektil nach vorne
        // und hinterlässt einen glühenden Plasmastrom (Trail) in der Luft.
        const trailAlpha = Math.max(0.0, Math.min(1.0, s.life / (s.maxLife - s.travelTime || 0.30)));
        ctx.save();

        // Lingering glowing plasma trail connecting origin to tip
        ctx.beginPath();
        ctx.moveTo(s.startX, s.startY);
        ctx.lineTo(s.x, s.y);

        // Outer glow beam (quality-gated)
        if (this.quality !== 'low') {
          ctx.strokeStyle = '#c084fc';
          ctx.lineWidth = 5.0;
          ctx.globalAlpha = 0.35 * trailAlpha;
          ctx.stroke();
        }

        // Inner glowing stream
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 2.4;
        ctx.globalAlpha = 0.75 * trailAlpha;
        ctx.stroke();

        // Hot white core thread
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.0;
        ctx.globalAlpha = 0.95 * trailAlpha;
        ctx.stroke();

        // Leading tip: searing white lightning discharge (abstrakt, keine Pfeilspitze)
        if (s.age <= s.travelTime + 0.05) {
          ctx.save();
          ctx.translate(s.x, s.y);
          const ang = Math.atan2(s.normY, s.normX);
          ctx.rotate(ang);

          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.4;
          ctx.globalAlpha = 1.0;
          ctx.beginPath();
          ctx.moveTo(8, 0);
          ctx.lineTo(-10, 0);
          ctx.stroke();

          // Crackling micro-fork
          const forkY = Math.sin(s.age * 50) * 4;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(4, 0);
          ctx.lineTo(-2, forkY);
          ctx.stroke();

          ctx.fillStyle = '#67e8f9';
          ctx.globalAlpha = 0.85;
          ctx.beginPath();
          ctx.arc(6, 0, 2.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        ctx.restore();
      } else if (s.isInvasionCapsule) {
        // 🛰️ Tumbling steel invasion capsule, lobbed in a high arc from the
        // Sling to the frontline (see addInvasionCapsule() above).
        const shadowAlpha = 0.3 * (1.0 - Math.min(1, s.elevation / (s.arcHeight * 1.6)));
        ctx.fillStyle = '#000000';
        ctx.globalAlpha = Math.max(0.06, shadowAlpha);
        ctx.beginPath();
        ctx.ellipse(s.x, s.y, 6, 2.2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;

        ctx.save();
        ctx.translate(s.x, s.drawY);
        ctx.rotate(s.spinAngle || 0);

        // Riveted steel pod body
        ctx.fillStyle = '#64748b';
        ctx.fillRect(-6, -4, 12, 8);
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(-6, -4, 12, 2.5);
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(-6, 2, 12, 2);
        // Nose/tail end caps
        ctx.fillStyle = '#334155';
        ctx.fillRect(-7, -3, 1.5, 6);
        ctx.fillRect(5.5, -3, 1.5, 6);
        // Three porthole windows - one per loaded Marine
        ctx.fillStyle = '#facc15';
        ctx.fillRect(-3, -1.2, 1.8, 1.8);
        ctx.fillRect(-0.9, -1.2, 1.8, 1.8);
        ctx.fillRect(1.2, -1.2, 1.8, 1.8);

        ctx.restore();
      } else if (s.isSuicideDrone) {
        // 🛸 Tactical FPV Suicide Quadcopter
        // Smoothly fade in as the drone clears the assembly bay aperture
        const droneAlpha = s.simDrone ? Math.min(1.0, Math.max(0, (s.age - 0.16) / 0.12)) : 1.0;
        if (droneAlpha <= 0) continue;

        const shadowAlpha = 0.25 * droneAlpha * (1.0 - Math.min(1, Math.max(0, s.y - s.drawY) / 25));
        ctx.fillStyle = '#000000';
        ctx.globalAlpha = Math.max(0.04, shadowAlpha);
        ctx.beginPath();
        ctx.ellipse(s.x, s.y, 5, 2.0, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = droneAlpha;

        ctx.save();
        ctx.translate(s.x, s.drawY);
        // Pitch forward in flight direction
        const fDir = s.headingX !== undefined ? (s.headingX >= 0 ? 1 : -1) : (s.team === 'western' ? 1 : -1);
        const pitch = s.headingY ? s.headingY * 0.25 : 0;
        ctx.rotate(fDir * 0.25 + pitch);

        // Dynamic afterburner jet trail behind quadcopter
        const fCol = s.team === 'western' ? 'rgba(0, 240, 255, 0.8)' : 'rgba(255, 0, 85, 0.8)';
        ctx.fillStyle = fCol;
        ctx.beginPath();
        ctx.moveTo(-fDir * 4, -1.2);
        ctx.lineTo(-fDir * 4, 1.2);
        ctx.lineTo(-fDir * (8 + Math.random() * 3), 0);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-fDir * 5, -0.6, 2, 1.2);

        // X-Frame
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(-5, -2);
        ctx.lineTo(5, 2);
        ctx.moveTo(-5, 2);
        ctx.lineTo(5, -2);
        ctx.stroke();

        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 0.9;
        ctx.beginPath();
        ctx.moveTo(-5, -2);
        ctx.lineTo(5, 2);
        ctx.moveTo(-5, 2);
        ctx.lineTo(5, -2);
        ctx.stroke();

        // Central explosive payload (olive/yellow brick with red wire)
        ctx.fillStyle = '#0a0f18';
        ctx.fillRect(-2.5, -2, 5, 4);
        ctx.fillStyle = '#ca8a04';
        ctx.fillRect(-2, -1.5, 4, 3);
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(-0.8, -1.5, 1.6, 3);

        // 4 Spinning rotor discs
        const rPositions = [
          { x: -5, y: -2 },
          { x: 5, y: -2 },
          { x: -5, y: 2 },
          { x: 5, y: 2 }
        ];
        ctx.fillStyle = 'rgba(203, 213, 225, 0.6)';
        for (let r = 0; r < 4; r++) {
          const rp = rPositions[r];
          ctx.beginPath();
          ctx.ellipse(rp.x, rp.y, 3.2, 1.4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Motor hub
          ctx.fillStyle = '#0a0f18';
          ctx.fillRect(rp.x - 0.6, rp.y - 0.6, 1.2, 1.2);
          ctx.fillStyle = 'rgba(203, 213, 225, 0.6)';
        }

        // Team-colored LED at tail
        const mainColor = s.team === 'western' ? '#00f0ff' : '#ff0055';
        ctx.fillStyle = mainColor;
        ctx.fillRect(-fDir * 2.5 - 0.6, -0.6, 1.2, 1.2);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-fDir * 2.5 - 0.3, -0.3, 0.6, 0.6);

        ctx.restore();
      } else if (s.isA9PigeonBombDrop) {
        // 💣 A9 Pigeon Heavy Ground Bomb (falling with aerodynamic nose-down rotation)
        const prog = Math.max(0, Math.min(1, 1.0 - (s.life / s.maxLife)));
        const tSec = typeof t !== 'undefined' ? t : (Date.now() * 0.001);

        // 1. Holographic Laser Target Designator Reticle on the Ground
        ctx.save();
        const reticleRadius = Math.max(7, 32 * (1.0 - prog * 0.72));
        ctx.strokeStyle = prog > 0.80 ? '#ef4444' : 'rgba(250, 204, 21, 0.85)';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.ellipse(s.destX, s.destY, reticleRadius, reticleRadius * 0.45, 0, 0, Math.PI * 2);
        ctx.stroke();

        // Crosshair brackets [ + ]
        ctx.strokeStyle = prog > 0.80 ? '#ef4444' : '#facc15';
        ctx.lineWidth = 1.4;
        const bCross = Math.max(4, reticleRadius * 0.45);
        ctx.beginPath();
        ctx.moveTo(s.destX - bCross, s.destY); ctx.lineTo(s.destX + bCross, s.destY);
        ctx.moveTo(s.destX, s.destY - bCross * 0.45); ctx.lineTo(s.destX, s.destY + bCross * 0.45);
        ctx.stroke();

        // Pulsing Laser Designation Guide Line (Bomb seeker tracking directly down to ground reticle)
        ctx.strokeStyle = prog > 0.75 ? 'rgba(239, 68, 68, 0.75)' : 'rgba(250, 204, 21, 0.50)';
        ctx.setLineDash([4, 4]);
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.destX, s.destY);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();

        // 2. Trailing Condensation Ribbons from Dual Tail Fins (Aerodynamic Wingtip Vortices)
        if (Array.isArray(s.history) && s.history.length > 1) {
          ctx.save();
          ctx.strokeStyle = 'rgba(226, 232, 240, 0.75)';
          ctx.lineWidth = 2.4;
          ctx.beginPath();
          for (let i = 0; i < s.history.length; i++) {
            const h = s.history[i];
            if (i === 0) ctx.moveTo(h.x, h.y);
            else ctx.lineTo(h.x, h.y);
          }
          ctx.stroke();
          ctx.restore();
        }

        // 3. Crisp High-Visibility 2.5D Bomb Body (Prominent & High Detail)
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);
        ctx.scale((s.dir || 1) * 1.16, 1.16); // High-visibility 116% scale

        // Cruciform Tail Stabilizer Fins (x: -9..-5, y: -6.5..+6.5)
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(-9, -6.5, 4.5, 13);
        ctx.fillStyle = '#475569';
        ctx.fillRect(-8, -5.5, 3.0, 11);
        // Yellow fin tips
        ctx.fillStyle = '#facc15';
        ctx.fillRect(-9, -6.5, 2.0, 2.0);
        ctx.fillRect(-9, 4.5, 2.0, 2.0);

        // Blinking Red Tail Beacon LED (14 Hz flashing)
        const isStrobeOn = Math.floor(tSec * 14) % 2 === 0;
        ctx.fillStyle = isStrobeOn ? '#ef4444' : '#7f1d1d';
        ctx.fillRect(-10, -1.2, 2.0, 2.4);
        if (isStrobeOn) {
          ctx.fillStyle = 'rgba(239, 68, 68, 0.55)';
          ctx.beginPath();
          ctx.arc(-9, 0, 4.5, 0, Math.PI * 2);
          ctx.fill();
        }

        // Armored Bomb Cylindrical Body (x: -6..+7, y: -4..+4)
        ctx.fillStyle = '#090d16';
        ctx.fillRect(-6.5, -4.5, 14.5, 9);
        ctx.fillStyle = '#334155';
        ctx.fillRect(-5.5, -3.5, 13, 7);
        ctx.fillStyle = '#475569';
        ctx.fillRect(-5.5, -3.5, 12, 3); // Upper sunlit bevel
        ctx.fillStyle = '#64748b';
        ctx.fillRect(-4.5, -2.5, 10, 1.2); // Specular line

        // Dual Bright Yellow Warning Ordnance Bands
        ctx.fillStyle = '#facc15';
        ctx.fillRect(1.5, -3.5, 2.5, 7);
        ctx.fillRect(5.5, -3.0, 2.0, 6);

        // White Dove Stencil Graphic on bomb side
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(-2.5, -0.8, 3.0, 1.8);
        ctx.fillRect(-1.0, -1.8, 1.8, 1.2);

        // Hardened Steel Penetrator Nose Cone (x: 7..11.5, y: -3.2..+3.2)
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.moveTo(7, -3.2);
        ctx.lineTo(11.5, 0);
        ctx.lineTo(7, 3.2);
        ctx.closePath();
        ctx.fill();
        // Impact Fuse Sensor Tip
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(10.5, -0.8, 1.6, 1.6);

        ctx.restore();
      } else if (s.isA9PigeonDeathFlock) {
        // 🕊️ A9 Pigeon Death Animation: Flock of Pigeons & Heroic Pilot Pigeon Escape!
        const fadeAlpha = Math.max(0, Math.min(1.0, s.life / 0.55));
        if (fadeAlpha > 0.01 && Array.isArray(s.birds)) {
          ctx.save();
          ctx.globalAlpha = fadeAlpha;
          const tSec = typeof t !== 'undefined' ? t : (Date.now() * 0.001);
          for (let i = 0; i < s.birds.length; i++) {
            const b = s.birds[i];
            const flapIdx = Math.floor(b.flapPhase % 4);
            const frameNum = flapIdx === 0 ? '1' : (flapIdx === 2 ? '3' : '2');
            const prefix = b.isPilot ? 'A9Pigeon_Pilot_Flap' : 'A9Pigeon_Bird_Flap';
            const birdKey = prefix + frameNum;
            const bmp = buildSpriteBitmap(birdKey, s.race || 'Human', 'western') || buildSpriteBitmap('A9Pigeon_Bird_Flap' + frameNum, s.race || 'Human', 'western');
            const src = bmp?.normal || bmp;
            const bSize = 24 * (b.scale || 1.0);
            const facingSign = b.dir >= 0 ? 1 : -1;
            const climbAngle = -0.30 + Math.sin(b.flapPhase * 0.5) * 0.08;

            ctx.save();
            ctx.translate(b.x, b.y);
            ctx.scale(facingSign, 1);
            ctx.rotate(climbAngle);

            if (src) {
              ctx.drawImage(src, -bSize / 2, -bSize / 2, bSize, bSize);
            } else {
              ctx.fillStyle = '#cbd5e1';
              ctx.fillRect(-bSize * 0.3, -bSize * 0.15, bSize * 0.6, bSize * 0.3);
            }

            // Pilot Pigeon Special Flairs (Fluttering Red Silk Aviator Scarf behind neck)
            if (b.isPilot) {
              const scarfWave1 = Math.sin(tSec * 22 + b.flapPhase) * 3.5;
              const scarfWave2 = Math.cos(tSec * 26 + b.flapPhase) * 4.2;
              ctx.fillStyle = '#ef4444'; // Radiant Red Silk
              ctx.beginPath();
              ctx.moveTo(-bSize * 0.18, bSize * 0.06);
              ctx.quadraticCurveTo(-bSize * 0.40, bSize * 0.06 + scarfWave1, -bSize * 0.68, bSize * 0.12 + scarfWave2);
              ctx.lineTo(-bSize * 0.65, bSize * 0.20 + scarfWave2);
              ctx.quadraticCurveTo(-bSize * 0.38, bSize * 0.14 + scarfWave1, -bSize * 0.16, bSize * 0.14);
              ctx.closePath();
              ctx.fill();

              // Scarf white fringe tip
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(-bSize * 0.70, bSize * 0.11 + scarfWave2, 3.2, 2.5);
            }

            ctx.restore();
          }
          ctx.restore();
        }
      } else if (s.isNukeWarhead || s.targetX !== undefined) {
        // Ground Targeting Laser Beacon (Pulsing Red Crosshairs)
        ctx.save();
        const flashAlpha = 0.5 + 0.5 * Math.sin(Date.now() * 0.02);
        ctx.strokeStyle = '#ef4444';
        ctx.globalAlpha = flashAlpha;
        ctx.lineWidth = 2;
        ctx.strokeRect(s.targetX - 24, s.targetY - 24, 48, 48);
        ctx.strokeRect(s.targetX - 12, s.targetY - 12, 24, 24);
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(s.targetX - 2, s.targetY - 2, 4, 4);

        // Nuke Rocket Warhead falling (Detailed 8-Bit Ballistic Missile)
        ctx.fillStyle = '#475569';
        ctx.fillRect(s.x - 6, s.y - 18, 12, 24);
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(s.x - 4, s.y + 6, 8, 8); // Nose cone
        ctx.fillStyle = '#facc15';
        ctx.fillRect(s.x - 2, s.y - 2, 4, 6); // Radiation band
        // Rocket Booster Thruster Fire
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(s.x - 3, s.y - 24, 6, 6);
        ctx.fillStyle = '#f97316';
        ctx.fillRect(s.x - 5, s.y - 34, 10, 10);
        ctx.restore();
      } else if (s.isWildRocketDeath) {
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle || 0);
        const drawSize = s.drawSize || ((s.r || 20) * 2.2);
        const gs = s.spriteData?.gridSize || 16;
        const pSize = drawSize / gs;
        const half = drawSize / 2;

        if (s.bitmap?.normal || s.bitmap) {
          ctx.drawImage(s.bitmap.normal || s.bitmap, -half, -half, drawSize, drawSize);
        } else if (s.spriteData?.grid) {
          const grid = s.spriteData.grid;
          const isWestern = s.isWestern !== false;
          const teamBadgeColor = isWestern ? '#00f0ff' : '#ff003c';
          for (let gy = 0; gy < gs; gy++) {
            for (let gx = 0; gx < gs; gx++) {
              const col = isWestern ? gx : (gs - 1 - gx);
              const c = grid[gy][col];
              if (c) {
                ctx.fillStyle = c === 'TEAM' ? teamBadgeColor : c;
                ctx.fillRect(-half + gx * pSize, -half + gy * pSize, pSize + 0.1, pSize + 0.1);
              }
            }
          }
        }

        // Two dynamic ammo belts whipping in the spiral draft
        const elapsed = (s.maxLife || 0.95) - s.life;
        const whip1 = Math.sin(elapsed * 22.0) * 0.5;
        const whip2 = Math.sin(elapsed * 26.0 + 1.8) * 0.55;

        // Belt 1 (rear hopper)
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(-half * 0.3, -half * 0.05);
        const b1EndX = -half * 0.85 - Math.cos(whip1) * 12;
        const b1EndY = -half * 0.2 + Math.sin(whip1) * 10;
        ctx.lineTo(b1EndX, b1EndY);
        ctx.stroke();
        ctx.fillStyle = '#facc15';
        ctx.fillRect(b1EndX - 1.5, b1EndY - 1.5, 3.5, 3.5);

        // Belt 2 (front drum)
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(-half * 0.05, half * 0.2);
        const b2EndX = -half * 0.6 - Math.cos(whip2) * 10;
        const b2EndY = half * 0.45 + Math.sin(whip2) * 8;
        ctx.lineTo(b2EndX, b2EndY);
        ctx.stroke();
        ctx.fillStyle = '#facc15';
        ctx.fillRect(b2EndX - 1.5, b2EndY - 1.5, 3.5, 3.5);

        // Jetpack thruster flame spurting out behind the tumbling unit
        const flameLen = 8 + Math.random() * 8;
        ctx.fillStyle = Math.random() > 0.5 ? '#f97316' : '#facc15';
        ctx.fillRect(-half - flameLen, -3, flameLen, 6);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(-half - (flameLen * 0.5), -1.5, flameLen * 0.5, 3);
        ctx.restore();
      } else if (s.isLootingVultureDeath) {
        ctx.save();
        ctx.translate(s.x + (s.shiver || 0), s.y);
        ctx.rotate(s.angle || 0);

        const drawSize = s.drawSize || 71;
        const forwardDir = s.forwardDir || 1;
        const stretchProg = s.stretchProg !== undefined ? s.stretchProg : 0;
        const t = (s.maxLife - s.life) * 2.3;

        // Unwinding progression per segment (1.0 = fully straight & unwound)
        const headUnwind = s.headUnwind !== undefined ? s.headUnwind : (1.0 - (s.waveScale !== undefined ? s.waveScale : 1.0));
        const pyramidUnwind = s.pyramidUnwind !== undefined ? s.pyramidUnwind : headUnwind;
        const bodyUnwind = s.bodyUnwind !== undefined ? s.bodyUnwind : headUnwind;
        const tailUnwind = s.tailUnwind !== undefined ? s.tailUnwind : headUnwind;

        const headWaveScale = Math.max(0, 1.0 - headUnwind);
        const pyramidWaveScale = Math.max(0, 1.0 - pyramidUnwind);
        const bodyWaveScale = Math.max(0, 1.0 - bodyUnwind);
        const tailWaveScale = Math.max(0, 1.0 - tailUnwind);

        // 3D Axial untwisting roll angles
        const untwistPhase = s.untwistPhase || 0;
        const headRoll = Math.sin(untwistPhase + 1.2) * headWaveScale * 0.34;
        const pyramidRoll = Math.sin(untwistPhase + 0.6) * pyramidWaveScale * 0.28;
        const bodyRoll = Math.sin(untwistPhase) * bodyWaveScale * 0.24;
        const tailRoll = Math.sin(untwistPhase - 0.8) * tailWaveScale * 0.36;

        // Dynamic longitudinal stretching ("gerade ausstrecken")
        // Segments expand outwards along the longitudinal spine, more than doubling body length!
        const tailShiftX = (-drawSize * 0.30 - drawSize * 0.28 * stretchProg) * forwardDir;
        const pyramidShiftX = (drawSize * 0.18 + drawSize * 0.08 * stretchProg) * forwardDir;
        const headShiftX = (drawSize * 0.22 + drawSize * 0.32 * stretchProg) * forwardDir;
        const pyramidScale = 0.78;

        // Sinuous traveling transverse waves (damped dynamically as each segment untwists)
        const tailWavePhase = t - 2.0;
        const tailSurgeX = Math.cos(tailWavePhase) * 4.5 * forwardDir * tailWaveScale;
        const tailWaveY = (Math.sin(tailWavePhase) * 11.5 + Math.sin(tailWavePhase * 2.0 - 0.6) * 3.2) * tailWaveScale;
        const tailPitch = (Math.cos(tailWavePhase) * 0.28 + Math.cos(tailWavePhase * 2.0) * 0.08) * forwardDir * tailWaveScale + tailRoll * forwardDir;

        const bodyWavePhase = t - 0.4;
        const bodySurgeX = Math.cos(bodyWavePhase) * 2.8 * forwardDir * bodyWaveScale;
        const bodyWaveY = (Math.sin(bodyWavePhase) * 7.5 + Math.sin(bodyWavePhase * 2.0 - 0.4) * 2.2) * bodyWaveScale;
        const bodyPitch = (Math.cos(bodyWavePhase) * 0.16 + Math.cos(bodyWavePhase * 2.0) * 0.04) * forwardDir * bodyWaveScale + bodyRoll * forwardDir;

        const pyramidWavePhase = t + 0.25;
        const pyramidSurgeX = Math.cos(pyramidWavePhase) * 1.8 * forwardDir * pyramidWaveScale;
        const pyramidWaveY = (Math.sin(pyramidWavePhase) * 5.2 + Math.sin(pyramidWavePhase * 2.0 - 0.3) * 1.5) * pyramidWaveScale;
        const pyramidPitch = (Math.cos(pyramidWavePhase) * 0.12 + Math.cos(pyramidWavePhase * 2.0) * 0.03) * forwardDir * pyramidWaveScale + pyramidRoll * forwardDir;

        const headLeadPhase = t + 0.8;
        const headSurgeX = Math.cos(headLeadPhase) * 1.4 * forwardDir * headWaveScale;
        const headBobY = (Math.sin(headLeadPhase) * 4.6 + Math.sin(headLeadPhase * 2.0 - 0.2) * 1.2) * headWaveScale;
        const headPitch = (Math.cos(headLeadPhase) * 0.14 + Math.cos(headLeadPhase * 2.0) * 0.035) * forwardDir * headWaveScale + headRoll * forwardDir;

        // If modular bitmaps exist, draw the full animated/straightened dragon
        if (s.bodyBitmap?.normal) {
          const isFlicker = s.isFrozen && Math.sin((s.freezeTimer || 0) * 24) > 0;
          const bodySrc = isFlicker ? (s.bodyBitmap.flash || s.bodyBitmap.normal) : s.bodyBitmap.normal;
          const pyramidSrc = isFlicker ? (s.pyramidBitmap.flash || s.pyramidBitmap.normal) : s.pyramidBitmap.normal;
          const headSrc = isFlicker ? (s.headBitmap.flash || s.headBitmap.normal) : s.headBitmap.normal;
          const tailSrc = isFlicker ? (s.tailBitmap?.flash || bodySrc) : (s.tailBitmap?.normal || bodySrc);

          // 1. Tail (Unwinds, stretches downward, caudal fin ray streamlines)
          ctx.save();
          const tailPivotX = ((10 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX;
          const tailPivotY = ((10 - 11.5) / 24) * drawSize + tailWaveY;
          ctx.translate(tailPivotX, tailPivotY);
          ctx.rotate(tailPitch);
          ctx.scale(1.0 + Math.abs(tailRoll) * 0.08, 1.0 - Math.abs(tailRoll) * 0.25);
          ctx.translate(-tailPivotX, -tailPivotY);
          ctx.drawImage(tailSrc, -drawSize / 2 + tailShiftX + tailSurgeX, tailWaveY - drawSize / 2, drawSize, drawSize);

          // Ethereal Caudal Fin Ray Tendril (streams straight down as it stretches taut)
          const finTipX = ((1 - 11.5) / 24) * drawSize * forwardDir + tailShiftX + tailSurgeX;
          const finTipY = tailWaveY + ((8 - 11.5) / 24) * drawSize;
          const finWhip = Math.sin(t * 3.4) * 4.5 * tailWaveScale;
          ctx.strokeStyle = stretchProg > 0.8 ? '#ffffff' : 'rgba(253, 224, 71, 0.85)';
          ctx.lineWidth = 1.3 + stretchProg * 0.6;
          ctx.beginPath();
          ctx.moveTo(finTipX, finTipY);
          ctx.bezierCurveTo(
            finTipX - 10 * forwardDir, finTipY + finWhip * 0.4,
            finTipX - 18 * forwardDir, finTipY - finWhip * 0.6,
            finTipX - (28 + stretchProg * 14) * forwardDir, finTipY + finWhip
          );
          ctx.stroke();
          ctx.restore();

          // 2. Body Chassis & Muscular Pelvis Bridge
          ctx.save();
          ctx.translate(bodySurgeX, bodyWaveY);
          ctx.rotate(bodyPitch);
          ctx.scale(1.0 + stretchProg * 0.08, 1.0 - Math.abs(bodyRoll) * 0.20);
          ctx.translate(-bodySurgeX, -bodyWaveY);

          // Muscular Pelvis Bridge (stretches taut between body pelvis and tail pivot)
          const bodyPelvisX = ((5.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX;
          const bodyPelvisY = bodyWaveY + ((11 - 11.5) / 24) * drawSize;
          const pelvisArc = -2.5 * tailWaveScale; // straightens into 0 as tail unwinds

          ctx.strokeStyle = '#1c1917';
          ctx.lineWidth = drawSize * (0.13 - stretchProg * 0.03); // becomes lean & taut
          ctx.beginPath();
          ctx.moveTo(bodyPelvisX, bodyPelvisY);
          ctx.quadraticCurveTo(
            (bodyPelvisX + tailPivotX) / 2,
            Math.min(bodyPelvisY, tailPivotY) + pelvisArc,
            tailPivotX,
            tailPivotY
          );
          ctx.stroke();

          // Glowing Golden Sinew Thread (drawn bowstring tension)
          ctx.strokeStyle = stretchProg > 0.85 ? '#ffffff' : '#fde047';
          ctx.lineWidth = 1.5 + stretchProg * 1.2;
          ctx.beginPath();
          ctx.moveTo(bodyPelvisX, bodyPelvisY - 0.5);
          ctx.quadraticCurveTo(
            (bodyPelvisX + tailPivotX) / 2,
            Math.min(bodyPelvisY, tailPivotY) + pelvisArc - 1.0,
            tailPivotX,
            tailPivotY - 0.5
          );
          ctx.stroke();

          // Golden Vertebrae Spines along elongated Pelvis Bridge
          const spineCount = 3 + Math.round(stretchProg * 3);
          for (let v = 1; v < spineCount; v++) {
            const u = v / spineCount;
            const vx = bodyPelvisX + (tailPivotX - bodyPelvisX) * u;
            const vy = bodyPelvisY + (tailPivotY - bodyPelvisY) * u + pelvisArc * Math.sin(u * Math.PI) - 2.0;
            ctx.fillStyle = stretchProg > 0.85 ? '#ffffff' : '#fde047';
            ctx.fillRect(vx - 1.0, vy - 1.0, 2.0, 2.0);
          }

          // Body sprite
          ctx.drawImage(bodySrc, -drawSize / 2 + bodySurgeX, bodyWaveY - drawSize / 2, drawSize, drawSize);
          ctx.restore();

          // 3. Ventral Throat Ridge (Connecting chest to head throat under pyramid)
          const bodyChestX = ((16.5 - 11.5) / 24) * drawSize * forwardDir + bodySurgeX;
          const bodyChestY = bodyWaveY + ((11.5 - 11.5) / 24) * drawSize;
          const headThroatX = ((14.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
          const headThroatY = (bodyWaveY + headBobY) + ((12 - 11.5) / 24) * drawSize;
          const throatArc = 1.2 * headWaveScale;

          ctx.strokeStyle = '#1c1917';
          ctx.lineWidth = drawSize * 0.08;
          ctx.beginPath();
          ctx.moveTo(bodyChestX, bodyChestY);
          ctx.quadraticCurveTo((bodyChestX + headThroatX) / 2, Math.max(bodyChestY, headThroatY) + throatArc, headThroatX, headThroatY);
          ctx.stroke();

          ctx.strokeStyle = stretchProg > 0.85 ? '#ffffff' : '#fde047';
          ctx.lineWidth = 1.2 + stretchProg * 0.8;
          ctx.beginPath();
          ctx.moveTo(bodyChestX, bodyChestY + 0.8);
          ctx.quadraticCurveTo((bodyChestX + headThroatX) / 2, Math.max(bodyChestY, headThroatY) + throatArc + 0.8, headThroatX, headThroatY + 0.8);
          ctx.stroke();

          // 4. Pyramid
          const pyramidPivotX = ((13.5 - 11.5) / 24) * drawSize * forwardDir + pyramidShiftX + pyramidSurgeX;
          const pyramidPivotY = pyramidWaveY + ((9 - 11.5) / 24) * drawSize;
          ctx.save();
          ctx.translate(pyramidPivotX, pyramidPivotY);
          ctx.rotate(pyramidPitch);
          ctx.scale(pyramidScale, pyramidScale * (1.0 - Math.abs(pyramidRoll) * 0.15));
          ctx.translate(-pyramidPivotX, -pyramidPivotY);
          ctx.drawImage(pyramidSrc, -drawSize / 2 + pyramidShiftX + pyramidSurgeX, pyramidWaveY - drawSize / 2, drawSize, drawSize);
          ctx.restore();

          // 5. Head & Streamlining Whiskers
          const neckJointX = ((14 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
          const neckJointY = bodyWaveY + ((10 - 11.5) / 24) * drawSize;
          ctx.save();
          ctx.translate(neckJointX, neckJointY + headBobY);
          ctx.rotate(headPitch);
          ctx.scale(1.0 + stretchProg * 0.05, 1.0 - Math.abs(headRoll) * 0.20);
          ctx.translate(-neckJointX, -(neckJointY + headBobY));
          ctx.drawImage(headSrc, -drawSize / 2 + headShiftX + headSurgeX, (bodyWaveY + headBobY) - drawSize / 2, drawSize, drawSize);

          // Flowing Whisker Barbels (streamlining straight back along the neck as it stretches taut)
          const chinX = ((18.5 - 11.5) / 24) * drawSize * forwardDir + headShiftX + headSurgeX;
          const chinY = (bodyWaveY + headBobY) + ((13.5 - 11.5) / 24) * drawSize;
          const b1WaveA = Math.sin(t * 3.6) * 2.6 * headWaveScale;
          const b1TipX = chinX - ((18 + stretchProg * 12) * forwardDir);
          const b1TipY = chinY + (10 - stretchProg * 4) + b1WaveA;
          ctx.strokeStyle = stretchProg > 0.85 ? '#ffffff' : '#fde047';
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          ctx.moveTo(chinX, chinY);
          ctx.quadraticCurveTo(chinX - 8 * forwardDir, chinY + 8 * (1.0 - stretchProg * 0.5), b1TipX, b1TipY);
          ctx.stroke();
          ctx.restore();

          // Phase 3 Freeze: Solar corona & starburst flare at pyramid peak
          if (s.isFrozen) {
            const fProg = Math.min(1.0, (s.freezeTimer || 0) / 0.38);
            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            const flareR = 14 + fProg * 26;
            ctx.strokeStyle = '#fef08a';
            ctx.lineWidth = 2.4;
            // Radiant 4-point solar flare
            ctx.beginPath();
            ctx.moveTo(pyramidPivotX, pyramidPivotY - flareR);
            ctx.lineTo(pyramidPivotX, pyramidPivotY + flareR);
            ctx.moveTo(pyramidPivotX - flareR, pyramidPivotY);
            ctx.lineTo(pyramidPivotX + flareR, pyramidPivotY);
            ctx.stroke();

            // Diagonal glint beams
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.4;
            const diagR = flareR * 0.65;
            ctx.beginPath();
            ctx.moveTo(pyramidPivotX - diagR, pyramidPivotY - diagR);
            ctx.lineTo(pyramidPivotX + diagR, pyramidPivotY + diagR);
            ctx.moveTo(pyramidPivotX + diagR, pyramidPivotY - diagR);
            ctx.lineTo(pyramidPivotX - diagR, pyramidPivotY + diagR);
            ctx.stroke();

            // Pulsing golden corona ring
            ctx.strokeStyle = 'rgba(253, 224, 71, 0.75)';
            ctx.lineWidth = 2.0;
            ctx.beginPath();
            ctx.arc(pyramidPivotX, pyramidPivotY, 6 + fProg * 20, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
          }
        } else if (s.wholeBitmap?.normal || s.wholeBitmap) {
          const half = drawSize / 2;
          ctx.drawImage(s.wholeBitmap.normal || s.wholeBitmap, -half, -half, drawSize, drawSize);
        } else if (s.spriteData?.grid) {
          const gs = s.spriteData.gridSize || 24;
          const pSize = drawSize / gs;
          const half = drawSize / 2;
          const grid = s.spriteData.grid;
          for (let gy = 0; gy < gs; gy++) {
            for (let gx = 0; gx < gs; gx++) {
              const col = forwardDir === 1 ? gx : (gs - 1 - gx);
              const c = grid[gy][col];
              if (c) {
                ctx.fillStyle = c;
                ctx.fillRect(-half + gx * pSize, -half + gy * pSize, pSize + 0.1, pSize + 0.1);
              }
            }
          }
        }
        ctx.restore();
      } else if (s.isPulpPencilTrick) {
        // 🤡 SUL PULP: "PENCIL TRICK VANISH"
        // 1. Stage Spotlight Pool on the floor
        if (s.spotlightAlpha > 0.01) {
          ctx.save();
          ctx.globalAlpha = s.spotlightAlpha * 0.40;
          ctx.translate(s.x, s.y + 12);
          ctx.fillStyle = getPulpSpotGrad(ctx);
          ctx.beginPath();
          ctx.ellipse(0, 0, 34, 11, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        // 2. Pulp Body & Articulated Clown Head (Fades & spins as it morphs into pencil in Phase 2)
        if (!s.hasVanished && s.morphProg < 0.95) {
          const clownAlpha = Math.max(0, 1.0 - s.morphProg * 1.3);
          if (clownAlpha > 0.02) {
            ctx.save();
            ctx.globalAlpha = clownAlpha;
            ctx.translate(s.x, s.y + s.slamOffsetY);
            ctx.scale(s.bodySquashX, s.bodySquashY);

            const half = s.drawSize / 2;

            // Layer 1: Dorsal Mortar on far shoulder (in background behind head and body!)
            if (s.weaponBitmap?.normal) {
              ctx.drawImage(s.weaponBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }
            // Layer 2: Legs
            const currentLegs = s.legsBitmap?.normal;
            if (currentLegs) {
              ctx.drawImage(currentLegs, -half, -half, s.drawSize, s.drawSize);
            }
            // Layer 3: Torso & Scepter in foreground
            if (s.bodyBitmap?.normal) {
              ctx.drawImage(s.bodyBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }
            // Layer 4: Articulated Clown Head with leaning headPitch
            if (s.headBitmap?.normal) {
              ctx.save();
              const neckY = -s.drawSize * (4 / 24);
              const neckX = -s.dir * s.drawSize * (1.5 / 24);
              ctx.translate(neckX, neckY);
              ctx.rotate(s.headPitch);
              ctx.drawImage(s.headBitmap.normal, -half - neckX, -half - neckY, s.drawSize, s.drawSize);
              ctx.restore();
            }

            ctx.restore();
          }
        }

        // 3. The Transformed Pencil (Standing point-down in asphalt & slammed into ground!)
        if (s.pencilScale > 0.02) {
          ctx.save();
          ctx.translate(s.x, s.y + 12 + s.pencilOffsetY); // Pivot at the needle tip on the ground
          const pScale = s.pencilScale * 1.80; // 80% larger pencil!
          ctx.scale(pScale, pScale);

          const pw = 6.0; // pencil width
          // Exposed cedar wood sharpening cone (y=0 to y=-8)
          ctx.fillStyle = '#fed7aa';
          ctx.beginPath();
          ctx.moveTo(0, 0); // needle point touching ground
          ctx.lineTo(-pw / 2, -8);
          ctx.lineTo(pw / 2, -8);
          ctx.closePath();
          ctx.fill();

          // Dark graphite lead tip (y=0 to y=-3.5)
          ctx.fillStyle = '#0f172a';
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-1.4, -3.5);
          ctx.lineTo(1.4, -3.5);
          ctx.closePath();
          ctx.fill();
          // Tip glint
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(-0.4, -2.5, 0.8, 0.8);

          // Yellow hexagonal pencil shaft (y=-8 to y=-24)
          // Left facet (shadow)
          ctx.fillStyle = '#ca8a04';
          ctx.fillRect(-pw / 2, -24, pw / 3, 16);
          // Center facet (bright yellow)
          ctx.fillStyle = '#eab308';
          ctx.fillRect(-pw / 6, -24, pw / 3, 16);
          // Right facet (highlight)
          ctx.fillStyle = '#fde047';
          ctx.fillRect(pw / 6, -24, pw / 3, 16);

          // Scalloped cutouts at wood cone transition
          ctx.fillStyle = '#ca8a04';
          ctx.beginPath();
          ctx.arc(-pw / 3, -8, 1.0, 0, Math.PI);
          ctx.arc(0, -8, 1.0, 0, Math.PI);
          ctx.arc(pw / 3, -8, 1.0, 0, Math.PI);
          ctx.fill();

          // Comical clown face detail on pencil shaft
          ctx.strokeStyle = '#713f12';
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.arc(0, -16, 1.8, 0.2, Math.PI - 0.2);
          ctx.stroke();
          ctx.fillStyle = '#ff0033';
          ctx.fillRect(-0.6, -18.5, 1.2, 1.2); // red nose

          // Silver ferrule band (y=-24 to y=-27)
          ctx.fillStyle = '#cbd5e1';
          ctx.fillRect(-pw / 2, -27, pw, 3);
          ctx.fillStyle = '#64748b';
          ctx.fillRect(-pw / 2, -25.5, pw, 0.8);

          // Pink rubber eraser (y=-27 to y=-31)
          ctx.fillStyle = '#f43f5e';
          ctx.beginPath();
          ctx.moveTo(-pw / 2, -27);
          ctx.lineTo(pw / 2, -27);
          ctx.lineTo(pw / 2, -30);
          ctx.arc(0, -30, pw / 2, 0, Math.PI, true);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = '#fb7185';
          ctx.fillRect(-pw / 4, -30, pw / 2, 1.2);

          // Golden jester bell on top of eraser
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(0, -32.5, 1.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(-0.4, -33.2, 0.8, 0.8);

          ctx.restore();
        }

        // 4. Large Confetti Playing Cards (Fluttering and tumbling in low gravity)
        if (s.cards && s.cards.length > 0) {
          const cw = 16;
          const ch = 23;
          for (let c of s.cards) {
            if (c.alpha <= 0.01) continue;
            ctx.save();
            ctx.globalAlpha = c.alpha;
            ctx.translate(c.x, c.y);
            ctx.rotate(c.angle);
            ctx.scale(c.scaleX, 1.0); // 3D flip card effect

            const isFaceUp = c.scaleX >= 0;
            if (isFaceUp) {
              // Front Face: Ivory card with border and suit
              ctx.fillStyle = '#f8fafc';
              ctx.fillRect(-cw / 2, -ch / 2, cw, ch);
              ctx.strokeStyle = '#1e293b';
              ctx.lineWidth = 0.9;
              ctx.strokeRect(-cw / 2, -ch / 2, cw, ch);

              // Inner border
              ctx.strokeStyle = '#e2e8f0';
              ctx.lineWidth = 0.5;
              ctx.strokeRect(-cw / 2 + 1.5, -ch / 2 + 1.5, cw - 3, ch - 3);

              // Center suit symbol
              const suitCol = c.isRed ? '#dc2626' : (c.suitIdx === 2 ? '#fbbf24' : '#7c3aed');
              ctx.fillStyle = suitCol;
              ctx.beginPath();
              if (c.isRed) {
                // Diamond
                ctx.moveTo(0, -4.5);
                ctx.lineTo(3.5, 0);
                ctx.lineTo(0, 4.5);
                ctx.lineTo(-3.5, 0);
                ctx.closePath();
              } else {
                // Spade / Sul Eye
                ctx.arc(0, 0, 3.2, 0, Math.PI * 2);
              }
              ctx.fill();

              // Corner pips
              ctx.fillStyle = suitCol;
              ctx.fillRect(-cw / 2 + 2, -ch / 2 + 2, 1.8, 2.2);
              ctx.fillRect(cw / 2 - 3.8, ch / 2 - 4.2, 1.8, 2.2);
            } else {
              // Back Face: Sul Royal Magenta & Gold
              ctx.fillStyle = '#6b21a8';
              ctx.fillRect(-cw / 2, -ch / 2, cw, ch);
              ctx.strokeStyle = '#fbbf24';
              ctx.lineWidth = 0.8;
              ctx.strokeRect(-cw / 2, -ch / 2, cw, ch);

              // Diamond emblem
              ctx.fillStyle = '#a855f7';
              ctx.beginPath();
              ctx.moveTo(0, -6);
              ctx.lineTo(4.5, 0);
              ctx.lineTo(0, 6);
              ctx.lineTo(-4.5, 0);
              ctx.closePath();
              ctx.fill();

              ctx.fillStyle = '#fef08a';
              ctx.beginPath();
              ctx.arc(0, 0, 1.5, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.restore();
          }
        }

        // 5. Bouncing Golden Jester Bells
        if (s.bells && s.bells.length > 0) {
          for (let b of s.bells) {
            if (b.alpha <= 0.01) continue;
            ctx.save();
            ctx.globalAlpha = b.alpha;
            ctx.translate(b.x, b.y);
            ctx.rotate(b.angle);
            // Gold bell sphere
            ctx.fillStyle = '#fbbf24';
            ctx.beginPath();
            ctx.arc(0, 0, 2.8, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#f59e0b';
            ctx.fillRect(-1.8, 1.2, 3.6, 1.0);
            // Glint
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(-1.2, -1.8, 1.2, 1.2);
            ctx.restore();
          }
        }

        // 6. Theatrical Floating Comic Text ("TADAA! IT'S GONE.") - Shown ONLY when vanished!
        if (s.textAlpha > 0.02 && s.hasVanished) {
          ctx.save();
          ctx.globalAlpha = s.textAlpha;
          ctx.translate(s.x, s.textY);
          ctx.scale(s.textScale, s.textScale);
          ctx.font = 'bold 12px Rajdhani, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const txt = "TADAA! IT'S GONE.";
          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 3.2;
          ctx.strokeText(txt, 0, 0);
          ctx.fillStyle = '#fef08a';
          ctx.fillText(txt, 0, 0);
          ctx.restore();
        }
      } else if (s.isPinkBeretKissDeath) {
        // 💋 PINK BERET: THEATRICAL BLOWING KISS DEATH & HEART EXPLOSION
        if (!s.hasPopped) {
          const half = s.drawSize / 2;

          // 🌹 0. Ground Shadow & Romantic Limelight Floor Pool (Anchors 2.5D Isometric Grounding)
          const groundY = s.y + (s.drawSize * 0.38) + (s.stepY || 0);
          const shadowScale = (s.stepScale || 1.0) * (1.0 + (s.bodyBreathe || 0) * 0.5);

          // Limelight floor pool
          ctx.save();
          ctx.translate(s.x, groundY);
          if (shadowScale !== 1.0) ctx.scale(shadowScale, shadowScale);
          ctx.fillStyle = getPinkBeretLimeGrad(ctx);
          ctx.beginPath();
          ctx.ellipse(0, 0, 26, 11, -0.25, 0, Math.PI * 2);
          ctx.fill();

          // Soft isometric contact shadow under boots
          ctx.fillStyle = 'rgba(15, 23, 42, 0.38)';
          ctx.beginPath();
          ctx.ellipse(0, 0, 14, 5.5, -0.25, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          // 🌸 1. Background Layer of Love Perfume Mist (depth behind soldier)
          if (s.perfumePuffs && s.perfumePuffs.length > 0) {
            for (let i = 0; i < s.perfumePuffs.length; i++) {
              const p = s.perfumePuffs[i];
              if (p.alpha <= 0.01) continue;
              ctx.save();
              ctx.globalAlpha = p.alpha * 0.45;
              ctx.fillStyle = p.color;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.r * 1.3, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }
          }

          // 🕺 2. Character Sprite Transform with Kinematic Breathing & Attitude Tilt
          ctx.save();
          const charScale = s.stepScale || 1.0;
          const breathe = s.bodyBreathe || 0;
          const yBob = s.bodyYBob || 0;
          const tilt = s.bodyTilt || 0;
          ctx.translate(s.x, s.y + (s.stepY || 0) + yBob);
          ctx.rotate(tilt);

          if (s.stage === 'turn_side') {
            // Turning from side towards camera: scale horizontally
            const turnScaleX = s.dir * Math.max(0.2, 1.0 - s.turnProgress * 0.6) * charScale;
            ctx.scale(turnScaleX, charScale * (1 + breathe));
            if (s.sideStandBitmap?.normal) {
              ctx.drawImage(s.sideStandBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }
            if (s.sideTorsoBitmap?.normal) {
              ctx.drawImage(s.sideTorsoBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }
          } else if (s.stage === 'turn_34') {
            // Intermediate 3/4 turn towards camera
            const turnScaleX = s.dir * (0.6 + s.turnProgress * 0.4) * charScale;
            ctx.scale(turnScaleX, charScale * (1 + breathe));
            if (s.turnBitmap?.normal) {
              ctx.drawImage(s.turnBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            } else if (s.frontKissBitmap?.normal) {
              ctx.drawImage(s.frontKissBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }
          } else if (s.stage === 'front_kiss') {
            // Full frontal view: Hand on hip, other hand kissing lips!
            ctx.scale(s.dir * charScale * (1 - breathe * 0.4), charScale * (1 + breathe));
            if (s.frontKissBitmap?.normal) {
              ctx.drawImage(s.frontKissBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }

            // Kiss lip sparkle glint on fingertips / puckered lips
            const kissPuckProgress = (s.maxLife - s.life - 0.30) / 0.35;
            if (kissPuckProgress > 0.25 && kissPuckProgress < 0.85) {
              const lipGlintAlpha = Math.sin((kissPuckProgress - 0.25) / 0.60 * Math.PI);
              const lipX = (1 / 24) * s.drawSize;
              const lipY = (-5 / 24) * s.drawSize;
              ctx.save();
              ctx.translate(lipX, lipY);
              ctx.globalAlpha = lipGlintAlpha * 0.85;
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(0, 0, 1.6, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = '#fda4af';
              ctx.beginPath();
              ctx.arc(0, 0, 3.2, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }
          } else if (s.stage === 'front_wink') {
            // Full frontal view: Hand thrown open, wink on sunglasses!
            ctx.scale(s.dir * charScale * (1 - breathe * 0.4), charScale * (1 + breathe));
            if (s.frontWinkBitmap?.normal) {
              ctx.drawImage(s.frontWinkBitmap.normal, -half, -half, s.drawSize, s.drawSize);
            }
          }

          // 4. White Sunglasses 8-Ray Diamond Sparkle / Wink Glint (Stage 4: 0.95s - 1.25s)
          if (s.sparkleProgress !== undefined && s.sparkleProgress > 0 && s.sparkleProgress < 1) {
            // Positioned at the winking eye (x: +2px / screen right)
            const glintX = (2 / 24) * s.drawSize;
            const glintY = (-9 / 24) * s.drawSize;
            const sp = s.sparkleProgress;
            const starScale = Math.sin(sp * Math.PI) * 2.5;
            ctx.save();
            ctx.translate(glintX, glintY);

            // Horizontal anamorphic lens flare beam
            ctx.save();
            ctx.globalAlpha = Math.sin(sp * Math.PI) * 0.75;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            ctx.moveTo(-14 * starScale * 0.5, 0);
            ctx.lineTo(14 * starScale * 0.5, 0);
            ctx.stroke();
            ctx.restore();

            ctx.rotate(sp * Math.PI * 1.4);
            ctx.scale(starScale, starScale);
            // 8-point Diamond Star (4 primary + 4 diagonal rays)
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.moveTo(0, -7.5);
            ctx.lineTo(2, -2);
            ctx.lineTo(7.5, 0);
            ctx.lineTo(2, 2);
            ctx.moveTo(0, 7.5);
            ctx.lineTo(-2, 2);
            ctx.lineTo(-7.5, 0);
            ctx.lineTo(-2, -2);
            ctx.closePath();
            ctx.fill();

            // Diagonal secondary rays
            ctx.fillStyle = 'rgba(253, 224, 71, 0.85)';
            ctx.beginPath();
            ctx.moveTo(0, -4);
            ctx.lineTo(1.2, -1.2);
            ctx.lineTo(4, 0);
            ctx.lineTo(1.2, 1.2);
            ctx.moveTo(0, 4);
            ctx.lineTo(-1.2, 1.2);
            ctx.lineTo(-4, 0);
            ctx.lineTo(-1.2, -1.2);
            ctx.closePath();
            ctx.fill();

            // Golden diamond glint core
            ctx.fillStyle = '#fef08a';
            ctx.beginPath();
            ctx.arc(0, 0, 2.2, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(0, 0, 1.0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }

          ctx.restore(); // restore soldier transform

          // 🌸 2. Foreground Veil of Love Perfume Mist (softly conceals & diffuses side-to-front perspective transition)
          if (s.perfumePuffs && s.perfumePuffs.length > 0) {
            for (let i = 0; i < s.perfumePuffs.length; i++) {
              const p = s.perfumePuffs[i];
              if (p.alpha <= 0.01) continue;
              ctx.save();
              // Outer soft mist cloud
              ctx.globalAlpha = p.alpha;
              ctx.fillStyle = p.color;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
              ctx.fill();
              // Inner warm rose highlight
              ctx.globalAlpha = p.alpha * 0.75;
              ctx.fillStyle = p.innerColor;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.r * 0.55, 0, Math.PI * 2);
              ctx.fill();
              // Luminous atomized center
              ctx.globalAlpha = p.alpha * 0.85;
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.r * 0.25, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
            }
          }

          // 🌸 3. Sparkling Aerosol Droplets & Shimmer Specks
          if (s.perfumeDroplets && s.perfumeDroplets.length > 0) {
            for (let i = 0; i < s.perfumeDroplets.length; i++) {
              const d = s.perfumeDroplets[i];
              if (d.alpha <= 0.01) continue;
              const twinkleAlpha = Math.min(1.0, d.alpha * (0.65 + Math.sin(d.twinkle) * 0.35));
              ctx.save();
              ctx.globalAlpha = twinkleAlpha;
              ctx.fillStyle = d.color;
              ctx.beginPath();
              ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
              ctx.fill();
              // Micro cross glint for prominent droplets
              if (d.r > 1.2 && twinkleAlpha > 0.4) {
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 0.8;
                ctx.beginPath();
                ctx.moveTo(d.x - 2, d.y);
                ctx.lineTo(d.x + 2, d.y);
                ctx.moveTo(d.x, d.y - 2);
                ctx.lineTo(d.x, d.y + 2);
                ctx.stroke();
              }
              ctx.restore();
            }
          }

          // ✨ 4. Stardust Micro-Heart Wake Trail behind flying kiss heart
          if (s.heartTrail && s.heartTrail.length > 0) {
            for (let i = 0; i < s.heartTrail.length; i++) {
              const tr = s.heartTrail[i];
              if (tr.alpha <= 0.01) continue;
              ctx.save();
              ctx.globalAlpha = tr.alpha;
              ctx.translate(tr.x, tr.y);
              ctx.rotate(tr.rot);
              ctx.scale(tr.scale, tr.scale);
              if (tr.isSpark) {
                // Sparkling stardust diamond cross
                ctx.fillStyle = tr.color;
                ctx.beginPath();
                ctx.arc(0, 0, 1.8, 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 0.6;
                ctx.beginPath();
                ctx.moveTo(-3, 0); ctx.lineTo(3, 0);
                ctx.moveTo(0, -3); ctx.lineTo(0, 3);
                ctx.stroke();
              } else {
                // Micro trailing heart
                ctx.fillStyle = tr.color;
                ctx.beginPath();
                ctx.moveTo(0, 1);
                ctx.bezierCurveTo(-3, -2, -5, 2, 0, 6);
                ctx.bezierCurveTo(5, 2, 3, -2, 0, 1);
                ctx.fill();
              }
              ctx.restore();
            }
          }

          // 💖 5. Volumetric 3D Kiss Heart flying towards camera perspective
          if (s.kissActive && s.kissAlpha > 0.01) {
            const throb = s.kissThrob || 1.0;
            const bank = s.kissBank || 0;
            const hScale = (s.kissScale || 1.0) * throb;
            ctx.save();
            ctx.globalAlpha = s.kissAlpha;
            ctx.translate(s.kissX, s.kissY);
            ctx.rotate(bank);
            ctx.scale(hScale, hScale);

            // Layer A: Radiant ambient pink/rose aura
            ctx.fillStyle = 'rgba(255, 20, 147, 0.38)';
            ctx.beginPath();
            ctx.arc(0, 3, 11, 0, Math.PI * 2);
            ctx.fill();

            // Layer B: 3D Base Under-Shadow (deep carmine depth)
            ctx.fillStyle = '#9f1239';
            ctx.beginPath();
            ctx.moveTo(0, 3.2);
            ctx.bezierCurveTo(-5.5, -2.5, -9.5, 4.5, 0, 10.8);
            ctx.bezierCurveTo(9.5, 4.5, 5.5, -2.5, 0, 3.2);
            ctx.fill();

            // Layer C: Vibrant Hot Pink Main Body
            ctx.fillStyle = '#ff1493';
            ctx.beginPath();
            ctx.moveTo(0, 2);
            ctx.bezierCurveTo(-5, -3, -9, 4, 0, 10);
            ctx.bezierCurveTo(9, 4, 5, -3, 0, 2);
            ctx.fill();

            // Layer D: 3D Volumetric Rim Highlight (soft rose pink)
            ctx.fillStyle = '#fb7185';
            ctx.beginPath();
            ctx.moveTo(0, 2);
            ctx.bezierCurveTo(-4, -2, -7.5, 3.5, 0, 8.5);
            ctx.bezierCurveTo(7.5, 3.5, 4, -2, 0, 2);
            ctx.fill();

            // Layer E: Glossy Specular Glass Reflection on Left Lobe
            ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
            ctx.beginPath();
            ctx.ellipse(-3.2, 1.8, 2.2, 1.1, -0.6, 0, Math.PI * 2);
            ctx.fill();

            // Micro glint on tip of reflection
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(-2.5, 1.2, 0.9, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
          }
        } else {
          // Phase 4: Beautiful Pink Hearts Explosion with 2.5D Depth
          for (let i = 0; i < s.hearts.length; i++) {
            const h = s.hearts[i];
            if (h.alpha <= 0.01) continue;
            ctx.save();
            ctx.globalAlpha = h.alpha;
            ctx.translate(h.x, h.y);
            ctx.rotate(h.rot);
            ctx.scale(h.scale, h.scale);

            // Shaded under-depth
            if (h.scale > 0.8) {
              ctx.fillStyle = 'rgba(159, 18, 57, 0.45)';
              ctx.beginPath();
              ctx.moveTo(0.6, 3.6);
              ctx.bezierCurveTo(-4.5, -2.5, -9.5, 4.5, 0.6, 11.6);
              ctx.bezierCurveTo(10.5, 4.5, 5.5, -2.5, 0.6, 3.6);
              ctx.fill();
            }

            // Main heart body
            ctx.fillStyle = h.color;
            ctx.beginPath();
            ctx.moveTo(0, 3);
            ctx.bezierCurveTo(-5, -3, -10, 4, 0, 11);
            ctx.bezierCurveTo(10, 4, 5, -3, 0, 3);
            ctx.fill();

            // Specular highlight on larger foreground hearts
            if (h.scale > 1.0) {
              ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
              ctx.beginPath();
              ctx.arc(-3, 2.5, 1.5, 0, Math.PI * 2);
              ctx.fill();
            }

            ctx.restore();
          }
        }
      } else if (s.isPinkBeretHomingRocket) {
        // 🚀 PINK BERET HOMING ROCKET
        this._renderTaperedRibbonTrail(ctx, s.trailHistory, s.trailColor || '#ff2a85', 3.8);
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle);
        // Rocket Body
        ctx.fillStyle = '#334155';
        ctx.fillRect(-8, -2.5, 14, 5);
        // Nosecone
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(6, -2.5);
        ctx.lineTo(11, 0);
        ctx.lineTo(6, 2.5);
        ctx.closePath();
        ctx.fill();
        // Pink Decal Stripe
        ctx.fillStyle = s.trailColor || '#ff2a85';
        ctx.fillRect(-2, -2.5, 3, 5);
        // Booster Flame
        const flameLen = 4 + Math.random() * 5;
        ctx.fillStyle = '#fde047';
        ctx.beginPath();
        ctx.moveTo(-8, -2);
        ctx.lineTo(-8 - flameLen, 0);
        ctx.lineTo(-8, 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      } else if (s.isDiscardedLauncher) {
        // 🗑️ DISCARDED LAUNCHER TUMBLING PROP
        const alpha = Math.max(0, Math.min(1.0, s.life / 0.35));
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle);
        ctx.fillStyle = '#334155';
        ctx.fillRect(-7, -2, 14, 4);
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(-8, -3, 3, 6); // Exhaust cone
        ctx.fillStyle = '#ff2a85';
        ctx.fillRect(1, -2, 2, 4); // Pink decal
        ctx.restore();
      }
    }
    ctx.restore();

    // 3. Render Particles & Voronoi Shards (Batched by Composite Operation)
    ctx.save();
    const nowSec = performance.now() * 0.001;

    // Pass A: standard particles (source-over)
    ctx.globalCompositeOperation = 'source-over';
    this._additiveQueue.length = 0;
    let lastColor = null;
    let lastAlpha = -1;

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      if (p.alpha <= 0.001) continue;

      const pMargin = p.isAnamorphicFlare ? 160 : 80;
      if (bounds && (p.x < bounds.left - pMargin || p.x > bounds.right + pMargin)) {
        continue;
      }

      if (p.isAnamorphicFlare || p.isHeatShimmer || p.isEmber || p.isGoldDustGlide) {
        this._additiveQueue.push(p);
        continue;
      }
      if (p.isVapor) {
        lastColor = null;
        lastAlpha = -1;
        ctx.fillStyle = p.color || 'rgba(224, 242, 254, 0.25)';
        ctx.globalAlpha = p.alpha;
        if (p.isPixel) {
          const px = Math.round(p.x);
          const py = Math.round(p.y);
          const pr = Math.max(1, Math.round(p.radius * 0.75));
          ctx.fillRect(px - pr, py - Math.floor(pr * 0.6), pr * 2, Math.max(1, Math.round(pr * 1.2)));
          ctx.fillRect(px - Math.floor(pr * 0.6), py - pr, Math.max(1, Math.round(pr * 1.2)), pr * 2);
        } else {
          ctx.beginPath();
          ctx.ellipse(p.x, p.y, p.radius * 1.3, p.radius * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (p.isShard && p.points) {
        lastColor = null;
        lastAlpha = -1;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        const cos = Math.cos(p.rot || 0);
        const sin = Math.sin(p.rot || 0);
        const pts = p.points;
        const p0x = p.x + cos * pts[0].x - sin * pts[0].y;
        const p0y = p.y + sin * pts[0].x + cos * pts[0].y;
        ctx.beginPath();
        ctx.moveTo(p0x, p0y);
        for (let pt = 1; pt < pts.length; pt++) {
          ctx.lineTo(p.x + cos * pts[pt].x - sin * pts[pt].y, p.y + sin * pts[pt].x + cos * pts[pt].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = p.highlightColor || '#ffffff';
        ctx.lineWidth = 0.75;
        ctx.beginPath();
        ctx.moveTo(p0x, p0y);
        ctx.lineTo(p.x + cos * pts[1].x - sin * pts[1].y, p.y + sin * pts[1].x + cos * pts[1].y);
        ctx.stroke();
      } else if (p.isLiquidDroplet) {
        lastColor = null;
        lastAlpha = -1;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        const spd = Math.hypot(p.vx, p.vy);
        const ang = Math.atan2(p.vy, p.vx);
        const stretch = Math.min(10, Math.max(1.4, spd * 0.04));
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(ang);
        ctx.beginPath();
        ctx.ellipse(0, 0, p.radius * stretch, p.radius, 0, 0, Math.PI * 2);
        ctx.fill();
        if (p.highlightColor) {
          ctx.fillStyle = p.highlightColor;
          ctx.beginPath();
          ctx.ellipse(p.radius * stretch * 0.25, 0, p.radius * stretch * 0.45, p.radius * 0.45, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      } else if (p.isPixelDroplet) {
        lastColor = null;
        lastAlpha = -1;
        ctx.globalAlpha = p.alpha;
        const px = Math.round(p.x);
        const py = Math.round(p.y);
        const spd = Math.hypot(p.vx, p.vy);

        if (spd < 15 || p.settled) {
          ctx.fillStyle = p.color;
          ctx.fillRect(px - 1, py - 1, 3, 2);
          if (p.highlightColor && p.alpha > 0.35) {
            ctx.fillStyle = p.highlightColor;
            ctx.fillRect(px, py - 1, 1, 1);
          }
        } else {
          const dirX = p.vx / spd;
          const dirY = p.vy / spd;
          const trailLen = Math.min(5, Math.max(2, Math.round(spd * 0.035)));

          if (trailLen >= 4) {
            ctx.fillStyle = p.color;
            ctx.globalAlpha = p.alpha * 0.45;
            const t2x = Math.round(px - dirX * 4);
            const t2y = Math.round(py - dirY * 4);
            ctx.fillRect(t2x, t2y, 1, 1);
          }

          if (trailLen >= 2) {
            ctx.fillStyle = p.color;
            ctx.globalAlpha = p.alpha * 0.75;
            const t1x = Math.round(px - dirX * 2);
            const t1y = Math.round(py - dirY * 2);
            ctx.fillRect(t1x, t1y, 2, 2);
          }

          ctx.globalAlpha = p.alpha;
          ctx.fillStyle = p.color;
          ctx.fillRect(px - 1, py - 1, 2, 2);

          if (p.highlightColor) {
            ctx.fillStyle = p.highlightColor;
            const hx = px + (dirX > 0.3 ? 0 : (dirX < -0.3 ? -1 : 0));
            const hy = py + (dirY > 0.3 ? 0 : (dirY < -0.3 ? -1 : 0));
            ctx.fillRect(hx, hy, 1, 1);
          }
        }
      } else if (p.isPixelRicochet) {
        lastColor = null;
        lastAlpha = -1;
        ctx.globalAlpha = p.alpha;
        const px = Math.round(p.x);
        const py = Math.round(p.y);
        const spd = Math.hypot(p.vx, p.vy);
        const dirX = spd > 0.001 ? p.vx / spd : 1;
        const dirY = spd > 0.001 ? p.vy / spd : 0;

        ctx.fillStyle = p.color || '#ffffff';
        ctx.fillRect(px - 1, py - 1, 2, 2);

        const streakSteps = Math.min(3, Math.max(1, Math.round(spd * 0.02)));
        ctx.fillStyle = p.highlightColor || '#38bdf8';
        for (let s = 1; s <= streakSteps; s++) {
          const stepDist = s * 2;
          const sx = Math.round(px - dirX * stepDist);
          const sy = Math.round(py - dirY * stepDist);
          ctx.globalAlpha = p.alpha * (1.0 - s * 0.28);
          ctx.fillRect(sx, sy, 1, 1);
        }
      } else if (p.isRicochet) {
        lastColor = null;
        lastAlpha = -1;
        const spd = Math.hypot(p.vx, p.vy);
        const ang = Math.atan2(p.vy, p.vx);
        const len = Math.min(18, Math.max(6, spd * 0.05));
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(ang);
        ctx.fillStyle = p.color || '#ffffff';
        ctx.globalAlpha = p.alpha;
        ctx.fillRect(-len, -1, len, 2);
        ctx.fillStyle = p.highlightColor || '#fbbf24';
        ctx.fillRect(-len * 0.7, -0.5, len * 0.7, 1);
        ctx.restore();
      } else if (p.isWreckage) {
        lastColor = null;
        lastAlpha = -1;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot || 0);
        ctx.globalAlpha = p.alpha;
        if (p.wreckageType === 0) {
          // Typ A: Rad-/Kettensegment (Dunkles 7x7 Profil)
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(-3.5, -3.5, 7, 7);
          ctx.fillStyle = '#334155';
          ctx.fillRect(-2.5, -2.5, 5, 5);
          ctx.fillStyle = '#64748b';
          ctx.fillRect(-1, -1, 2, 2);
        } else if (p.wreckageType === 1) {
          // Typ B: Chassisscherbe (Gezacktes 8x7 Panzerblech mit Teamfarbe)
          const teamCol = p.team === 'eastern' ? '#ff003c' : '#00f0ff';
          const teamDark = p.team === 'eastern' ? '#991b1b' : '#0369a1';
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(-4, -3.5, 8, 7);
          ctx.fillStyle = teamDark;
          ctx.fillRect(-3, -2.5, 6, 5);
          ctx.fillStyle = teamCol;
          ctx.fillRect(-2, -1.5, 4, 3);
        } else {
          // Typ C: Kanonenrohr-Stummel (8x4 Rohr mit orange glühendem Abrissrand)
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(-4, -2, 8, 4);
          ctx.fillStyle = '#475569';
          ctx.fillRect(-3, -1, 5, 2);
          ctx.fillStyle = '#ea580c';
          ctx.fillRect(2, -2, 2, 4);
          ctx.fillStyle = '#fef08a';
          ctx.fillRect(3, -1, 1, 2);
        }
        ctx.restore();
      } else if (p.isGroundArc && p.points && p.points.length > 1) {
        lastColor = null;
        lastAlpha = -1;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.strokeStyle = p.color || '#00f0ff';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(p.x + p.points[0].x, p.y + p.points[0].y);
        for (let j = 1; j < p.points.length; j++) {
          ctx.lineTo(p.x + p.points[j].x, p.y + p.points[j].y);
        }
        ctx.stroke();

        ctx.strokeStyle = p.highlightColor || '#ffffff';
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(p.x + p.points[0].x, p.y + p.points[0].y);
        for (let j = 1; j < p.points.length; j++) {
          ctx.lineTo(p.x + p.points[j].x, p.y + p.points[j].y);
        }
        ctx.stroke();
        ctx.restore();
      } else if (p.isSlimeGlaze) {
        lastColor = null;
        lastAlpha = -1;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color || '#65a30d';
        ctx.beginPath();
        ctx.ellipse(p.x, p.y, p.radius * 1.5, p.radius * 0.6, 0, 0, Math.PI * 2);
        ctx.fill();
        if (p.highlightColor && p.alpha > 0.15) {
          ctx.fillStyle = p.highlightColor;
          ctx.beginPath();
          ctx.ellipse(p.x - 0.5, p.y - 0.3, p.radius * 0.5, p.radius * 0.22, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      } else if (p.isRound || p.isBubble) {
        if (p.color !== lastColor) {
          ctx.fillStyle = lastColor = p.color;
        }
        const a = Math.round(p.alpha * 100) / 100;
        if (a !== lastAlpha) {
          ctx.globalAlpha = lastAlpha = a;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
      } else {
        if (p.color !== lastColor) {
          ctx.fillStyle = lastColor = p.color;
        }
        const a = Math.round(p.alpha * 100) / 100;
        if (a !== lastAlpha) {
          ctx.globalAlpha = lastAlpha = a;
        }
        const r = p.radius;
        ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
      }
    }

    // Pass B: additive particles (lighter) - fast iterate only queued additive particles
    ctx.globalCompositeOperation = 'lighter';
    let lastAddColor = null;
    let lastAddAlpha = -1;
    for (let i = 0; i < this._additiveQueue.length; i++) {
      const p = this._additiveQueue[i];
      if (p.isAnamorphicFlare) {
        if (p.isBastionFlare) continue;
        lastAddColor = null;
        lastAddAlpha = -1;
        const flareAlpha = p.alpha;
        const hw = (p.width || 160) * (0.8 + 0.2 * (1.0 - p.life / p.maxLife));
        const hh = (p.height || 3.5);

        if (MAX_PARTICLES <= 150) {
          ctx.fillStyle = p.color || '#38bdf8';
          ctx.globalAlpha = flareAlpha * 0.75;
          ctx.fillRect(p.x - hw * 0.65, p.y - hh / 2, hw * 1.3, hh);
        } else {
          const lut = getAnamorphicFlareLUT(p.color || '#38bdf8');
          ctx.globalAlpha = flareAlpha * 0.9;
          if (lut) {
            ctx.drawImage(lut, p.x - hw, p.y - hh / 2, hw * 2, hh);
          } else {
            ctx.fillStyle = p.color || '#38bdf8';
            ctx.fillRect(p.x - hw, p.y - hh / 2, hw * 2, hh);
          }
        }

        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = flareAlpha;
        ctx.fillRect(p.x - 4, p.y - hh * 1.2, 8, hh * 2.4);
      } else if (p.isHeatShimmer) {
        const wave = Math.sin(p.waveOffset || 0) * 4.0;
        const sw = p.width || 24;
        const sh = p.height || 16;
        const c = p.color || 'rgba(254, 215, 170, 0.2)';
        if (c !== lastAddColor) {
          ctx.fillStyle = lastAddColor = c;
        }
        const a = Math.round(p.alpha * 100) / 100;
        if (a !== lastAddAlpha) {
          ctx.globalAlpha = lastAddAlpha = a;
        }
        ctx.fillRect(p.x - sw / 2 + wave, p.y - sh / 2, sw, sh);
      } else if (p.isGoldDustGlide || p.isEmber) {
        if (p.color !== lastAddColor) {
          ctx.fillStyle = lastAddColor = p.color;
        }
        const a = Math.round(p.alpha * 100) / 100;
        if (a !== lastAddAlpha) {
          ctx.globalAlpha = lastAddAlpha = a;
        }
        const flicker = 0.8 + 0.2 * Math.sin(nowSec * 30 + (p.sparkleOffset || p.x));
        const r = (p.radius || 1.5) * flicker;
        if (p.isStarGlint && r > 1.3) {
          // 4-point glittering diamond star glint for falling gold foil motes
          ctx.beginPath();
          ctx.moveTo(p.x, p.y - r * 1.8);
          ctx.lineTo(p.x + r * 0.5, p.y);
          ctx.lineTo(p.x, p.y + r * 1.8);
          ctx.lineTo(p.x - r * 0.5, p.y);
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(p.x - r * 1.8, p.y);
          ctx.lineTo(p.x, p.y + r * 0.5);
          ctx.lineTo(p.x + r * 1.8, p.y);
          ctx.lineTo(p.x, p.y - r * 0.5);
          ctx.fill();
        } else {
          ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
        }
      }
    }
    ctx.restore();

    // 4. Render Floating Damage Text (Font-cached & property-batched)
    if (this.floatingTexts.length > 0) {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
      ctx.lineWidth = 3;
      let lastSize = null;
      const lowTierText = this.quality === 'low';
      for (let i = 0; i < this.floatingTexts.length; i++) {
        const ft = this.floatingTexts[i];
        if (ft.size !== lastSize) {
          lastSize = ft.size;
          ctx.font = DAMAGE_TEXT_FONT_CACHE[lastSize] || `bold ${lastSize}px "Orbitron", "Courier New", monospace`;
        }
        ctx.globalAlpha = ft.alpha;
        const hasScale = ft.scale && ft.scale !== 1.0;
        if (hasScale) {
          ctx.save();
          ctx.translate(ft.x, ft.y);
          ctx.scale(ft.scale, ft.scale);
        }
        const tx = hasScale ? 0 : ft.x;
        const ty = hasScale ? 0 : ft.y;
        if (ft.type === 'gold') {
          // Gilded shining damage text: warm dark-amber outline + bright incandescent gold fill (no emoji)
          ctx.strokeStyle = 'rgba(120, 53, 15, 0.95)';
          ctx.lineWidth = 3.5;
          ctx.strokeText(ft.text, tx, ty);
          ctx.fillStyle = ft.color;
          ctx.fillText(ft.text, tx, ty);
          // Restore default stroke styling
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
          ctx.lineWidth = 3;
        } else {
          // Outlined text (strokeText at 3 px) is the costly half on phones: on the
          // 'low' tier only the larger numbers (crits, nukes, heals >= 15 px) keep it.
          if (!lowTierText || ft.size >= 15) ctx.strokeText(ft.text, tx, ty);
          ctx.fillStyle = ft.color;
          ctx.fillText(ft.text, tx, ty);
        }
        if (hasScale) {
          ctx.restore();
        }
      }
      ctx.restore();
    }

    // 5. Authentic Nuclear / Superweapon Flash & Flare (Smooth radiant dissipation without dark boxes)
    if (this.nukeFlash && this.nukeFlash.duration > 0) {
      const prog = 1.0 - (this.nukeFlash.duration / this.nukeFlash.maxDuration); // 0.0 to 1.0
      ctx.save();

      // Initial blinding white-hot flash (fading smoothly over first 28%)
      if (prog < 0.28) {
        const whiteAlpha = (1.0 - prog / 0.28) * 0.85;
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = whiteAlpha;
        ctx.fillRect(0, 0, 1600, 600);
      }

      // 🌪️ Post-Flash Desaturation Shockwave (Monochrome bleach while shockwave expands)
      if (this.quality !== 'low' && prog >= 0.08 && prog < 0.65) {
        const desatFactor = (prog < 0.22)
          ? ((prog - 0.08) / 0.14)
          : (1.0 - (prog - 0.22) / 0.43);
        const desatAlpha = Math.sin(Math.max(0, Math.min(1.0, desatFactor)) * Math.PI * 0.5) * 0.78;

        ctx.save();
        ctx.globalCompositeOperation = 'color';
        ctx.fillStyle = `rgba(128, 128, 136, ${desatAlpha})`;
        ctx.fillRect(0, 0, 1600, 600);
        ctx.restore();
      }

      // Expanding warm radial thermal halo (Golden/Amber glow)
      const haloR = 120 + prog * 550;
      const haloAlpha = Math.pow(1.0 - prog, 1.4) * 0.65;
      ctx.save();
      ctx.translate(this.nukeFlash.x, this.nukeFlash.y);
      ctx.scale(haloR, haloR);
      ctx.fillStyle = getNukeHaloGrad(ctx);
      ctx.globalAlpha = haloAlpha;
      ctx.beginPath();
      ctx.arc(0, 0, 1, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.restore();
    }
  }
}
